/**
 * Chaostrack REST API Utilities (Version 0)
 *
 */
import { Buffer } from "buffer"
import { noteServerDate } from "../utilities/serverClock.js"
import { v4 as uuidv4 } from "uuid"
import logger from "../Logger"
import { defaultSessionNLPModel, API_PATHS } from "../utilities/constants.js"
import { authHeadersFor } from "./authUtils"
import { encodeRFC3986URIComponent, markdownToPlainText } from "./uiUtils.js"

const pageLogger = logger.child({ module: "REST API utilities" })

const reqHeaders = (accessToken, userId) => {
  return {
    ...authHeadersFor(accessToken, userId),
    "content-type": "application/json",
  }
}

const API_BASE_URL = `${window.__RUNTIME_CONFIG__.REACT_APP_API}`

const fetchOrLogError = async (url, options) => {
  try {
    return await fetch(url, options)
  } catch (error) {
    // A cancelled request (the caller's AbortSignal) is not a network failure: let the caller see it.
    if (error?.name === "AbortError") throw error
    pageLogger.error(`Fetch error trying URL: '${url}': ${error}`)
  }
  return null
}

// One parser for API error bodies: Hasura REST / action errors put the code in `code` (KPI_*,
// ORG_REVISION_CONFLICT, …) or `extensions.code`, and the text in `error` / `message`.
const apiErrorFrom = async ({ resRaw, fallback }) => {
  let serverCode = null
  let details = null
  let msg = fallback
  try {
    const e = await resRaw.json()
    serverCode = e?.code || e?.extensions?.code || e?.errors?.[0]?.extensions?.code || null
    msg = e?.error || e?.message || e?.errors?.[0]?.message || msg
    // KPI P2: a 409 can carry structured details (e.g. the alias row count to confirm).
    details = e?.extensions?.details ?? e?.errors?.[0]?.extensions?.details ?? null
  } catch (e) {
    // non-JSON body
  }
  const err = new Error(msg)
  err.status = resRaw?.status ?? null
  err.serverCode = serverCode
  err.details = details
  return err
}

/**
 * Get a game session (monolithic game session object)
 */

// The browser's IANA time zone ("America/New_York"), or undefined when the browser can't say.
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export const getSession = async (accessToken, userId, { name }) => {
  const requestStartedAt = Date.now()
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  // The gameplay poll doubles as the server-clock sample for the CTF turn countdown (serverClock.js).
  noteServerDate(resRaw, { requestStartedAt })

  let result = null
  let status = resRaw?.status

  if (status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return { result, status }
}

/**
 * Create a new game
 */
export const createSession = async (
  accessToken,
  userId,
  {
    name,
    test,
    org,
    executiveDetailsEnabled = false,
    useNewGameplayExperience = false,
    guidanceMode,
    aiSupport = { facilitatorOn: false, meetingLink: "", stakeholders: [] },
  }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      name,
      test,
      org,
      executiveDetailsEnabled,
      useNewGameplayExperience,
      guidanceMode,
      aiSupport,
    }),
  })

  let result = null
  let message = null

  if (resRaw?.status > 400) {
    // Try to parse the body for a real server error message; fall back to generic.
    let bodyMsg = null
    try {
      const resJSON = await resRaw.json()
      bodyMsg = resJSON?.error ?? resJSON?.message ?? null
    } catch (_) {
      // non-JSON body — ignore
    }
    result = null
    message = bodyMsg ?? `No results. There was an internal error (${resRaw.status}).`
  } else if (resRaw?.status === 400) {
    // Parse message — server may return either `error` or `message`.
    const resJSON = await resRaw.json()
    result = null
    message = resJSON?.error ?? resJSON?.message ?? `The game couldn't be created due to an error.`
  } else if (resRaw?.status === 200) {
    // Parse JSON
    const resJSON = await resRaw.json()
    result = resJSON?.result ?? null
    // A 200 with NO session under `result`: surface it instead of returning a silent falsy error
    // that the caller renders as the generic "contact support" modal with no detail.
    message = result
      ? null
      : (resJSON?.error ??
        resJSON?.message ??
        `The server accepted the request but returned no session (HTTP 200, empty result). Please try again — if this keeps happening, contact support.`)
  }

  return { message, result }
}

/**
 * Create a new game group
 */
export const createSessionGroup = async (
  accessToken,
  userId,
  {
    name,
    test,
    org,
    players,
    spectators,
    nlpEnabled,
    nlpMatchThreshold,
    nlpModel = defaultSessionNLPModel,
    nlpAutoDisableThreshold,
    variables,
    nlpContext,
    metadata,
    batchSize,
    endTime = null,
    scheduledTimeForFirstBatch,
    timeIntervalBetweenBatches,
    executiveDetailsEnabled = false,
    useNewGameplayExperience = false,
    guidanceMode,
    defaultLanguage,
    // facilitator(s) intentionally NOT destructured/sent: GroupMiniScenariosInput declares neither,
    // so Hasura would reject the createGameGroup mutation. The channel is players[].isFacilitator.
    bots = undefined,
    roleBots = undefined,
    customBots = undefined,
    botRisk = undefined,
    ctfCoordinator = undefined,
    // "Independent Group — your role": the chosen role each member plays + how the other roles are
    // handled. null/absent on a whole-scenario group. Declared on GroupMiniScenariosInput.
    soloRoles = undefined,
    otherRolesMode = undefined,
    // The Roles check was confirmed: players[].scoringRole is each member's confirmed scenario role.
    rolesConfirmed = undefined,
    // "Count AI stakeholders' decisions in the score", inherited by every member.
    aiStakeholdersScored = undefined,
  }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/group`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      input: {
        groupName: name,
        players,
        test,
        org,
        spectators,
        nlpEnabled,
        nlpMatchThreshold,
        nlpModel,
        nlpAutoDisableThreshold,
        variables,
        nlpContext,
        metadata,
        batchSize,
        endTime,
        scheduledTimeForFirstBatch,
        timeIntervalBetweenBatches,
        executiveDetailsEnabled,
        useNewGameplayExperience,
        guidanceMode,
        defaultLanguage,
        // NOTE: facilitator(s) are NOT sent here — GroupMiniScenariosInput does not declare them,
        // so including them makes Hasura REJECT the whole createGameGroup mutation (breaks launch).
        // The facilitator channel for group exercises is players[].isFacilitator; the server derives
        // session.facilitators from it. (Regression fix 2026-09-02.)
        bots,
        roleBots,
        customBots,
        botRisk,
        ctfCoordinator,
        ...(soloRoles ? { soloRoles, otherRolesMode } : {}),
        ...(rolesConfirmed === true ? { rolesConfirmed: true } : {}),
        ...(aiStakeholdersScored === true ? { aiStakeholdersScored: true } : {}),
        // The launcher's time zone: each member's incident date launch day is read in it.
        ...(browserTimeZone() ? { timeZone: browserTimeZone() } : {}),
      },
    }),
  })

  let result = null
  let message = null

  if (resRaw?.status > 400) {
    result = null
    message = `No results. There was an internal error (${resRaw.status}).`
  } else if (resRaw?.status === 400) {
    // Parse message
    const resJSON = await resRaw.json()
    result = null
    message = resJSON?.error ?? `The game group couldn't be created due to an error.`
  } else if (resRaw?.status === 200) {
    // Parse JSON
    const resJSON = await resRaw.json()
    result = resJSON?.result?.result ?? null
    message = resJSON?.result?.message ?? null
  }

  return { message, result }
}

/**
 * Update a game session
 */
/**
 * Permanently delete a single (ungrouped) exercise. Server: removeSession action —
 * manager any org (via `org`), sponsor/trainer own org; refuses group members with a
 * "delete the group instead" message. Throws Error carrying the server reason (reads
 * both `message` and the Hasura action `error` wrapper shapes).
 */
export const deleteSession = async (accessToken, userId, { name, org = null } = {}) => {
  const query = org ? `?org=${encodeRFC3986URIComponent(org)}` : ""
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}${query}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return { ok: true }
  }
  let message = "Failed to delete the exercise."
  try {
    const body = await resRaw.json()
    message = body?.message ?? body?.error ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Permanently delete a group exercise and ALL its member sessions. Server:
 * removeSessionGroup — same role binding as deleteSession.
 */
export const deleteSessionGroup = async (accessToken, userId, { name, org = null } = {}) => {
  const query = org ? `?org=${encodeRFC3986URIComponent(org)}` : ""
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/group/${encodeRFC3986URIComponent(name)}${query}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return { ok: true }
  }
  let message = "Failed to delete the group exercise."
  try {
    const body = await resRaw.json()
    message = body?.message ?? body?.error ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

// The Roles check (exercise wizard, last step): the server matches each typed role to the scenario's
// roles once — strict matches apply, one AI check suggests the rest with a reason (design 2026-10-29 §4).
// Returns { scenarioRoles, people, aiParticipants, aiCheck } or throws with the server's message.
export const checkExerciseRoles = async (accessToken, userId, { test, people = [], aiParticipants = [] }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/exercise-roles-check`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ test, people, aiParticipants }),
  })
  let resJSON = null
  try {
    resJSON = await resRaw?.json()
  } catch {
    // non-JSON body
  }
  if (resRaw?.status !== 200 || !resJSON?.result) {
    throw new Error(resJSON?.error ?? resJSON?.message ?? "Couldn't check the roles right now.")
  }
  return resJSON.result
}

export const updateSession = async (
  accessToken,
  userId,
  {
    name,
    test,
    roleAssignments,
    roleAssigneeNames,
    roleAssigneeTitles,
    roleAssigneeLanguages,
    players,
    spectators,
    defaultLanguage,
    dispatched,
    scheduledTime,
    ended,
    endTime,
    nlpEnabled,
    nlpMatchThreshold,
    nlpModel = defaultSessionNLPModel,
    nlpAutoDisableThreshold,
    variables,
    nlpContext,
    metadata,
    chatEnabled,
    showOtherPlayersActions,
    guidanceMode,
    facilitator = undefined,
    facilitators = undefined,
    bots = undefined,
    roleBots = undefined,
    customBots = undefined,
    botRisk = undefined,
    ctfCoordinator = undefined,
    // "Solo — your role" / role-scoped scope: the chosen role the solo player is scored on and how
    // the OTHER roles are handled ("skip"). null on a whole-incident Solo. email-setup.jsx persists
    // Solo via updateSession, so these MUST be sent here or role-scoped scoring is never saved.
    soloRoles = undefined,
    otherRolesMode = undefined,
    // When a selected AI stakeholder matches a role a human already holds, the first save comes
    // back asking for confirmation (see below). Re-send with this flag true to confirm both play.
    confirmStakeholderConflicts = undefined,
    subTeams = undefined, // collaborative sub-teams [{ id, name, roles }]; [] clears
    // The Roles check was confirmed: players[].scoringRole is each person's confirmed scenario role.
    rolesConfirmed = undefined,
    // "Count AI stakeholders' decisions in the score" set on an edit (create sends it in aiSupport).
    aiStakeholdersScored = undefined,
    // The run format the author chose (solo / single / multiplayer / facilitated) — stored so an edit
    // can restore it.
    runFormat = undefined,
  }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      test,
      roleAssignments,
      roleAssigneeNames,
      roleAssigneeTitles,
      roleAssigneeLanguages,
      players,
      spectators,
      dispatched,
      scheduledTime,
      ended,
      endTime,
      nlpEnabled,
      nlpContext,
      nlpMatchThreshold,
      nlpModel,
      nlpAutoDisableThreshold,
      variables,
      metadata,
      chatEnabled,
      showOtherPlayersActions,
      guidanceMode,
      facilitator,
      facilitators,
      defaultLanguage,
      bots,
      roleBots,
      customBots,
      botRisk,
      ctfCoordinator,
      soloRoles,
      otherRolesMode,
      subTeams,
      confirmStakeholderConflicts,
      rolesConfirmed,
      aiStakeholdersScored,
      runFormat,
      // The launcher's time zone: the incident date's launch day is read in it. The server keeps
      // only the first one, before launch, so a later edit never moves an exercise's date.
      timeZone: browserTimeZone(),
    }),
  })

  let result = null
  let message = null

  if (resRaw?.status > 400) {
    // Try to parse the body for a real server error message; fall back to generic.
    let bodyMsg = null
    try {
      const resJSON = await resRaw.json()
      bodyMsg = resJSON?.error ?? resJSON?.message ?? null
    } catch (_) {
      // non-JSON body — ignore
    }
    result = null
    message = bodyMsg ?? `The game setup was not completed due to an error (${resRaw.status}). Please contact support if this issue persists.`
  } else if (resRaw?.status === 400) {
    // Parse message — Hasura may return either `error` or `message` at the top level.
    const resJSON = await resRaw.json()
    message = resJSON?.error ?? resJSON?.message ?? `The game couldn't be configured due to an error. Please contact support if this issue persists.`
  } else if (resRaw?.status === 200) {
    // Parse JSON
    const resJSON = await resRaw.json()
    // Stakeholder-vs-human role conflict: the server did NOT persist; it needs the user to confirm
    // the AI stakeholder(s) will also participate (non-scored). Surface it so the caller can prompt
    // and retry with confirmStakeholderConflicts: true. Not an error — no result yet.
    // NOTE: this endpoint goes through a Hasura action, which nests the handler's JSON under
    // `result` — so the flag arrives as resJSON.result.needsStakeholderConfirmation. Check BOTH
    // shapes so a direct (un-proxied) response keeps working too.
    const confirmPayload = resJSON?.needsStakeholderConfirmation ? resJSON : resJSON?.result?.needsStakeholderConfirmation ? resJSON.result : null
    if (confirmPayload) {
      return { message: null, result: null, needsStakeholderConfirmation: true, conflicts: confirmPayload.conflicts ?? [] }
    }
    result = resJSON?.result ?? null
    if (!result) {
      // A 200 with NO session under `result` and NO stakeholder-confirmation flag: don't let the
      // caller fall through to the generic "contact support" modal with zero detail. Surface any
      // server-provided message, else a clear "accepted but returned nothing" so the failure is
      // diagnosable (this was a silent dead-end for solo + AI-stakeholder + observer dispatches).
      message =
        resJSON?.error ??
        resJSON?.message ??
        `The server accepted the setup but returned no session (HTTP 200, empty result). Please try again — if this keeps happening, contact support.`
    }
  }

  return { message, result }
}

/**
 * Restart a game session
 * This command clears all game play progress and metadata from an existing game but leaves all configuration intact
 */
export const restartSession = async (accessToken, userId, { name }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/restartSession`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      name,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.session ?? null
  }

  return result
}

/**
 * Set archived flag for session
 */
export const setSessionArchived = async (accessToken, userId, { name, archived = true }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      archived,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Set ended flag for session
 */
export const setSessionEnded = async (accessToken, userId, { name, ended = true }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      ended,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Mark the calling participant as completed in a multiplayer game session.
 * For sponsor/manager roles this force-finishes the whole session.
 * For user (participant) role this marks only that player as done; the session
 * auto-finishes once all players have completed.
 */
export const setParticipantCompleted = async (accessToken, userId, { name }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/participantCompleteGame`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ name }),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

export const scenarioSources = Object.freeze({
  IMPORT: "IMPORT", // Direct import
  GENERATED: "GENERATED", // Created using scenario builder
  CUSTOMIZED: "CUSTOMIZED", // Customized using scenario builder
})

/**
 * List all scenarios available for creating games
 */
export const getScenarios = async (accessToken, userId, { source = null } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/tests${source ? `?source=${source}` : ""}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.tests ?? null
  }

  return result
}

// Fetch the CTF question-contract verdict for ONE scenario. The scenario LIST (`/tests`)
// deliberately does not compute the contract (it needs a per-scenario graph traversal), so
// the exercise wizard asks for it on the single scenario the author selected via
// `includeCtfContract=true`. Returns just `{ ctfContractOk, ctfContractViolations }` — merge
// these into the selected scenario so buildCtfCoordinatorFields can gate the CTF toggle.
// Best-effort: a null/absent verdict must NOT disable the toggle (back-compat), so we return
// nulls on any failure and let the caller default to "enabled".
export const getScenarioContract = async (accessToken, userId, { name } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}?includeCtfContract=true`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    const test = (await resRaw.json())?.result ?? null
    return {
      ctfContractOk: test?.ctfContractOk ?? null,
      ctfContractViolations: test?.ctfContractViolations ?? null,
    }
  }

  return { ctfContractOk: null, ctfContractViolations: null }
}

/**
 * Patch a TEST_NODE's non-profile fields. Pass only the fields being changed; `undefined`
 * is omitted by JSON.stringify so the server treats it as "leave alone". To clear
 * `orgAccess` (make a scenario public) pass `null` explicitly.
 *
 * Profile binding is changed via `setScenarioProfile` instead — keeping the two paths
 * separate prevents an omitted `profileId` from being interpreted as "clear".
 */
export const updateTest = async (accessToken, userId, { name, displayName, archived, orgAccess, source } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ displayName, archived, orgAccess, source }),
  })

  let result = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }
  return result
}

/**
 * Directly edit a scenario's role roster (no AI rebuild). `roles` is the comma-joined
 * roster string; the server normalizes separators, stores it as the scenario's role list +
 * Info roster, and realigns the turns so the exercise can launch. Throws on ≥400 so the
 * caller can surface the server message (e.g. roster too long).
 */
export const updateScenarioRoles = async (accessToken, userId, { name, roles } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ roles }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  let message = "Failed to update roles."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Delete a scenario (TEST_NODE) by its key name.
 * Server-side: removeScenario action — manager + sponsor only, ownership-gated.
 * Throws Error on ≥400 so callers can surface the server message.
 */
export const deleteTest = async (accessToken, userId, { name }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return { ok: true }
  }

  let message = "Failed to delete scenario."
  try {
    const body = await resRaw.json()
    // A direct server 4xx carries `message`, but a Hasura-wrapped ACTION error puts the
    // handler's message under `error` (REST action error shape). Reading only `message`
    // dropped the real reason — e.g. "in use by existing exercises — archive instead" —
    // and users saw a doubled generic toast (the production complaint).
    message = body?.message ?? body?.error ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Import a scenario from an XLSX file (manager/admin only).
 *
 * Hits the Hasura REST endpoint POST /importDataFromFile → mutation importDataFromFile
 * → action importFromFile → server /importFromFileV2. The endpoint accepts a flat JSON
 * body with base64 file content.
 *
 * `ownerOrg` is the WRITE-authorization anchor and is honored ONLY for a manager caller
 * (a non-manager is force-bound to their own org server-side). Pass a specific org id to
 * mint a scenario OWNED by that org (owning org can edit/configure/delete it), or omit it
 * (undefined) / pass null for a GLOBAL scenario with no owner. When a specific owner is
 * chosen we also scope orgAccess to that org so it is visible to the owning org.
 *
 * Throws Error on failure (incl. 400 duplicate-name / validation) so callers can toast.
 */
export const importScenarioFromFile = async (accessToken, userId, { file, test, ownerOrg } = {}) => {
  const arrayBuffer = await file.arrayBuffer()
  const fileContent = Buffer.from(arrayBuffer).toString("base64")

  const body = {
    fileContent,
    test,
    fileName: file.name,
    source: "IMPORT",
  }
  // ownerOrg: undefined/null → global (no owner). A specific org id → owned by that org;
  // scope orgAccess to that org so the owning org can see it. Managers only (server-enforced).
  if (ownerOrg) {
    body.ownerOrg = ownerOrg
    body.orgAccess = [ownerOrg]
  }

  const resRaw = await fetchOrLogError(`${API_BASE_URL}${API_PATHS.IMPORT_SCENARIO_FROM_FILE}`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(body),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? { ok: true }
  }

  let message = "Failed to import scenario."
  try {
    const errorBody = await resRaw.json()
    message = errorBody?.message ?? errorBody?.error ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Rebind a scenario to a different builder prompt profile (or clear the binding).
 * `profileId: null` clears the binding so runtime falls back to in-code prompt defaults.
 * Manager/admin only — the server enforces the role gate.
 */
export const setScenarioProfile = async (accessToken, userId, { name, profileId } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}/profile`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ profileId }),
  })

  let result = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }
  return result
}

/**
 * Fetch the list of active builder prompt profiles ({id, profile_name} only). Lightweight
 * — suitable for populating a dropdown. Routes through the Hasura REST endpoint
 * `GET /v1/simulation-configs/profile-lists` which runs the GraphQL query
 * `{ data: getSimulationModelProfileListsApi }` — the field alias is `data`, so the
 * response body is `{ data: [...profiles] }`. Other endpoints in this codebase use
 * different aliases (`result`, `tests`), so handle the common shapes defensively.
 */
export const fetchBuilderProfileList = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profile-lists`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status !== 200) return []
  const body = await resRaw.json()
  if (Array.isArray(body)) return body
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body?.result)) return body.result
  return []
}

export const sessionTimeSortParms = Object.freeze({
  NONE: "NONE",
  ASC: "ASC",
  DESC: "DESC",
})

/**
 * List all game session groups
 */
export const getSessionGroups = async (
  accessToken,
  userId,
  { org, sort, timeSort, timeFilter, statusFilter, searchInput, test, sku, similarToTest, archived } = {}
) => {
  let queryParams = ""
  if (org || sort || timeSort || timeFilter || statusFilter || searchInput || test || sku || similarToTest || archived) {
    const params = []
    if (org) params.push(`org=${encodeRFC3986URIComponent(org)}`)
    if (sort) params.push(`sort=${sort}`)
    if (timeSort) params.push(`timeSort=${timeSort}`)
    if (timeFilter) params.push(`timeFilter=${timeFilter}`)
    if (statusFilter) params.push(`statusFilter=${statusFilter}`)
    if (searchInput) {
      params.push(`searchInput=${encodeRFC3986URIComponent(searchInput)}`)
    }
    if (test) params.push(`test=${encodeRFC3986URIComponent(test)}`)
    if (sku) params.push(`sku=${encodeRFC3986URIComponent(sku)}`)
    if (similarToTest) params.push(`similarToTest=${encodeRFC3986URIComponent(similarToTest)}`)
    if (archived) params.push(`archived=${archived}`)
    queryParams = `?${params.join("&")}`
  }
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/groups${queryParams}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Get optimized exercises list for game home screen
 */
export const getExercises = async (accessToken, userId, { org, sort, timeSort, timeFilter, statusFilter, searchInput, test, sku, archived } = {}) => {
  let queryParams = ""
  if (org || sort || timeSort || timeFilter || statusFilter || searchInput || test || sku || archived) {
    const params = []
    if (org) params.push(`org=${encodeRFC3986URIComponent(org)}`)
    if (sort) params.push(`sort=${sort}`)
    if (timeSort) params.push(`timeSort=${timeSort}`)
    if (timeFilter) params.push(`timeFilter=${timeFilter}`)
    if (statusFilter) params.push(`statusFilter=${statusFilter}`)
    if (searchInput) {
      params.push(`searchInput=${encodeRFC3986URIComponent(searchInput)}`)
    }
    if (test) params.push(`test=${encodeRFC3986URIComponent(test)}`)
    if (sku) params.push(`sku=${encodeRFC3986URIComponent(sku)}`)
    if (archived) params.push(`archived=${archived}`)
    queryParams = `?${params.join("&")}`
  }
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/exercises${queryParams}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * List all game session group reports
 */
export const getSessionGroupReports = async (
  accessToken,
  userId,
  { org, sort, timeSort, timeFilter, statusFilter, searchInput, test, sku, similarToTest, archived, includeUngrouped = false } = {}
) => {
  let queryParams = ""
  const params = [`ungroupedSessions=${includeUngrouped ? "true" : "false"}`]
  if (org || sort || timeSort || timeFilter || statusFilter || searchInput || test || sku || similarToTest || archived) {
    if (org) params.push(`org=${encodeRFC3986URIComponent(org)}`)
    if (sort) params.push(`sort=${sort}`)
    if (timeSort) params.push(`timeSort=${timeSort}`)
    if (timeFilter) params.push(`timeFilter=${timeFilter}`)
    if (statusFilter) params.push(`statusFilter=${statusFilter}`)
    if (searchInput) {
      params.push(`searchInput=${encodeRFC3986URIComponent(searchInput)}`)
    }
    if (test) params.push(`test=${encodeRFC3986URIComponent(test)}`)
    if (sku) params.push(`sku=${encodeRFC3986URIComponent(sku)}`)
    if (similarToTest) params.push(`similarToTest=${encodeRFC3986URIComponent(similarToTest)}`)
    if (archived) params.push(`archived=${archived}`)
  }
  queryParams = `?${params.join("&")}`
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/group-reports${queryParams}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Get a session group by name
 */
export const getSessionGroup = async (accessToken, userId, { groupName } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/group/${encodeRFC3986URIComponent(groupName)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Update a session group by name
 */
export const updateSessionGroup = async (accessToken, userId, { name, archived, ended, endTime, org }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/group/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      archived,
      ended,
      endTime,
      org,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

// Parse a non-200 response into an Error carrying status + server code/message,
// so callers (dialogs) can show a meaningful message.
const errorFromResponse = async (resRaw) => {
  let serverCode = null
  let serverMessage = null
  try {
    const body = await resRaw.json()
    serverCode = body?.code || body?.extensions?.code || body?.errors?.[0]?.extensions?.code || null
    serverMessage = body?.error || body?.message || body?.errors?.[0]?.message || null
  } catch (e) {
    // non-JSON body
  }
  const err = new Error(serverMessage || `HTTP ${resRaw?.status}`)
  err.status = resRaw?.status
  err.serverCode = serverCode
  return err
}

/**
 * Add new participant(s) to an existing GROUP exercise (SCHEDULED or ONGOING).
 * Each participant gets their own member session; the server dispatches/invites.
 */
export const addGroupParticipants = async (accessToken, userId, { groupName, participants, org, scheduledTime }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/group/${encodeRFC3986URIComponent(groupName)}/participants`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ participants, org, ...(scheduledTime != null ? { scheduledTime } : {}) }),
  })
  if (resRaw?.status !== 200) {
    throw await errorFromResponse(resRaw)
  }
  return (await resRaw.json())?.result ?? null
}

/**
 * Add a player (assigned a role) to an existing standalone collaborative session.
 */
export const addSessionPlayer = async (accessToken, userId, { session, player, org }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/players`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ player, org }),
  })
  if (resRaw?.status !== 200) {
    throw await errorFromResponse(resRaw)
  }
  return (await resRaw.json())?.result ?? null
}

/**
 * Update Organization Metadata
 */
export const updateOrg = async (
  accessToken,
  userId,
  {
    id,
    displayName,
    sponsorName,
    sponsorEmail,
    variables,
    logoURL,
    brandColor,
    gameplayPreferences,
    companyURL,
    entitlements,
    archived,
    managingOrgId,
    isPartnerOrg,
    inheritPartnerBranding,
    showManagedByLabel,
    showPoweredByChaostrack,
    maturityTarget,
    maturityTargetLabel,
    departments,
    kpiPrivacy,
    partnerGrantableEntitlements,
    expectedUpdated = null,
  }
) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(id)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      displayName,
      sponsorName,
      sponsorEmail,
      // KPI P1 (M17): the org concurrency token rides in variables.__expectedUpdated, exactly as
      // patchOrgVariables sends it; the server strips it before any merge (an otherwise empty
      // variables object merges as a no-op).
      variables: expectedUpdated != null ? { ...(variables ?? {}), __expectedUpdated: expectedUpdated } : variables,
      logoURL,
      ...(brandColor !== undefined ? { brandColor } : {}),
      ...(gameplayPreferences !== undefined ? { gameplayPreferences } : {}),
      ...(companyURL !== undefined ? { companyURL } : {}),
      ...(entitlements !== undefined ? { entitlements } : {}),
      // Platform admin only (server-enforced): which entitlements this partner may grant its clients.
      ...(partnerGrantableEntitlements !== undefined ? { partnerGrantableEntitlements } : {}),
      ...(archived !== undefined ? { archived } : {}),
      ...(managingOrgId !== undefined ? { managingOrgId } : {}),
      // ITF-02: partner designation. Manager-only server-side; setting true designates a
      // regular org as a partner, false revokes it (server 409s if it still has children).
      ...(isPartnerOrg !== undefined ? { isPartnerOrg } : {}),
      // CT-1163 partner-branding settings. inheritPartnerBranding is a bool; the two
      // label fields are tri-state strings "on"/"off"/"default" (see partnerBranding).
      ...(inheritPartnerBranding !== undefined ? { inheritPartnerBranding } : {}),
      ...(showManagedByLabel !== undefined ? { showManagedByLabel } : {}),
      ...(showPoweredByChaostrack !== undefined ? { showPoweredByChaostrack } : {}),
      // Response-maturity program target — manager-only General settings. The dashboard's
      // "Program target" line reads these from metrics.maturity.{target,targetLabel}, which
      // the backend sources from the ORG_NODE fields maturityTarget (number) / maturityTargetLabel (string).
      ...(maturityTarget !== undefined ? { maturityTarget } : {}),
      ...(maturityTargetLabel !== undefined ? { maturityTargetLabel } : {}),
      // KPI P1 (spec §3.8, §9.1): wholesale replacement of each list; omitted = unchanged.
      ...(departments !== undefined ? { departments } : {}),
      ...(kpiPrivacy !== undefined ? { kpiPrivacy } : {}),
    }),
  })

  if (!resRaw) {
    const err = new Error("Network error")
    err.name = "TypeError"
    throw err
  }
  if (resRaw.status !== 200) throw await apiErrorFrom({ resRaw, fallback: `HTTP ${resRaw.status}` })

  return (await resRaw.json())?.result ?? null
}

/**
 * Get a game session (monolithic game session object)
 */
export const getOrg = async (accessToken, userId, { id }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(id)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

// ─── Org Member Management APIs ───────────────────────────────────────────────

/**
 * Fetch paginated members (sponsors, trainers, users) for an organization.
 * Returns { members: [], pagination: { page, perPage, total, ... } }
 */
export const getOrgMembers = async (
  accessToken,
  userId,
  { orgId, page = 0, perPage = 10, includeArchived = false, search = "", roleFilter = [], statusFilter = [] }
) => {
  const params = new URLSearchParams({ page, perPage })
  if (includeArchived) params.set("includeArchived", "true")
  if (search) params.set("search", search)
  if (roleFilter.length > 0) params.set("roleFilter", roleFilter.join(","))
  if (statusFilter.length > 0) params.set("statusFilter", statusFilter.join(","))
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/members?${params}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    const body = await resRaw.json()
    // REST endpoint wraps in { result: { members, pagination } }
    return body?.result ?? body ?? { members: [], pagination: { page: 0, perPage: 10, total: 0 } }
  }
  if (resRaw?.status) {
    const errBody = await resRaw.json().catch(() => ({}))
    const msg = errBody?.message ?? `HTTP ${resRaw.status}`
    pageLogger.error({ status: resRaw.status }, `getOrgMembers failed: ${msg}`)
    throw new Error(msg)
  }
  return { members: [], pagination: { page: 0, perPage: 10, total: 0 } }
}

/**
 * Invite a new sponsor to an organization.
 */
export const inviteSponsor = async (accessToken, userId, { orgId, name, email, force = false }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/sponsors`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ email, name, force }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else {
    // Any non-200 (400/403/409/5xx/network) MUST surface as an error — never fall through
    // as a silent success. Covers the ORG-42 cross-org 409 and any 5xx/network failure.
    const resJSON = await resRaw?.json?.().catch(() => null)
    message = resJSON?.message ?? resJSON?.error ?? `Error inviting sponsor (HTTP ${resRaw?.status ?? "network error"})`
  }

  return { result, message }
}

/**
 * Invite an trainer-role member to an organization.
 */
export const inviteOrgTrainer = async (accessToken, userId, { orgId, name, email, force = false }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/trainers`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ email, name, force }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else {
    // Any non-200 must surface (incl. the CT-1195 cross-org 409) — never silent success.
    const resJSON = await resRaw?.json?.().catch(() => null)
    message = resJSON?.message ?? resJSON?.error ?? `Error inviting trainer (HTTP ${resRaw?.status ?? "network error"})`
  }

  return { result, message }
}

/**
 * Invite a regular (user-role) member to an organization without a game session.
 */
export const inviteOrgUser = async (accessToken, userId, { orgId, name, email }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/users`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ email, name }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 400 || resRaw?.status === 403) {
    const resJSON = await resRaw.json()
    message = resJSON?.message ?? "Error inviting user"
  } else if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }

  return { result, message }
}

/**
 * Remove any org member (sponsor or user) from the organization.
 */
export const deleteOrgMember = async (accessToken, userId, { orgId, targetUserId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/members/${encodeRFC3986URIComponent(targetUserId)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null
  let message = null

  if (resRaw?.status === 400 || resRaw?.status === 403 || resRaw?.status === 404) {
    const resJSON = await resRaw.json()
    message = resJSON?.message ?? "Error removing member"
  } else if (resRaw?.status === 207) {
    const resJSON = await resRaw.json()
    result = true
    message = resJSON?.warning ?? null
  } else if (resRaw?.status === 200) {
    result = true
  } else {
    const resJSON = await resRaw?.json?.().catch(() => null)
    message = resJSON?.message ?? `Error removing member (HTTP ${resRaw?.status ?? "network error"})`
  }

  return { result, message }
}

export const restoreOrgMember = async (accessToken, userId, { orgId, targetUserId }) => {
  const resRaw = await fetchOrLogError(
    `${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/members/${encodeRFC3986URIComponent(targetUserId)}/restore`,
    {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
    }
  )

  let result = null
  let message = null

  if (resRaw?.status === 400 || resRaw?.status === 403 || resRaw?.status === 404) {
    const resJSON = await resRaw.json()
    message = resJSON?.message ?? "Error restoring member"
  } else if (resRaw?.status === 200) {
    result = true
  }

  return { result, message }
}

/**
 * Fetch login activity (stats + event logs) for a single org member.
 */
export const getMemberActivity = async (accessToken, userId, { orgId, targetUserId }) => {
  const resRaw = await fetchOrLogError(
    `${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/members/${encodeRFC3986URIComponent(targetUserId)}/activity`,
    {
      method: "GET",
      headers: reqHeaders(accessToken, userId),
    }
  )
  if (!resRaw || resRaw.status !== 200) {
    const msg = resRaw ? (await resRaw.json().catch(() => null))?.message : null
    throw new Error(msg ?? "Error fetching member activity")
  }
  const body = await resRaw.json()
  return body?.result ?? body
}

/**
 * Resend an invitation email to an org member who has not yet logged in.
 */
export const resendMemberInvite = async (accessToken, userId, { orgId, targetUserId }) => {
  const resRaw = await fetchOrLogError(
    `${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/members/${encodeRFC3986URIComponent(targetUserId)}/resend-invite`,
    {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
    }
  )

  let result = null
  let message = null

  if (resRaw?.status === 400 || resRaw?.status === 403 || resRaw?.status === 404) {
    const resJSON = await resRaw.json()
    message = resJSON?.message ?? "Error resending invite"
  } else if (resRaw?.status === 200) {
    result = true
  }

  return { result, message }
}

/**
 * Update a sponsor's details (name and/or blocked status).
 */
export const updateSponsor = async (accessToken, userId, { targetUserId, name, blocked, role, orgId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/sponsor/${encodeRFC3986URIComponent(targetUserId)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ name, blocked, ...(role !== undefined ? { role } : {}), ...(orgId !== undefined ? { orgId } : {}) }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else {
    // Any non-200 must surface (incl. the primary-sponsor-role-locked 409) — never silent success.
    const resJSON = await resRaw?.json?.().catch(() => null)
    message = resJSON?.message ?? resJSON?.error ?? `Error updating sponsor (HTTP ${resRaw?.status ?? "network error"})`
  }

  return { result, message }
}

/**
 * Delete a sponsor from an organization.
 */
export const deleteSponsor = async (accessToken, userId, { orgId, targetUserId }) => {
  const resRaw = await fetchOrLogError(
    `${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/sponsors/${encodeRFC3986URIComponent(targetUserId)}`,
    {
      method: "DELETE",
      headers: reqHeaders(accessToken, userId),
    }
  )

  let result = null
  let message = null

  if (resRaw?.status === 400 || resRaw?.status === 403 || resRaw?.status === 404) {
    const resJSON = await resRaw.json()
    message = resJSON?.message ?? "Error deleting sponsor"
  } else if (resRaw?.status === 207) {
    const resJSON = await resRaw.json()
    result = true
    message = resJSON?.warning ?? null
  } else if (resRaw?.status === 200) {
    result = true
  } else {
    const resJSON = await resRaw?.json?.().catch(() => null)
    message = resJSON?.message ?? `Error deleting sponsor (HTTP ${resRaw?.status ?? "network error"})`
  }

  return { result, message }
}

/**
 * Transfer primary sponsor status to another sponsor in the same organization.
 */
export const transferPrimarySponsorship = async (accessToken, userId, { orgId, toUserId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/sponsors/transfer-primary`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ toUserId }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 400 || resRaw?.status === 403 || resRaw?.status === 404) {
    const resJSON = await resRaw.json()
    message = resJSON?.message ?? "Error transferring primary sponsorship"
  } else if (resRaw?.status === 200) {
    result = true
  }

  return { result, message }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all orgs defined on the server
 */
export const getOrgs = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/orgs`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let raw = null

  if (resRaw?.status === 200) {
    // Parse JSON
    raw = (await resRaw.json())?.results
  }

  return raw
}

/**
 * Create a customer organization. For a non-manager (partner) caller the server stamps
 * managingOrgId = the caller's org and isPartnerOrg = false, so the new org becomes a
 * managed child — those fields are intentionally NOT sent from the client. When
 * inviteContactAsSponsor is true the contact (sponsorEmail) is invited as the new org's
 * primary sponsor. Throws with the server message on failure so the UI can surface it.
 */
export const addOrganization = async (
  accessToken,
  userId,
  {
    id,
    displayName,
    sponsorName,
    sponsorEmail,
    inviteContactAsSponsor = false,
    companyURL,
    entitlements,
    isPartnerOrg,
    partnerGrantableEntitlements,
    analyzeWebsite,
  } = {}
) => {
  const body = { id, displayName, sponsorName, sponsorEmail, inviteContactAsSponsor }
  if (companyURL !== undefined) body.companyURL = companyURL
  if (entitlements !== undefined) body.entitlements = entitlements
  if (isPartnerOrg !== undefined) body.isPartnerOrg = isPartnerOrg
  if (partnerGrantableEntitlements !== undefined) body.partnerGrantableEntitlements = partnerGrantableEntitlements
  // Research companyURL in the background and fill the org's profile (server job).
  if (analyzeWebsite === true) body.analyzeWebsite = true
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(body),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  let message = "Failed to create organization."
  let code
  try {
    const body = await resRaw.json()
    message = body?.message ?? message
    code = body?.code
  } catch {
    // response had no JSON body
  }
  const err = new Error(message)
  // Preserve the backend error code (e.g. ORG_ID_CONFLICT) so the dialog can match
  // on code rather than brittle message-text matching (Codex SC3).
  if (code) err.code = code
  throw err
}

// ─── Partner / MSSP org-management endpoints ─────────────────────────────────

/**
 * List the orgs the caller manages (partner/manager scope).
 * For trainers use getOrgs (/orgs) instead — /managed-orgs is sponsor+manager only.
 * Throws on ≥400 so callers can surface the failure.
 */
export const getManagedOrgs = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/managed-orgs`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.results ?? []
  }

  let message = "Failed to load managed organizations."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Org-wide exercise/player/readiness metrics for the Dashboard. orgId is
 * optional (sponsors are server-scoped to their own org regardless).
 * rangeMonths (optional integer) windows ALL dashboard facets server-side to the
 * trailing N months; omitted = all-time. Returns null when the endpoint isn't
 * available so the screen degrades gracefully.
 */
export const fetchOrgMetrics = async (accessToken, userId, { orgId, rangeMonths } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/org-metrics`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ ...(orgId ? { orgId } : {}), ...(rangeMonths ? { rangeMonths } : {}) }),
  })

  if (resRaw?.status === 200) {
    // The REST alias puts the metrics object directly under `data`
    const payload = (await resRaw.json())?.data
    return payload?.exercises ? payload : (payload?.data ?? null)
  }

  // 404 = the metrics action genuinely isn't deployed on this environment — a
  // stable state the dashboard reports as "not available here". Anything else
  // (5xx, gateway timeout, dropped connection) is transient: THROW so react-query
  // retries and surfaces an error state, instead of caching a null that renders
  // the misleading "hasn't been deployed" copy for a blip.
  if (resRaw?.status === 404) return null
  const err = new Error(`org-metrics request failed (${resRaw?.status ?? "network error"})`)
  err.status = resRaw?.status ?? null
  throw err
}

// ─── KPI P1 (spec §7, §7.11) ─────────────────────────────────────────────────
// Hasura REST aliases put each action result under `data`.

const kpiRequest = async ({ accessToken, userId, path, method, body, signal }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/${path}`, {
    method,
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
  if (!resRaw) {
    const err = new Error("Network error")
    err.name = "TypeError"
    throw err
  }
  if (resRaw.status === 200) return (await resRaw.json())?.data ?? null
  if (resRaw.status === 404) return null
  throw await apiErrorFrom({ resRaw, fallback: `KPI request failed (${resRaw.status})` })
}

// POST /v1/kpis/query. Returns null when the action isn't deployed (404); throws otherwise.
// `signal` (optional) cancels a superseded request.
export const fetchKpis = async (accessToken, userId, { orgId, filters, queries, options, signal } = {}) =>
  kpiRequest({
    accessToken,
    userId,
    path: "v1/kpis/query",
    method: "POST",
    body: { ...(orgId ? { orgId } : {}), ...(filters ? { filters } : {}), queries, ...(options ? { options } : {}) },
    signal,
  })

export const getRoleDepartments = async (accessToken, userId, { orgId, search, kind, status, sort, limit, cursor } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/role-departments", method: "POST", body: { orgId, search, kind, status, sort, limit, cursor } })

export const setRoleDepartments = async (accessToken, userId, { orgId, updates, acceptSuggested } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/role-departments", method: "PATCH", body: { orgId, updates, acceptSuggested } })

// scenarioType null deletes this org's override ("Reset to default").
export const setScenarioTypeOverride = async (accessToken, userId, { orgId, scenarioId, scenarioType = null } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/scenario-type", method: "PATCH", body: { orgId, scenarioId, scenarioType } })

// ─── KPI P2 (spec §12) ───────────────────────────────────────────────────────
// against: "scenario" (default, same-scenario runs) | "attackType" (with attackType) | "exercise".
export const compareRuns = async (accessToken, userId, { orgId, run, baseline, mode, summaryOnly, against, attackType } = {}) =>
  kpiRequest({
    accessToken,
    userId,
    path: "v1/kpis/compare-runs",
    method: "POST",
    body: { orgId, run, baseline, mode, summaryOnly, against, attackType },
  })

// Platform admins: confirm or change a scenario's attack types ([] clears them).
export const setScenarioAttackTypes = async (accessToken, userId, { scenarioId, attackTypes } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/scenario-attack-types", method: "PATCH", body: { scenarioId, attackTypes } })

export const compareGroups = async (accessToken, userId, args = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/compare-groups", method: "POST", body: args })

// Compare departments: every scenario in the window and whether it can be compared (same rules).
export const compareGroupsScenarios = async (accessToken, userId, args = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/compare-groups/scenarios", method: "POST", body: args })

export const getOrgMemberProfiles = async (accessToken, userId, { orgId, emails, missingGroupKey } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/members/profiles", method: "POST", body: { orgId, emails, missingGroupKey } })

export const upsertOrgMembers = async (accessToken, userId, { orgId, members, applyToHistory, confirmMerge } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/members", method: "PATCH", body: { orgId, members, applyToHistory, confirmMerge } })

export const previewMemberImport = async (accessToken, userId, { orgId, fileName, fileBase64, importId, mapping, confirmMapping, options } = {}) =>
  kpiRequest({
    accessToken,
    userId,
    path: "v1/kpis/members/import/preview",
    method: "POST",
    body: { orgId, fileName, fileBase64, importId, mapping, confirmMapping, options },
  })

export const commitMemberImport = async (accessToken, userId, { orgId, importId, diffHash } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/members/import/commit", method: "POST", body: { orgId, importId, diffHash } })

export const getMemberImport = async (accessToken, userId, { orgId, importId } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/members/import/status", method: "POST", body: { orgId, importId } })

export const erasePersonData = async (accessToken, userId, { orgId, email, personRef, confirmEmail } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/people/erase", method: "POST", body: { orgId, email, personRef, confirmEmail } })

export const getKpiAccessLog = async (accessToken, userId, { orgId, since, action, limit, cursor } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/access-log", method: "POST", body: { orgId, since, action, limit, cursor } })

// ─── KPI P3 frameworks ───────────────────────────────────────────────────────
export const getKpiFrameworks = async (accessToken, userId, { orgId } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/frameworks", method: "POST", body: { orgId } })

export const updateKpiFrameworkSettings = async (accessToken, userId, { orgId, enabled, pins, targets } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/frameworks/settings", method: "PATCH", body: { orgId, enabled, pins, targets } })

// file: { name, contentBase64 } (CSV/XLSX) supplies the categories; dryRun validates only.
export const saveKpiCustomFramework = async (accessToken, userId, { orgId, framework, file, dryRun } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/frameworks/custom", method: "POST", body: { orgId, framework, file, dryRun } })

export const deleteKpiCustomFramework = async (accessToken, userId, { orgId, id } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/frameworks/custom/delete", method: "POST", body: { orgId, id } })

export const getScenarioFrameworkMap = async (accessToken, userId, { orgId, scenarioId } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/frameworks/scenario-map", method: "POST", body: { orgId, scenarioId } })

// ─── KPI P4 dashboard views and export ───────────────────────────────────────
export const getKpiDashboardViews = async (accessToken, userId, { orgId } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/dashboard-views", method: "POST", body: { orgId } })

export const saveKpiDashboardView = async (accessToken, userId, { orgId, view } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/dashboard-views/save", method: "POST", body: { orgId, view } })

export const deleteKpiDashboardView = async (accessToken, userId, { orgId, id } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/dashboard-views/delete", method: "POST", body: { orgId, id } })

// Report page (one exercise) and Person page as PPTX / XLSX (KPI P4).
export const exportKpiReport = async (accessToken, userId, { orgId, format, run, against, attackType, baseline } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/report/export", method: "POST", body: { orgId, format, run, against, attackType, baseline } })

export const exportKpiPerson = async (accessToken, userId, { orgId, format, personRef } = {}) =>
  kpiRequest({ accessToken, userId, path: "v1/kpis/people/person/export", method: "POST", body: { orgId, format, personRef } })

// format "pptx" | "xlsx" → { fileName, mimeType, contentBase64 }.
export const exportKpiDashboard = async (accessToken, userId, { orgId, format, filters, widgets, viewName, filterLine, frameworkId } = {}) =>
  kpiRequest({
    accessToken,
    userId,
    path: "v1/kpis/dashboard/export",
    method: "POST",
    body: { orgId, format, filters, widgets, viewName, filterLine, frameworkId },
  })

// Scenario TTX package (zip: Exercise Brief .pptx, Situation Manual .docx, Facilitator Guide .docx)
// → { fileName, mimeType, contentBase64 }. Throws with the server's message (e.g. the AI step
// failed — "please try again") on any failure.
export const exportScenarioTtxPackage = async (accessToken, userId, { name } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/scenarios/ttx-package`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ name }),
  })
  if (resRaw?.status === 200) {
    const pkg = (await resRaw.json())?.data ?? null
    if (pkg?.contentBase64) return pkg
    throw new Error("Couldn't build the package — please try again.")
  }
  throw await apiErrorFrom({ resRaw, fallback: `Couldn't build the package (${resRaw?.status ?? "network error"}) — please try again.` })
}

// Owner edit ("Set for all organizations", managers). "profile" resets to the profile's type.
export const updateScenarioType = async (accessToken, userId, { name, scenarioType } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ scenarioType }),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  throw await apiErrorFrom({ resRaw, fallback: `Couldn't change the scenario type (${resRaw?.status ?? "network error"}).` })
}

/**
 * Get stats for a single org (total users, per-role split, sponsors list, last activity).
 * Throws on ≥400.
 */
export const getOrgStats = async (accessToken, userId, { orgId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/stats`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  let message = "Failed to load org stats."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Assign a customer org to a partner org (manager only).
 * POST /org/:orgId/partner  body: { partnerOrgId }
 * Throws on ≥400.
 */
export const assignOrg = async (accessToken, userId, { orgId, partnerOrgId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/partner`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ partnerOrgId }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  let message = "Failed to assign organization."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Release a customer org from a partner (sponsor + manager).
 * DELETE /org/:orgId/partner — sets managingOrgId = ''.
 * Throws on ≥400.
 */
export const releaseOrg = async (accessToken, userId, { orgId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(orgId)}/partner`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  let message = "Failed to release organization."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Hard-delete a customer organization (manager only). `force: true` removes an EMPTY real org
 * past the prod ephemeral-scope guard (the server re-checks manager + emptiness). Throws on
 * ≥400 so the confirm dialog can surface the reason (e.g. "org still has content").
 */
export const deleteOrganization = async (accessToken, userId, { id, force = false } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(id)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ force }),
  })

  if (resRaw?.status === 200) {
    const body = await resRaw.json().catch(() => null)
    return body?.result ?? body ?? { success: true }
  }

  let message = "Failed to delete organization."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

/**
 * Update the org-access list on a scenario using a delta (add/remove arrays).
 * SEPARATE from updateTest — this function throws on ≥400 so the Configure dialog
 * can surface the failure. updateTest keeps its null-on-error contract.
 */
export const updateScenarioOrgAccess = async (accessToken, userId, { name, orgAccessAdd, orgAccessRemove }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(name)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ orgAccessAdd, orgAccessRemove }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  let message = "Failed to update scenario org access."
  try {
    message = (await resRaw.json())?.message ?? message
  } catch {
    // non-JSON body
  }
  throw new Error(message)
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all NLP Models defined on the server
 */
export const getNLPModels = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/getNLPModels`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let raw = null

  if (resRaw?.status === 200) {
    // Parse JSON
    raw = (await resRaw.json())?.results
  }

  return raw
}

/**
 * Select an action for the given game session's current turn
 */
export const pickAction = async (accessToken, userId, { actionId, session, state }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/pickAction`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      session,
      actionId,
      state,
    }),
  })

  let result = null
  let code = 0

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.session ?? null
    code = resRaw.status
  } else if (resRaw?.status) {
    code = resRaw.status
  }

  return { result, code }
}

/**
 * Query actions matching a given 'phrase' for the specified game session's current turn
 */
export const rankedActionsForPhrase = async (accessToken, userId, { phrase, session, attachedArtifactIds = [] }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/getRankedActionsForPhrase`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      phrase,
      session,
      // Files attached to this message in the composer (📎) — the AI reads them first.
      ...(attachedArtifactIds?.length ? { attachedArtifactIds } : {}),
    }),
  })

  let result = null
  let message = null
  let code = 0

  if (resRaw?.status > 400) {
    // Parse message
    // const response = await resRaw.json()
    result = null
    message = `No results. There was an internal error (${resRaw.status}). Try again.`
    code = resRaw.status
  } else if (resRaw?.status === 400) {
    // Parse message
    // const response = await resRaw.json()
    result = null
    message = "No results. Check the test configuration and try again."
    code = resRaw.status
  } else if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
    message = null
    code = resRaw.status
  } else if (resRaw === null) {
    result = null
    message = "No results. Check your network connection and try again."
  }

  return { message, result, code }
}

/**
 * Streaming variant of {@link rankedActionsForPhrase}. Hits the SSE endpoint
 * /streamRankedActionsForPhrase (bypasses Hasura — Hasura buffers responses).
 *
 * Calls `onMilestone({ step, ...payload })` as the server emits progress events:
 * accepted, resolving-session, ai-started, ai-step-complete, ready, error, heartbeat.
 * Returns the same `{ message, result, code }` shape as the buffered helper so
 * call-sites can swap them with minimal change. On stream error, callers should
 * fall back to `rankedActionsForPhrase()`.
 */
export const streamRankedActionsForPhrase = async (
  accessToken,
  userId,
  { phrase, session, spokenBridge = false, attachedArtifactIds = [] },
  { onMilestone, signal } = {}
) => {
  let resRaw
  try {
    resRaw = await fetch(`${API_BASE_URL}/streamRankedActionsForPhrase`, {
      method: "POST",
      headers: { ...reqHeaders(accessToken, userId), Accept: "text/event-stream" },
      // spokenBridge: a VOICE submission leaves the room silent while this generates, so the server
      // rephrases the partial narrative and emits `voice-bridge` events on this same stream. Off for
      // typed submissions — nothing is being spoken there, so there is no gap to fill.
      body: JSON.stringify({ phrase, session, spokenBridge, ...(attachedArtifactIds?.length ? { attachedArtifactIds } : {}) }),
      signal,
    })
  } catch (err) {
    return { message: err?.message ?? "Network error", result: null, code: 0 }
  }

  // Pre-stream HTTP error (validation, auth, busy, etc.). Body is JSON, not SSE.
  if (!resRaw?.ok) {
    let message = `No results. There was an internal error (${resRaw?.status ?? "unknown"}). Try again.`
    if (resRaw?.status === 503) {
      message = "Your previous request is still being processed. Please wait and try again."
    } else if (resRaw?.status === 400) {
      message = "No results. Check the test configuration and try again."
    }
    return { message, result: null, code: resRaw?.status ?? 0 }
  }
  if (!resRaw.body) {
    return { message: "Streaming not supported by this browser.", result: null, code: 0 }
  }

  const reader = resRaw.body.getReader()
  const decoder = new TextDecoder()
  let buf = ""
  let finalResult = null
  let errorEvent = null

  // Parse SSE frames separated by "\n\n", each containing one or more
  // "data: <json>" lines (we only emit `data:` from server-node).
  while (true) {
    let chunk
    try {
      chunk = await reader.read()
    } catch (err) {
      return { message: err?.message ?? "Stream interrupted", result: null, code: 0 }
    }
    if (chunk.done) break
    buf += decoder.decode(chunk.value, { stream: true })
    let idx
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const block = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      for (const line of block.split("\n")) {
        if (!line.startsWith("data: ")) continue
        let payload
        try {
          payload = JSON.parse(line.slice(6))
        } catch {
          continue
        }
        if (payload?.step === "ready") {
          finalResult = payload.result ?? null
        } else if (payload?.step === "error") {
          errorEvent = payload
        }
        try {
          onMilestone?.(payload)
        } catch {
          // never let a UI callback crash the stream
        }
      }
    }
  }

  if (errorEvent) {
    return { message: errorEvent.message || "There was an internal error processing the query.", result: null, code: errorEvent.code || 500 }
  }
  if (finalResult === null) {
    return { message: "Stream ended without a result.", result: null, code: 0 }
  }
  return { message: null, result: finalResult, code: 200 }
}

/**
 * Send a generic chat message to the current session
 */
export const addSessionMessage = async (accessToken, userId, { session, message }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/message`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      message,
    }),
  })

  let result = null
  let code = 0

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
    code = resRaw.status
  } else if (resRaw?.status) {
    code = resRaw.status
  }

  return { result, code }
}

/**
 * Store a user login (caches user info for game session)
 */
export const registerUser = async (accessToken, userId, { email, picture, name }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/registerUser`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      email,
      // RegisterUserInput.picture is `String!` (non-nullable) in the Hasura schema.
      // Auth0 always supplies an avatar URL, so this was never exercised — but
      // Keycloak users have no `picture` claim unless the IdP sends one, and the
      // email-OTP plane has no avatar at all. Passing null makes Hasura reject the
      // whole mutation with "null value found for non-nullable type" BEFORE the
      // handler runs, so the user is never registered.
      // Coerced here rather than in the schema so the Auth0 path is untouched.
      picture: picture ?? "",
      name,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Update user details
 */
export const updateUser = async (
  accessToken,
  userId,
  { picture, name, showHelpWizard = null, useNewGameplayExperience = undefined, gameplayTheme = undefined }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/user`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      picture,
      name,
      showHelpWizard,
      // Omitted from the payload when undefined so a normal profile update never
      // clobbers the gameplay preferences (only an explicit change sets them).
      useNewGameplayExperience,
      gameplayTheme,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Get user login status for the supplied user accounts (emails)
 */
export const getUserData = async (accessToken, userId, { emails }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/userData`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      emails,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.results ?? null
  }

  return result
}

/**
 * (Re)send a player/spectator invitation to the specified email address for the given session (game)
 */
export const reInviteUser = async (accessToken, userId, { session, email }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/userInvite`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      email,
      session,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Submit a user rating/comment
 */
export const submitUserReview = async (accessToken, userId, { session, rating, comment }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/review`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      rating,
      comment,
      session,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Submit a user scenario suggestion
 */
export const submitActionSuggestion = async (accessToken, userId, { session, comment, phrase, options }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/suggestion`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      comment,
      phrase,
      options,
      session,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Fetch attachment data for a specific attachment (identified by scenario, action/state, name, extention)
 */
export const getAttachmentSource = async (accessToken, userId, { test, action, state, name, extension }) => {
  let sourceURL
  if (action) {
    sourceURL = `${API_BASE_URL}/test-action-attachments/${encodeRFC3986URIComponent(test)}/${encodeRFC3986URIComponent(
      action
    )}/${encodeRFC3986URIComponent(name)}/${encodeRFC3986URIComponent(extension)}`
  } else if (state) {
    sourceURL = `${API_BASE_URL}/test-state-attachments/${encodeRFC3986URIComponent(test)}/${encodeRFC3986URIComponent(
      state
    )}/${encodeRFC3986URIComponent(name)}/${encodeRFC3986URIComponent(extension)}`
  }

  // Send to Hasura
  const resRaw = await fetchOrLogError(sourceURL, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    const attachment = (await resRaw.json())?.result ?? null

    // Build src string
    if (attachment?.data) {
      result = `data:image/${extension};base64,${attachment.data}`
    }
  }

  return result
}

// Custom Report APIs

/**
 * Fetch custom report for game session
 */
export const getReport = async (accessToken, userId, { session, sessionGroup, orgId, promptVariableId }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/report`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      session,
      sessionGroup,
      orgId,
      promptVariableId,
    }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.result?.text ?? null
  } else {
    // Look for message
    message = (await resRaw.json())?.error ?? "No report output returned."
  }

  return { result, message }
}

// New Scenario Builder APIs

const MAX_RETRIES = 2

export const generateSessionId = () => {
  return `sess-${Math.random().toString(36).substr(2, 9)}`
}

export const initSimulation = async (accessToken, userId) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

export const getSimulationById = async (accessToken, userId, { simulationId }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

// List the caller's in-progress builder drafts (is_draft rows, newest first).
// Returns [] when the endpoint isn't deployed yet so the Drafts tab degrades
// to its empty state instead of erroring.
export const fetchSimulationDrafts = async (accessToken, userId) => {
  // Hasura path is v1/simulation-drafts (a literal .../drafts would be ambiguous
  // with GET /v1/simulations/:id in Hasura's REST router).
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-drafts`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data?.data ?? []
  }

  return []
}

export const downloadSimulation = async (simulationId, simulationData) => {
  throw Error("Not implemented")
  // try {
  //   const response = await apiClient.get(`/v1/simulations/${simulationId}/download`, {
  //     responseType: "blob", // Ensures binary file download
  //   });

  //   // Extract required values for file name
  //   const name = simulationData?.display_name || "Chaostrack";
  //   const sku = simulationData?.sku_name || "CHT-000";

  //   // Format today's date as "DD-MMM-YYYY" (e.g., 20-Jan-2025)
  //   const today = new Date();
  //   const formattedDate = today.toLocaleDateString("en-GB", {
  //     day: "2-digit",
  //     month: "short",
  //     year: "numeric",
  //   }).replace(/ /g, "-");

  //   // Construct the filename
  //   let fileName = `${name}-${sku}-${formattedDate}-Template.xlsx`;

  //   if(simulationData?.file_name){
  //     fileName = simulationData.file_name
  //   }

  //   // Create a blob URL for the file
  //   const url = window.URL.createObjectURL(new Blob([response.data]));

  //   // Create a temporary anchor element to trigger download
  //   const link = document.createElement("a");
  //   link.href = url;
  //   link.setAttribute("download", fileName); // Set dynamic file name
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);

  //   // Cleanup URL object
  //   window.URL.revokeObjectURL(url);
  // } catch (error) {
  //   console.error("Error downloading spreadsheet:", error);
  //   throw error;
  // }
}

export const simulationPrompt = async (accessToken, userId, { simulationId, variable, dataDetail }) => {
  let attempts = 0
  while (attempts < MAX_RETRIES) {
    // Send to Hasura
    const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/prompt`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify({
        data: {
          variable,
          data: dataDetail,
        },
      }),
    })

    if (resRaw?.status === 200) {
      // Parse JSON
      return (await resRaw.json())?.data ?? null
    } else if (resRaw?.status === 400) {
      // Handle 400 errors (like URL validation failures) - throw error so mutation can catch it
      const errorData = await resRaw.json().catch(() => ({}))
      const errorMessage = errorData?.error || "Failed to process request"
      const error = new Error(errorMessage)
      error.status = 400
      error.response = errorData
      throw error
    } else if (attempts >= MAX_RETRIES) {
      pageLogger.error("Failed to submit simulation prompt after multiple attempts.")
      const error = new Error("Failed to submit simulation prompt after multiple attempts.")
      error.status = resRaw?.status || 500
      throw error
    } else {
      attempts += 1
    }
  }
}

export const updateSimulation = async (accessToken, userId, { simulationId, data }) => {
  // company_context is authored as markdown (with embedded HTML) in the RTE but the builder feeds
  // it straight to the LLM — strip it to plain text here so only plain text is ever persisted/sent.
  // Org-profile storage (coreSystems) uses separate helpers and intentionally keeps its markdown.
  const payload = data && typeof data.company_context === "string" ? { ...data, company_context: markdownToPlainText(data.company_context) } : data
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}`, {
    method: "PATCH",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data: payload,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  } else {
    // ANY non-200 is a failed write and must throw — not only 400. The original fix
    // threw solely on 400 (the builder's validation status), which still let a
    // 403/404/5xx/network failure return null and callers toast "saved." over a
    // rejected write (review IMP-1). For 400 the builder returns
    // { error, errors: [...] } (e.g. the CTF question contract), so the specific
    // validation errors are preserved for the author.
    const status = resRaw?.status ?? 0
    const errorData = status ? await resRaw.json().catch(() => ({})) : {}
    const error = new Error(errorData?.error || `Failed to update simulation (HTTP ${status || "network error"})`)
    error.status = status
    error.response = errorData
    throw error
  }

  return result
}

export const updateSimulationTurns = async (simulationId, turnsId, data) => {
  throw Error("Not implemented")
  // try {
  //   const response = await apiClient.patch(`/v1/simulations/${simulationId}/turns/${turnsId}/urls`,data);
  //   return response.data;
  // } catch (error) {
  //   console.error("Error updating simulation:", error);
  //   throw error;
  // }
}

// export const submitPrompt = async (promptText, fieldData, replaceFlag) => {
//   try {
//     const response = await apiClient.post('/v1/prompt/submit', {
//       prompt: promptText,
//       field_data: fieldData,
//       replace_xml_tags:replaceFlag
//     });
//     return response.data;
//   } catch (error) {
//     console.error('Error in submitPrompt:', error);
//     throw error;
//   }
// };

export const promptChaining = async (accessToken, userId, { simulationId, sessionId, promptData }) => {
  let attempt = 1 // Start counting from 1 for clarity
  const maxAttempts = MAX_RETRIES + 1 // Convert retries to total attempts

  while (attempt <= maxAttempts) {
    // Send to Hasura
    const resRaw = await fetchOrLogError(
      `${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/sessions/${encodeRFC3986URIComponent(sessionId)}/prompt-chaining`,
      {
        method: "POST",
        headers: reqHeaders(accessToken, userId),
        body: JSON.stringify({
          data: promptData,
        }),
      }
    )

    if (resRaw?.status === 200) {
      const responseData = await resRaw.json()
      return responseData?.data ?? responseData ?? null
    } else if (resRaw?.status === 400 || resRaw?.status === 404) {
      // Handle client errors - extract error message
      try {
        const errorData = await resRaw.json()
        const errorMessage = errorData?.message || errorData?.error || `Request failed with status ${resRaw.status}`
        pageLogger.error({ error: errorMessage, status: resRaw.status }, "Prompt chaining error")
        throw new Error(errorMessage)
      } catch (parseError) {
        // If JSON parsing fails, throw generic error
        pageLogger.error({ error: parseError, status: resRaw.status }, "Failed to parse error response")
        throw new Error(`Request failed with status ${resRaw.status}`)
      }
    } else {
      // Server error - check if we should retry
      if (attempt >= maxAttempts) {
        // Exhausted all attempts
        pageLogger.error(`Failed to complete prompt chaining after ${maxAttempts} attempts (${MAX_RETRIES} retries).`)
        throw new Error(`Failed to complete prompt chaining after ${maxAttempts} attempts.`)
      }
      attempt += 1
      // Loop continues to retry
    }
  }

  // Should not reach here
  throw new Error("Failed to complete prompt chaining.")
}

// Fetch default simulation prompts
export const fetchSimulationConfigs = async (accessToken, userId) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

// Fetch all simulation profiles configs
export const fetchSimulationProfilesConfigs = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

// --- LLM Evals (V2) ---------------------------------------------------------
// Exposed via Hasura REST (v1/evals/*), same pattern as the builder calls; they
// proxy to the eval-service.

// Scenarios usable as eval inputs. Optionally target-aware: pass a target (builder step) and the server
// filters to scenarios that have the fields that step consumes.
export const fetchEvalScenarios = async (accessToken, userId, target) => {
  const qs = target ? `?target=${encodeURIComponent(target)}` : ""
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/scenarios${qs}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? []
  }
  return []
}

// Past eval runs (history)
// Returns the rows, or NULL when the request failed.
//
// It used to return [] on any failure, which the Evaluations page could not tell apart from "no
// runs yet" — so a failed fetch replaced the rows already on screen with an empty state reading
// "No saved runs yet". The caller's own try/catch could never fire, because the error had already
// been swallowed here. Callers must treat null as "keep what you have and say so".
//
// The list response carries NO generated outputs (the service strips them); use fetchEvalRun for
// a single run's full detail.
// `kind` (prompts | models | replay) asks for ONE tab's rows, so each tab has its own newest-50 window.
export const fetchEvalHistory = async (accessToken, userId, kind) => {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : ""
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/history${qs}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? []
  }
  return null
}

// ONE eval run, complete with the generated outputs its accordions render. Null on failure or if
// no such run exists — the caller distinguishes those by whether it also has a load error.
export const fetchEvalRun = async (accessToken, userId, runId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/history/${encodeURIComponent(runId)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    const rows = (await resRaw.json())?.data ?? []
    return Array.isArray(rows) ? (rows[0] ?? null) : (rows ?? null)
  }
  return null
}

// --- Model comparison (same prompt, two models) --------------------------------------------------

// Prompts the app logged from FINISHED exercises for a gameplay target — what the "pick a completed
// exercise" list shows. Empty for builder targets (their prompts are not per-exercise). Null on failure.
export const fetchEvalExercises = async (accessToken, userId, target, limit = 30) => {
  const qs = `?target=${encodeURIComponent(target)}&limit=${encodeURIComponent(limit)}`
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/exercises${qs}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? []
  }
  return null
}

// Model ids the gameplay generator (server-node) can actually run. The profile page's model list is
// the builder's (bigger) list; a GAMEPLAY comparison may only use the intersection.
export const fetchEvalGameplayModels = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/gameplay-models`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? []
  }
  return null
}

// Start a compare-models batch. Returns { batch_id, total } straight away; the evals run in the
// background and land in the history as ordinary rows carrying the batch id. Throws with the
// service's message on failure so the form can show it.
export const compareModels = async (accessToken, userId, body) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/compare-models`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ data: body }),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  let detail = resRaw ? `${resRaw.status} ${resRaw.statusText}` : "request failed"
  try {
    const j = await resRaw.json()
    if (j?.detail || j?.error) detail = j.detail || j.error
  } catch (_) {
    // keep the status-based detail
  }
  throw new Error(detail)
}

// Progress + roll-up of one batch. Null on failure (the caller keeps polling).
export const fetchEvalBatchStatus = async (accessToken, userId, batchId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/compare-models/status?batchId=${encodeURIComponent(batchId)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  return null
}

// --- Score a model (one model, one prompt type, graded alone on a fixed 0-100 yardstick) ----------

// Start a score batch: { target, model, label, inputs, judge_model, generations?, passes?, template?,
// prompt_name? }. Returns { batch_id, total, status } at once; poll fetchScoreStatus.
export const scoreModel = async (accessToken, userId, body) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/score`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ data: body }),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  let detail = resRaw ? `${resRaw.status} ${resRaw.statusText}` : "request failed"
  try {
    const j = await resRaw.json()
    if (j?.detail || j?.error) detail = j.detail || j.error
  } catch (_) {
    // keep the status-based detail
  }
  throw new Error(detail)
}

export const fetchScoreStatus = async (accessToken, userId, batchId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/score/status?batchId=${encodeURIComponent(batchId)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  return null
}

// Prompt types that have an absolute-scoring rubric: [{ target, noun, dimensions: [{name, weight, anchors}], cap }].
export const fetchScoringTargets = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/score/targets`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? []
  }
  return null
}

// --- Replay a game (a finished exercise replayed on another model, judged as two whole games) -----

// Finished exercises that can be replayed, newest first. Null on failure.
export const fetchReplayGames = async (accessToken, userId, limit = 30) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/replay/games?limit=${encodeURIComponent(limit)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? []
  }
  return null
}

// Start a replay. Returns { replay_id } straight away; the game plays in the background and its result
// lands in the history as one row carrying results.comparison = "replay". Throws with the service's
// message on failure so the form can show it.
export const replayGame = async (accessToken, userId, body) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/replay`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ data: body }),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  let detail = resRaw ? `${resRaw.status} ${resRaw.statusText}` : "request failed"
  try {
    const j = await resRaw.json()
    if (j?.detail || j?.error) detail = j.detail || j.error
  } catch (_) {
    // keep the status-based detail
  }
  throw new Error(detail)
}

// Progress of one replay. Null on failure (the caller keeps polling).
export const fetchReplayStatus = async (accessToken, userId, replayId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/replay/status?replayId=${encodeURIComponent(replayId)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  return null
}

// Run a V2 compare eval (regenerate both templates on a scenario, judge, aggregate)
export const runEval = async (accessToken, userId, body) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/evals/run`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ data: body }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }

  let detail = resRaw ? `${resRaw.status} ${resRaw.statusText}` : "request failed"
  try {
    const j = await resRaw.json()
    if (j?.detail || j?.error) detail = j.detail || j.error
  } catch (_) {
    // keep the status-based detail
  }
  throw new Error(detail)
}

// Fetch all simulation profiles list
export const fetchSimulationProfilesList = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profile-lists`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

export const fetchScenarioSuggestions = async (accessToken, userId, { orgId, profileId = null, themeKey = "", category = null }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/scenario-suggestions`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // (category, themeKey) select one cached list; category omitted = Cyber default, themeKey omitted = no focus
    body: JSON.stringify({ params: { orgId, profileId, ...(themeKey ? { themeKey } : {}), ...(category ? { category } : {}) } }),
  })
  if (resRaw?.status === 200) {
    const j = await resRaw.json()
    return j?.getScenarioSuggestions ?? j?.data ?? j ?? null
  }
  return null
}

export const refreshScenarioSuggestions = async (accessToken, userId, { orgId, profileId, theme = null, category = null }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/scenario-suggestions/refresh`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // theme: optional free-text focus (e.g. "natural disaster"); category: the domain (Cyber default / BC-DR IT / BC-DR Business / Other Crisis)
    body: JSON.stringify({ params: { orgId, profileId, ...(theme ? { theme } : {}), ...(category ? { category } : {}) } }),
  })
  if (resRaw?.status === 200) {
    const j = await resRaw.json()
    return j?.refreshScenarioSuggestions ?? j?.data ?? j ?? null
  }
  return null
}

// Create a create sccenario profiles
export const createScenarioProfiles = async (accessToken, userId, { NewProfileName, llm_model }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data: { profile_name: NewProfileName, llm_model: llm_model },
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json()
    const errorMsg = errorBody?.error || errorBody?.message || "Bad request while creating scenario profile."
    throw new Error(errorMsg)
  } else {
    throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
  }

  return result
}

// Delete a scenario profile
export const deleteScenarioProfile = async (accessToken, userId, { profile }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/delete`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      profile: profile,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const errorMsg = errorBody?.error || errorBody?.message || "Bad request while deleting profile."
    throw new Error(errorMsg)
  } else if (resRaw?.status === 404) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const errorMsg = errorBody?.error || errorBody?.message || "Profile not found."
    throw new Error(errorMsg)
  } else {
    throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
  }

  return result
}

// Duplicate a scenario profile
export const duplicateScenarioProfile = async (accessToken, userId, { sourceProfileName, newProfileName }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/duplicate`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      source_profile_name: sourceProfileName,
      new_profile_name: newProfileName,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const errorMsg = errorBody?.error || errorBody?.message || "Bad request while duplicating profile."
    throw new Error(errorMsg)
  } else if (resRaw?.status === 404) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const errorMsg = errorBody?.error || errorBody?.message || "Source profile not found."
    throw new Error(errorMsg)
  } else {
    throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
  }

  return result
}

// Rename a scenario profile
export const renameScenarioProfile = async (accessToken, userId, { profileName, newProfileName }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/rename`, {
    method: "PUT",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      profile: profileName,
      data: {
        new_profile_name: newProfileName,
      },
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const errorMsg = errorBody?.error || errorBody?.message || "Bad request while renaming profile."
    throw new Error(errorMsg)
  } else if (resRaw?.status === 404) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const errorMsg = errorBody?.error || errorBody?.message || "Profile not found."
    throw new Error(errorMsg)
  } else {
    throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
  }

  return result
}

// Export a scenario profile as a downloadable JSON
export const exportScenarioProfile = async (accessToken, userId, { profileId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/${encodeRFC3986URIComponent(profileId)}/export`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 404) {
    const errorBody = await resRaw.json().catch(() => ({}))
    throw new Error(errorBody?.error || errorBody?.message || "Profile not found.")
  }
  throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
}

// Preflight an import — validate payload and report name conflicts without writing
export const preflightImportScenarioProfile = async (accessToken, userId, { payload }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/import/preflight`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ payload }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const err = new Error(errorBody?.error || errorBody?.message || "Validation failed.")
    err.details = errorBody?.details || []
    throw err
  } else if (resRaw?.status === 413) {
    throw new Error("Import file is too large (max 100MB).")
  }
  throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
}

// Import a scenario profile from an export JSON, with conflict handling
export const importScenarioProfile = async (accessToken, userId, { payload, conflictStrategy }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/import`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ payload, conflict_strategy: conflictStrategy }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const err = new Error(errorBody?.error || errorBody?.message || "Validation failed.")
    err.details = errorBody?.details || []
    throw err
  } else if (resRaw?.status === 409) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const err = new Error(errorBody?.error || errorBody?.message || "Profile already exists.")
    err.status = 409
    throw err
  } else if (resRaw?.status === 413) {
    throw new Error("Import file is too large (max 100MB).")
  }
  throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
}

// Export ALL prompt profiles as a single prompt-profile-bundle (CT-1168)
export const exportAllScenarioProfiles = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/export-all`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  }
  throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
}

// Preflight a bundle import — validate + report per-profile conflicts without writing (CT-1168)
export const preflightImportScenarioBundle = async (accessToken, userId, { payload }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/import/preflight-bundle`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ payload }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const err = new Error(errorBody?.error || errorBody?.message || "Bundle validation failed.")
    err.details = errorBody?.details || []
    throw err
  } else if (resRaw?.status === 413) {
    throw new Error("Import file is too large (max 100MB).")
  }
  throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
}

// Import a prompt-profile-bundle with a per-profile action (create|overwrite|rename|skip) (CT-1168)
export const importScenarioBundle = async (accessToken, userId, { items }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/profiles/import-bundle`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ items }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.data ?? null
  } else if (resRaw?.status === 400) {
    const errorBody = await resRaw.json().catch(() => ({}))
    const err = new Error(errorBody?.error || errorBody?.message || "Bundle import failed.")
    err.details = errorBody?.details || []
    throw err
  } else if (resRaw?.status === 413) {
    throw new Error("Import file is too large (max 100MB).")
  }
  throw new Error(`Unexpected error: ${resRaw?.statusText || "Unknown error"}`)
}

// Create a scenario from a template
export const scenarioCustomize = async (accessToken, userId, { simulationId, data }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/customize`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

// Update a specific simulation prompt
export const updateSimulationConfig = async (accessToken, userId, { id, updatedData }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/${encodeRFC3986URIComponent(id)}`, {
    method: "PUT",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data: updatedData,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

// Upload Excel file
export const uploadExcelFile = async (accessToken, userId, { file, turnType }) => {
  // Parse the file data
  const fileParsePromise = new Promise((resolve, reject) => {
    const fileName = `${file.name}`
    pageLogger.info(`Parsing excel file from '${fileName}' (size: ${file.size})...`)
    const reader = new FileReader()
    reader.readAsArrayBuffer(file)
    reader.onload = (event) => {
      const b64 = Buffer.from(event.target.result).toString("base64")
      pageLogger.info(`Finished parsing excel file data from '${fileName}'.`)
      resolve({ dataB64: b64, fileName })
    }
    reader.onerror = () => {
      pageLogger.error(`Error parsing excel data from file '${fileName}'!`)
      reject()
    }
  })

  const { dataB64, fileName } = await fileParsePromise

  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/upload`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data: {
        dataB64,
        fileName,
        turn_type: turnType,
      },
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

// Upload Images
export const uploadImage = async (file) => {
  throw Error("Not implemented")
  // try {
  //   const formData = new FormData();
  //   formData.append("file", file);

  //   const response = await apiClient.post("/v1/files/upload", formData, {
  //     headers: { "Content-Type": "multipart/form-data" },
  //   });

  //   return response.data;
  // } catch (error) {
  //   console.error("Error uploading Excel file:", error);
  //   throw error;
  // }
}

// Fetch list of all templates
export const fetchScenarioTemplates = async (accessToken, userId) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/templates`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

// Sync simulation to ArangoDB (Edit save flow) - direct update, no Excel download
export const syncSimulationToArangoDB = async (accessToken, userId, { simulationId, orgAccess, testKey }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/sync-to-arangodb`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ orgAccess, testKey }),
  })

  let message = null
  if (resRaw?.status === 200) {
    return { ok: true }
  }
  if (resRaw?.status >= 400) {
    const errData = await resRaw.json().catch(() => ({}))
    message = errData?.error ?? errData?.message ?? `Sync failed: ${resRaw.status}`
  }
  return { ok: false, message }
}

// Obtain the adaptive_spec for the screen-3 review card. Best-effort: the endpoint returns the
// persisted spec if present, else classifies + generates one (and persists it), else {is_adaptive:false}.
// Read-shaped (get-or-compute) — safe to call on mount. Returns the spec object or null on failure.
// Step-1 roles confirmation: normalize the free-text "Participating roles" into a
// clean roster ([{name}]) the author confirms before generation. source:
// "deterministic" (already clean), "llm" (parsed), "fallback" (comma-split; warn).
export const normalizeSimulationRoles = async (accessToken, userId, { simulationId, playerRole }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/normalize-roles`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ player_role: playerRole }),
  })
  if (resRaw?.status === 200) {
    const body = await resRaw.json().catch(() => null)
    return body?.data?.data ?? null
  }
  return null
}

export const analyzeSimulationAdaptive = async (accessToken, userId, { simulationId, orgAccess, instruction, regenerate, reconcileText }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/analyze-adaptive`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // regenerate=true forces a clean recompute; reconcileText carries the author's whole-rubric
    // flat-text edit for the server to re-map back into the structured spec.
    body: JSON.stringify({ orgAccess, instruction, regenerate, reconcile_text: reconcileText }),
  })

  if (resRaw?.status === 200) {
    const body = await resRaw.json().catch(() => null)
    return body?.data?.data?.adaptive_spec ?? null
  }

  return null
}

// CTF Questions stage: propose an AI rewrite of ONE question (builder validates it against the set;
// nothing is stored — the caller saves it through PATCH on Accept). Throws with the server's
// customer-safe message on failure.
export const aiEditCtfInject = async (accessToken, userId, { simulationId, injectId, instruction, orgAccess }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/ctf-injects/ai-edit`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ inject_id: injectId, instruction, orgAccess }),
  })
  const body = await resRaw?.json?.().catch(() => null)
  if (resRaw?.status === 200 && body?.data?.data?.inject) return body.data.data.inject
  throw new Error(body?.error || body?.message || "The AI couldn't rewrite this question. Please try again.")
}

// CTF run of show — "Ask AI to change this" for a beat / round / debrief / event meta (spec §9).
// Same gates as the per-question AI edit; returns the proposed object (nothing is saved — the author
// accepts it into the outline and saves the set).
export const aiEditCtfRunOfShowPart = async (accessToken, userId, { simulationId, part, id, instruction, orgAccess }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/ctf-run-of-show/ai-edit`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ part, id: id ?? null, instruction, orgAccess }),
  })
  const body = await resRaw?.json?.().catch(() => null)
  if (resRaw?.status === 200 && body?.data?.data?.proposal) return body.data.data.proposal
  throw new Error(body?.error || body?.message || "The AI couldn't rewrite this part. Please try again.")
}

// Streaming AI edit — calls onProgress({ step, percent, message, warning?, error? }) as SSE events arrive.
// Resolves with the last event data on success, throws on error.
export const aiEditScenarioStream = async (accessToken, userId, { simulationId, instructions, displayName, testKey, orgAccess }, onProgress) => {
  const response = await fetch(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/ai-edit/stream`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ instructions, display_name: displayName, testKey, orgAccess }),
  })

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}))
    throw new Error(errData?.error || errData?.message || `AI edit failed: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let lastData = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop()
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        const data = JSON.parse(line.slice(6))
        lastData = data
        onProgress(data)
        if (data.step === "error") {
          throw new Error(data.error || "AI edit failed")
        }
      }
    }
  }

  return lastData
}

// "Describe a change" refine, streamed. Mirrors aiEditScenarioStream: this hits the
// builder DIRECTLY through nginx (600s read timeout) instead of the Hasura action,
// which carries a 300s ceiling we cannot raise from the app. onChunk receives text
// as it arrives; the resolved value is the complete rewritten artifact.
export const promptChainingStream = async (accessToken, userId, { simulationId, sessionId, promptData }, onChunk = () => {}) => {
  const response = await fetch(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/prompt-chaining/stream`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ session_id: sessionId, ...promptData }),
  })
  if (!response.ok || !response.body) {
    throw new Error(`Unexpected error: ${response?.statusText || "Unknown error"}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let full = ""
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop()
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue
      const data = JSON.parse(line.slice(6))
      if (data.error) throw new Error(data.error)
      if (data.chunk) {
        full += data.chunk
        onChunk(full)
      }
      // The server sends the authoritative full text on completion — prefer it
      // over the locally accumulated copy.
      if (data.done && data.llm_response) full = data.llm_response
    }
  }
  if (!full.trim()) throw new Error("The AI returned an empty result. Please try again.")
  return full
}

// uploading the excel
export const uploadingExcel = async (accessToken, userId, { simulationId, orgAccess }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/download`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      orgAccess,
      upload: true,
    }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    // Parse JSON?
  } else if (resRaw?.status >= 400) {
    // Parse JSON
    message = (await resRaw.json())?.error ?? `There was a problem uploading the scenario: ${resRaw.status}`
  }

  return { result, message }
}

// Delete a scenario by ID
export const deleteScenario = async (accessToken, userId, { simulationId }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })

  // Surface failures to callers instead of returning null silently — otherwise a denied
  // (403) or missing (404) delete looks identical to success and produces a false toast.
  if (resRaw?.status !== 200) {
    throw new Error(`Delete failed (status ${resRaw?.status ?? "no response"})`)
  }

  return (await resRaw.json())?.data?.data ?? null
}

// Create a scenario from a template
export const createScenarioFromTemplate = async (accessToken, userId, { simulationId }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/copy`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

export const bulkCustomizeScenarios = async (accessToken, userId, bulkCustomizeData) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/bulk-customize`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(bulkCustomizeData),
  })

  let result = null
  let status = resRaw?.status

  if (status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  } else if (status) {
    const errorData = await resRaw.json().catch(() => ({}))
    throw new Error(errorData.error || "Failed to bulk customize scenarios")
  }

  return { result, status }
}

export const getBulkCustomizeStatus = async (accessToken, userId, { orgId } = {}) => {
  const url = orgId
    ? `${API_BASE_URL}/v1/simulations/bulk-customize/status?orgId=${encodeURIComponent(orgId)}`
    : `${API_BASE_URL}/v1/simulations/bulk-customize/status`

  const resRaw = await fetchOrLogError(url, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null
  let status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.data ?? null
  } else if (status) {
    const errorData = await resRaw.json().catch(() => ({}))
    throw new Error(errorData.error || "Failed to get bulk customize status")
  }
  return { result, status }
}

// Answer a question
// export const answerQuestion = async (simulationId, questionId, answer) => {
//   try {
//     const response = await apiClient.patch(`/v1/simulations/${simulationId}/questions/${questionId}`,
//       { answer },
//       { headers: { "Content-Type": "application/json" } }
//     );

//     return response.data;
//   } catch (error) {
//     console.error("Error answering question:", error);
//     throw error;
//   }
// };

// Questions Answer a submit
export const submitQuestions = async (accessToken, userId, { simulationId, dataQuestionsAnswer }) => {
  // Send to Hasura
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/questions`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data: { questions: dataQuestionsAnswer },
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

// Fetch list of all generated scenarios
export const fetchNontemplatesScenarios = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/non-templates`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

export const extractContextPolicy = async (accessToken, userId, { simulationId, file, promptType, media_type, profile_name }) => {
  try {
    // Parse the file data
    const fileParsePromise = new Promise((resolve, reject) => {
      const fileName = `${file.name}`
      pageLogger.info(`Parsing file from '${fileName}' (size: ${file.size})...`)
      const reader = new FileReader()
      reader.readAsArrayBuffer(file)
      reader.onload = (event) => {
        const b64 = Buffer.from(event.target.result).toString("base64")
        pageLogger.info(`Finished parsing file data from '${fileName}'.`)
        resolve({ dataB64: b64 })
      }
      reader.onerror = () => {
        pageLogger.error(`Error parsing data from file '${fileName}'!`)
        reject(new Error("Failed to parse file"))
      }
    })

    const { dataB64 } = await fileParsePromise

    // Use new endpoint without simulation_id if available, otherwise fall back to old endpoint
    const endpoint = simulationId
      ? `${API_BASE_URL}/v1/simulations/${simulationId}/extract-context-policy-file`
      : `${API_BASE_URL}/v1/simulations/extract-context-policy`

    const requestBody = simulationId
      ? {
          data: {
            prompt_type: promptType,
            file_content: dataB64,
            media_type: media_type,
          },
        }
      : {
          prompt_type: promptType,
          file_content: dataB64,
          media_type: media_type,
          ...(profile_name && { profile_name }),
        }

    const response = await fetchOrLogError(endpoint, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify(requestBody),
    })

    if (response?.status === 200) {
      const result = simulationId ? ((await response.json())?.data?.data ?? null) : ((await response.json())?.data ?? null)
      return result
    } else if (response?.status === 400) {
      const errorBody = await response.json()
      const errorMsg = errorBody?.error || errorBody?.message || "Bad request while extracting context policy."
      throw new Error(errorMsg)
    } else {
      throw new Error(`Unexpected error: ${response?.statusText || "Unknown error"}`)
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.response?.data?.error || error?.message || "Something went wrong while extracting uploaded file."
    throw new Error(message)
  }
}

export const downloadOutputScenario = async (accessToken, userId, { simulationId, scenarioFileName }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/download`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let fileContentB64 = null
  let fileName = null

  if (resRaw?.status === 200) {
    // Parse JSON
    const payload = (await resRaw.json())?.result ?? null
    fileContentB64 = payload?.fileContent
    fileName = payload?.fileName

    // Kick off file download in browser
    const downloadBytes = Uint8Array.from(Buffer.from(fileContentB64, "base64"))
    const downloadBlob = new Blob([downloadBytes])
    const url = window.URL.createObjectURL(downloadBlob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", fileName)
    document.body.appendChild(link)
    link.click()
    // Cleanup URL object
    link.remove()
    window.URL.revokeObjectURL(url)
  } else {
    throw Error(`Error downloading spreadsheet for simulation with id '${simulationId}': Got response code ${resRaw?.status}`)
  }
}

// Delete a question
// export const deleteQuestion = async (simulationId, questionId) => {
//   try {
//     const response = await apiClient.delete(`/v1/simulations/${simulationId}/questions/${questionId}`, {
//       headers: { "Content-Type": "application/json" },
//     });

//     return response.data;
//   } catch (error) {
//     console.error("Error deleting question:", error);
//     throw error;
//   }
// }

// Fetch list of all templates simulations/templates/2504
export const fetchTemplatesScenarios = async (accessToken, userId, { simulationId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeRFC3986URIComponent(simulationId)}/templates`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data?.data ?? null
  }

  return result
}

// perf/lean-org-fetch: dedicated endpoint returning the FULL org variables (heavy
// keys included). getOrg() no longer needs to carry them for these consumers.
export const getOrgVariables = async (accessToken, userId, { id }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/org/${encodeRFC3986URIComponent(id)}/variables`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

const fetchOrgVariables = async (accessToken, userId, { orgId }) => {
  // perf/lean-org-fetch: fetch the heavy variables from the dedicated endpoint
  // instead of the whole org doc. All dependents (core-systems profiles, supporting
  // docs, IR policy/policys) read only variable keys, so this is a drop-in swap.
  const orgVars = await getOrgVariables(accessToken, userId, { id: orgId })
  return orgVars?.variables ?? null
}

// perf/lean-org-fetch: /getOrg is trimmed (heavy variable keys stripped), so a
// read-modify-WRITE of the org variables can no longer read its base off getOrg — it
// would drop the heavy sibling keys on writeback. Rebuild the FULL org needed by those
// paths from the light org doc (top-level fields + `updated` revision token) overlaid
// with the full variables from /org/:id/variables. Fetched in parallel; the light org
// only supplies the revision + any non-variable field a caller might read.
const getOrgWithFullVariables = async (accessToken, userId, { orgId }) => {
  const [lightOrg, fullVariables] = await Promise.all([getOrg(accessToken, userId, { id: orgId }), fetchOrgVariables(accessToken, userId, { orgId })])
  if (!lightOrg && !fullVariables) return null
  return { ...(lightOrg ?? {}), variables: fullVariables ?? lightOrg?.variables ?? {} }
}

// Fetch "Your Core Systems" stored content for the organization
const ORG_VARIABLE_CORE_SYSTEMS_ID = "core_systems"
const ORG_VARIABLE_CORE_SYSTEM_SUPPORTING_DOCS_ID = "core_system_supporting_documents"
const ORG_VARIABLE_TYPE_STRING = "STRING"
const ORG_VARIABLE_TYPE_ARRAY = "ARRAY"

// Fetch supporting documents stored alongside core systems
export const fetchOrgCoreSystemSupportingDocs = async (accessToken, userId, { orgId }) => {
  const theOrgVars = await fetchOrgVariables(accessToken, userId, { orgId })
  const supportingDocs = theOrgVars?.[ORG_VARIABLE_CORE_SYSTEM_SUPPORTING_DOCS_ID]?.value
  return Array.isArray(supportingDocs) ? supportingDocs : []
}

const ORG_VARIABLE_CORE_SYSTEMS_PROFILES_ID = "core_systems_profiles"

// Fetch all core systems profiles for an org
export const fetchOrgCoreSystemsProfiles = async (accessToken, userId, { orgId }) => {
  const theOrgVars = await fetchOrgVariables(accessToken, userId, { orgId })
  const profiles = theOrgVars?.[ORG_VARIABLE_CORE_SYSTEMS_PROFILES_ID]?.value
  return Array.isArray(profiles) ? profiles : []
}

/**
 * Patch specific org variables without sending the full variable map.
 * Sends only changed keys, not the full org variables map.
 */
export const patchOrgVariables = async (accessToken, userId, { orgId, variableUpdates, expectedUpdated = null }) => {
  if (!variableUpdates || !Object.keys(variableUpdates).length) return null
  // Optimistic-concurrency (opt-in): when the caller supplies the org's last-known `updated`
  // timestamp, smuggle it as a control key inside the variables payload. The server extracts +
  // strips __expectedUpdated and rejects the write (409 ORG_REVISION_CONFLICT) if the org changed
  // since. Callers that omit expectedUpdated behave exactly as before (last-write-wins).
  const variables = expectedUpdated != null ? { ...variableUpdates, __expectedUpdated: expectedUpdated } : variableUpdates
  const updatedOrg = await updateOrg(accessToken, userId, { id: orgId, variables })
  return updatedOrg
}

// True when an updateOrg error is an optimistic-concurrency conflict. Detected by both the HTTP
// status and the machine token in the message, since Hasura may normalize the status but relays
// the handler's message verbatim as errors[0].message.
export const isOrgRevisionConflict = (err) =>
  err?.status === 409 || err?.serverCode === "ORG_REVISION_CONFLICT" || /ORG_REVISION_CONFLICT/.test(err?.message || "")

// Persist the runbooks / IR-plans array (ir_policys) with optimistic concurrency. Unlike the
// legacy updateOrgVariables (which re-sends the WHOLE variables object and last-writes-wins),
// this sends ONLY ir_policys and, when the caller passes the org's last-known `updated`, guards
// the write so it is rejected (409 ORG_REVISION_CONFLICT) if the org changed since — e.g. the
// per-minute processIRPoliciesForOrg cron filled in extracted text. Callers surface the conflict
// (refetch + ask the user to retry) instead of clobbering the cron's extraction (H1). Only touching
// ir_policys also avoids resurrecting stale sibling variables (M1).
export const updateOrgRunbooks = async (accessToken, userId, { orgId, runbooks, expectedUpdated = null }) =>
  patchOrgVariables(accessToken, userId, {
    orgId,
    variableUpdates: { [ORG_VARIABLE_IR_POLICYS_ID]: { type: ORG_VARIABLE_TYPE_ARRAY, value: runbooks } },
    expectedUpdated,
  })

// Save one IR plan into the org library via read-modify-write on the FRESH server list —
// NEVER from a caller-supplied snapshot: the wizard's cached list can predate a delete made
// in Settings, and appending to it resurrects the deleted plan (customer-reported on ui-demo).
// A plan with the same name/file_name is REPLACED (re-upload = update), so same-name uploads
// can't create duplicate entries that the name-keyed wizard lookup then resolves to the OLD one.
// Conflict-retried once, mirroring mutateCoreSystemsProfiles.
export const saveOrgIRPlan = async (accessToken, userId, { orgId, plan }) => {
  let lastErr
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const org = await getOrgWithFullVariables(accessToken, userId, { orgId })
    const current = org?.variables?.[ORG_VARIABLE_IR_POLICYS_ID]?.value
    const plans = Array.isArray(current) ? current : []
    const sameName = (p) => (p?.name || p?.file_name) === (plan.name || plan.file_name)
    const next = [...plans.filter((p) => !sameName(p)), plan]
    try {
      await patchOrgVariables(accessToken, userId, {
        orgId,
        variableUpdates: { [ORG_VARIABLE_IR_POLICYS_ID]: { type: ORG_VARIABLE_TYPE_ARRAY, value: next } },
        expectedUpdated: org?.updated ?? null,
      })
      return next
    } catch (err) {
      lastErr = err
      if (isOrgRevisionConflict(err)) {
        if (attempt === 0) continue // stale base — re-read fresh plans + re-apply once
        throw new Error("The plan library was changed in another session. Please reload the page and try again.")
      }
      throw err
    }
  }
  throw lastErr
}

// Read-modify-write of core_systems_profiles with optimistic concurrency. `transform(profiles)`
// returns the next profiles array (it may throw to abort with a validation error). On a revision
// conflict we re-read the FRESH profiles and re-apply the transform once, so concurrent edits to
// *other* profiles are preserved instead of silently clobbered.
const mutateCoreSystemsProfiles = async (accessToken, userId, { orgId, transform }) => {
  let lastErr
  for (let attempt = 0; attempt < 2; attempt += 1) {
    // perf/lean-org-fetch: read the profiles from the FULL variables (getOrg is trimmed)
    // while still taking the optimistic-concurrency `updated` token from the light org.
    const org = await getOrgWithFullVariables(accessToken, userId, { orgId })
    const current = org?.variables?.[ORG_VARIABLE_CORE_SYSTEMS_PROFILES_ID]?.value
    const profiles = Array.isArray(current) ? current : []
    const next = transform(profiles)
    try {
      return await patchOrgVariables(accessToken, userId, {
        orgId,
        variableUpdates: { [ORG_VARIABLE_CORE_SYSTEMS_PROFILES_ID]: { type: ORG_VARIABLE_TYPE_ARRAY, value: next } },
        expectedUpdated: org?.updated ?? null,
      })
    } catch (err) {
      lastErr = err
      if (isOrgRevisionConflict(err)) {
        if (attempt === 0) continue // stale base — re-read fresh profiles + re-apply once
        throw new Error("These settings were changed in another session. Please reload the page and try again.")
      }
      throw err
    }
  }
  throw lastErr
}

// Default profile name, generated against the FRESH server-side list (inside the
// read-modify-write transform, so it re-runs on a revision-conflict retry). Generating it
// from the page's local state raced the server: delete/add churn or a second tab could
// leave the local list stale, and the server's case-insensitive uniqueness check then
// rejected the add with "Duplicate profile name: 'Profile N'" (customer-reported on demo).
const nextDefaultProfileName = (profiles) => {
  const taken = new Set(profiles.map((p) => String(p?.name ?? "").toLowerCase()))
  let counter = profiles.length + 1
  while (taken.has(`profile ${counter}`)) counter += 1
  return `Profile ${counter}`
}

// CRUD for core systems profiles
export const addCoreSystemsProfile = async (accessToken, userId, { orgId, profile }) =>
  mutateCoreSystemsProfiles(accessToken, userId, {
    orgId,
    transform: (profiles) => {
      const now = new Date().toISOString()
      const newProfile = {
        id: profile.id || uuidv4(),
        // No explicit name → collision-proof default. An explicit name is kept as-is:
        // if the user picked a duplicate, the server's validator should say so loudly.
        name: profile.name || nextDefaultProfileName(profiles),
        value: profile.value || "",
        is_primary: profile.is_primary || false,
        supporting_doc_ids: profile.supporting_doc_ids || [],
        created_at: profile.created_at || now,
        updated_at: profile.updated_at || now,
      }
      if (profiles.length === 0) newProfile.is_primary = true
      return [...profiles, newProfile]
    },
  })

export const updateCoreSystemsProfile = async (accessToken, userId, { orgId, profileId, updates }) =>
  mutateCoreSystemsProfiles(accessToken, userId, {
    orgId,
    transform: (profiles) => {
      const idx = profiles.findIndex((p) => p.id === profileId)
      if (idx === -1) throw new Error(`Profile ${profileId} not found`)
      const next = [...profiles]
      next[idx] = { ...next[idx], ...updates, updated_at: new Date().toISOString() }
      return next
    },
  })

export const deleteCoreSystemsProfile = async (accessToken, userId, { orgId, profileId }) =>
  mutateCoreSystemsProfiles(accessToken, userId, {
    orgId,
    transform: (profiles) => {
      if (profiles.length <= 1) throw new Error("Cannot delete the last profile")
      const profile = profiles.find((p) => p.id === profileId)
      if (profile?.is_primary) throw new Error("Cannot delete the primary profile")
      return profiles.filter((p) => p.id !== profileId)
    },
  })

export const setPrimaryCoreSystemsProfile = async (accessToken, userId, { orgId, profileId }) =>
  mutateCoreSystemsProfiles(accessToken, userId, {
    orgId,
    transform: (profiles) => {
      if (!profiles.some((p) => p.id === profileId)) throw new Error(`Profile ${profileId} not found`)
      return profiles.map((p) => ({ ...p, is_primary: p.id === profileId }))
    },
  })

// Clear the primary designation so no profile is the default. Downstream
// consumers fall back to the first profile (find(is_primary) || profiles[0]),
// so a no-primary state is safe.
export const unsetPrimaryCoreSystemsProfile = async (accessToken, userId, { orgId, profileId }) =>
  mutateCoreSystemsProfiles(accessToken, userId, {
    orgId,
    transform: (profiles) => {
      if (!profiles.some((p) => p.id === profileId)) throw new Error(`Profile ${profileId} not found`)
      return profiles.map((p) => (p.id === profileId ? { ...p, is_primary: false } : p))
    },
  })

// Fetch "Incident Response Plan" stored content from the organization
const ORG_VARIABLE_INCIDENT_RESPONSE_PLAN_ID = "ir_policy"
export const fetchOrgIncidentResponsePlan = async (accessToken, userId, { orgId }) => {
  const theOrgVars = await fetchOrgVariables(accessToken, userId, { orgId })
  return theOrgVars?.[ORG_VARIABLE_INCIDENT_RESPONSE_PLAN_ID]?.value ?? null
}

// Organization variable IDs for onboarding
const ORG_VARIABLE_IR_POLICYS_ID = "ir_policys"

// Fetch "IR Plans & Runbooks" stored content from the organization
export const fetchOrgIRPolicys = async (accessToken, userId, { orgId }) => {
  const theOrgVars = await fetchOrgVariables(accessToken, userId, { orgId })
  const irPolicys = theOrgVars?.[ORG_VARIABLE_IR_POLICYS_ID]?.value
  return Array.isArray(irPolicys) ? irPolicys : []
}

// Extract base64 and media_type from data URL
const parseDataUrl = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return null

  // Format: data:media_type;filename=...;base64,base64Content
  const match = dataUrl.match(/^data:([^;]+);(?:filename=[^;]+;)?base64,(.+)$/)
  if (!match) return null

  return {
    media_type: match[1],
    base64Content: match[2],
  }
}

// Extract IR policy from first attachment using extract API
export const extractIRPolicyFromAttachment = async (accessToken, userId, { simulationId, fileUrl, mediaType }) => {
  try {
    const parsed = parseDataUrl(fileUrl)
    if (!parsed) {
      throw new Error("Invalid data URL format")
    }

    const fileContent = parsed.base64Content
    const media_type = mediaType || parsed.media_type || "application/pdf"

    const response = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${simulationId}/extract-context-policy-file`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify({
        data: {
          prompt_type: "extract_ir_policy_prompt",
          file_content: fileContent,
          media_type: media_type,
        },
      }),
    })

    if (response?.status === 200) {
      const result = (await response.json())?.data?.data ?? null
      return result
    } else if (response?.status === 400) {
      const errorBody = await response.json()
      const errorMsg = errorBody?.error || errorBody?.message || "Bad request while extracting IR policy."
      throw new Error(errorMsg)
    } else {
      throw new Error(`Unexpected error: ${response?.statusText || "Unknown error"}`)
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong while extracting IR policy from attachment."
    throw new Error(message)
  }
}

// Set stored variable content for the organization
// Set "Your Core Systems" stored content for the organization
/**
 * Generate security analysis document for an organization
 */
export const generateSecurityAnalysis = async (
  accessToken,
  userId,
  { org_id, org_name, company_url, attachments = [], regenerate = false, core_systems_profile_id = null }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/generatesecurityanalysis`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      input: {
        org_id,
        org_name,
        company_url,
        attachments,
        regenerate,
        core_systems_profile_id,
      },
    }),
  })

  let result = null
  let message = null

  if (resRaw?.status >= 400) {
    result = null
    try {
      const resJSON = await resRaw.json()
      message = resJSON?.message || resJSON?.error || `Error generating security analysis. Status: ${resRaw?.status}.`
    } catch {
      message = `Error generating security analysis. Status: ${resRaw?.status}.`
    }
  } else if (resRaw?.status === 200) {
    const resJSON = await resRaw.json()
    result = resJSON?.result ?? null
    message = null
  }

  return { message, result }
}

export const generateSecurityAnalysisStream = async (
  accessToken,
  userId,
  { org_id, org_name, company_url, attachments = [], regenerate = false, core_systems_profile_id = null },
  onProgress
) => {
  const response = await fetch(`${API_BASE_URL}/v1/generatesecurityanalysis/stream`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ org_id, org_name, company_url, attachments, regenerate, core_systems_profile_id }),
  })

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}))
    throw new Error(errData?.message || `Security analysis stream failed: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let lastData = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop()
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        const data = JSON.parse(line.slice(6))
        lastData = data
        onProgress(data)
        if (data.step === "error") {
          throw new Error(data.error || "Security analysis generation failed")
        }
      }
    }
  }

  return lastData
}

/**
 * Meeting Co-pilot (gameplay/tabletop) — POST /sessionCopilotStream.
 *
 * SSE over fetch (EventSource can't send Authorization / Auth-Access-Id headers).
 * This endpoint is an nginx passthrough, NOT a Hasura action, so we hit
 * `${API_BASE_URL}/sessionCopilotStream` directly with the standard auth headers.
 *
 * Streaming modes (interpret | challenge | answer): the server flushes SSE frames
 * in the shape `event: <name>\ndata: <json>\n\n`:
 *   - started {outputId}, token {text} (repeated), done {outputId, grounding},
 *     error {code, message} (code may be TOO_LARGE).
 * takeaways mode is NON-streaming: the server returns JSON (200 { ok, outputId,
 * items }) or 413 { skipped, reason } when over the token budget. We surface the
 * 413 as a friendly onError so the caller doesn't need to special-case it.
 *
 * @param {string} accessToken
 * @param {string} userId
 * @param {object} opts
 * @param {string} opts.sessionName
 * @param {'interpret'|'challenge'|'answer'|'takeaways'} opts.mode
 * @param {string} [opts.question] required for 'answer' mode
 * @param {string} [opts.org] manager cross-org override
 * @param {AbortSignal} [opts.signal]
 * @param {(data: object) => void} [opts.onStarted]
 * @param {(text: string) => void} [opts.onToken]
 * @param {(data: object) => void} [opts.onDone]  streaming: { outputId, grounding }; takeaways: { outputId, items }
 * @param {(err: { code?: string, message: string }) => void} [opts.onError]
 */
// Voice-path requests MUST NOT hang. The mic orb shows "Thinking…" for exactly as long as a
// turn/STT request is in flight, so a stalled connection — a server restart mid-request, a proxy
// dropping the socket, a network stall — strands the orb there permanently: bare fetch() has no
// timeout, so the await never settles and no catch ever runs. Every voice call gets a deadline
// and surfaces as a normal rejection, which the callers already handle by resetting the orb and
// asking the player to repeat. These endpoints all return a complete blob/JSON (never an
// incremental stream), so aborting on the deadline cannot truncate a good response.
const VOICE_FETCH_TIMEOUT_MS = 30000

const fetchVoiceWithTimeout = async (url, options = {}, timeoutMs = VOICE_FETCH_TIMEOUT_MS) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timeoutId)
  }
}

// Mic-mode voice assistant TTS: POST the text Tracy should speak, get back a WAV
// synthesized by the self-hosted Kokoro voice-pipeline (via the server-node proxy).
// Returns an object URL to play, or null when there's nothing speakable (204).
export const fetchCopilotTts = async (accessToken, userId, { text, summarize = false }) => {
  const response = await fetchVoiceWithTimeout(`${API_BASE_URL}/sessionCopilotTts`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(summarize ? { text, summarize: true } : { text }),
  })
  if (response.status === 204) return null
  if (!response.ok) throw new Error(`sessionCopilotTts ${response.status}`)
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

// Summarize a long action result into ONE natural spoken recap and return the TEXT (not audio),
// so the WS-TTS path can speak + persist the SUMMARY instead of reading the whole IR response
// line-by-line. Falls back to null on any error (caller then uses the raw text).
export const fetchCopilotSpeechSummary = async (accessToken, userId, { text }) => {
  try {
    const response = await fetchVoiceWithTimeout(`${API_BASE_URL}/sessionCopilotTts`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify({ text, summarize: true, textOnly: true }),
    })
    if (!response.ok) return null
    const data = await response.json()
    return typeof data?.text === "string" && data.text.trim() ? data.text.trim() : null
  } catch {
    return null
  }
}

// Tracy Voice v2 turn orchestrator: POST one spoken utterance; the SERVER owns the action
// boundary and returns { mode } telling the client what to do:
//   { mode: "answer", text }                          → speak the grounded reply
//   { mode: "confirm", text, pendingActionToken }      → speak, then replay the token on the next turn
//   { mode: "execute", phrase, text }                  → submit via the existing rank→pick path
// pendingActionToken is echoed back on the confirming turn so the server can verify + resolve it.
// MERGED wake+turn: pass `text` (the RAW utterance, wake phrase included) and the server classifies
// it before doing any work, returning `wake` on the response. That replaces the separate
// /sessionCopilotWake round trip that used to precede every call — an addressed utterance paid two
// round trips plus a duplicated JWT verify and org read for a ~1ms decision.
// `hasLastSpoken` is the one thing the server cannot know: whether we have a line to repeat. Without
// it the server could not tell "say that again" (client re-speaks, no turn) from the same words with
// nothing to repeat (falls through to a real turn).
// `transcript` still works for the legacy path, so the server can be deployed ahead of this.
export const sessionCopilotTurn = async (
  accessToken,
  userId,
  { sessionName, transcript, text, hotWindow, hasLastSpoken, playerId, turnId, pendingActionToken }
) => {
  const response = await fetchVoiceWithTimeout(`${API_BASE_URL}/sessionCopilotTurn`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ sessionName, transcript, text, hotWindow, hasLastSpoken, playerId, turnId, pendingActionToken }),
  })
  if (!response.ok) throw new Error(`sessionCopilotTurn ${response.status}`)
  return response.json()
}

// Streaming variant of the turn. Same endpoint, `stream: true`, SSE instead of one JSON blob:
//
//   event: verdict  { ...wake, mode }   -- ALWAYS first; the six client branches key off it
//   event: delta    { text }            -- answer text, phrase by phrase
//   event: done     { mode, finalText? }
//
// Why it exists: the buffered turn returned a finished string, so nothing could be SPOKEN until the
// whole answer had been generated -- 2-6s of silence per utterance. Streaming lets TTS start after
// sentence one. `delta` is surfaced as onToken so it plugs straight into the existing
// streamAndSpeak chunker with no second chunker anywhere.
//
// The server enforces the ordering (see voiceTurnSse.js); this mirrors it defensively, because a
// delta treated as metadata (or metadata spoken as text) would both fail silently.
export const sessionCopilotTurnStream = async (
  accessToken,
  userId,
  { sessionName, text, hotWindow, hasLastSpoken, playerId, turnId, pendingActionToken, signal, onVerdict, onToken, onDone, onError }
) => {
  let response
  try {
    response = await fetch(`${API_BASE_URL}/sessionCopilotTurn`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify({ sessionName, text, hotWindow, hasLastSpoken, playerId, turnId, pendingActionToken, stream: true }),
      signal,
    })
  } catch (err) {
    onError?.({ message: err?.message || "turn request failed" })
    return
  }
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}))
    onError?.({ message: errData?.message || `sessionCopilotTurn ${response.status}` })
    return
  }
  if (!response.body) {
    onError?.({ message: "The turn stream returned no data." })
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let sawVerdict = false

  const parseFrame = (frame) => {
    let eventName = "message"
    const dataLines = []
    for (const rawLine of frame.split("\n")) {
      const line = rawLine.replace(/\r$/, "")
      if (!line || line.startsWith(":")) continue // ": ka" keepalive
      if (line.startsWith("event:")) eventName = line.slice(6).trim()
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""))
    }
    if (!dataLines.length) return null
    try {
      return { event: eventName, data: JSON.parse(dataLines.join("\n")) }
    } catch {
      return null
    }
  }

  const handle = (frame) => {
    const parsed = parseFrame(frame)
    if (!parsed) return
    const { event, data } = parsed
    if (event === "verdict") {
      sawVerdict = true
      onVerdict?.(data)
    } else if (event === "delta") {
      // A delta before a verdict means the contract broke. Drop it rather than speaking text the
      // client has not been told how to route -- losing a token beats speaking an unrouted answer.
      if (!sawVerdict) return
      onToken?.(data?.text ?? "")
    } else if (event === "done") {
      onDone?.(data)
    } else if (event === "error") {
      onError?.({ message: data?.message || "turn stream error" })
    }
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let idx
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      handle(buffer.slice(0, idx))
      buffer = buffer.slice(idx + 2)
    }
  }
  if (buffer.trim()) handle(buffer)
}

// Mic speech-to-text: POST a recorded audio chunk (Blob) to the server-node proxy, which
// forwards it to the self-hosted faster-whisper. Returns the transcript text (accurate domain
// terms + number/time formatting) — replaces the browser Web Speech API on the mic path.
export const fetchSttTranscript = async (accessToken, userId, { blob }) => {
  const response = await fetchVoiceWithTimeout(`${API_BASE_URL}/sessionCopilotStt`, {
    method: "POST",
    headers: { ...reqHeaders(accessToken, userId), "Content-Type": "application/octet-stream" },
    body: blob,
  })
  if (!response.ok) throw new Error(`sessionCopilotStt ${response.status}`)
  const data = await response.json().catch(() => ({}))
  return typeof data?.text === "string" ? data.text : ""
}

// Streaming-STT bootstrap: authenticate + get an ephemeral { session, wsToken } for the browser
// mic WebSocket (/api/voiceStream), which streams PCM to the server-side Silero VAD + faster-whisper.
// `hints` optionally overrides Silero endpointing per-session (the AiTest tuning knobs). The wsToken
// is a single-use capability the WS carries as a query param (browsers can't set WS headers).
export const sessionCopilotVoiceStart = async (accessToken, userId, { hints, session, dictation } = {}) => {
  const response = await fetch(`${API_BASE_URL}/sessionCopilotVoiceStart`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // `session` = the gameplay session name → the server builds the per-session STT glossary (hotword
    // bias + correction terms) from that scenario. Optional; omitted = no vocab bias, mic still works.
    // `dictation` = the mic that types into the action box (not Tracy): the server turns on live
    // partials and the constrained correction pass for that session only.
    body: JSON.stringify({ args: { hints: hints || undefined, session: session || undefined, dictation: dictation || undefined } }),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err?.message || `sessionCopilotVoiceStart ${response.status}`)
  }
  return response.json() // { session, wsToken }
}

// Backend-authoritative wake-word + intent classifier. POST one final transcript; the server
// decides { addressed, bare, command, intent, hotWindowMs } using the shared matcher (fuzzy /
// edit-distance / aliases) instead of the client hardcoding it. No-LLM, fast. `hotWindow` tells
// the server we're still inside the post-response window so an un-prefixed utterance counts.
export const sessionCopilotWake = async (accessToken, userId, { session, text, hotWindow, followUp } = {}) => {
  const response = await fetch(`${API_BASE_URL}/sessionCopilotWake`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // `followUp` = Tracy just answered you, so a request-shaped utterance with no wake word is
    // treated as a continued conversation. `hotWindow` (a bare "Tracy") accepts anything.
    body: JSON.stringify({ sessionName: session, text, hotWindow: !!hotWindow, followUp: !!followUp }),
  })
  if (!response.ok) throw new Error(`sessionCopilotWake ${response.status}`)
  return response.json()
}

// Ask whether Tracy has anything worth saying about what the room has been discussing.
//
// The browser has to ASK for this — it cannot be pushed to. voice-pipeline's /say only knows
// meeting sessions; a browser mic session lives in a different registry, so there is no channel
// from the server to this page. The client polls it at a lull instead.
//
// The answer is almost always { mode: 'none', reason } and that path is deliberately cheap on the
// server (it gates on a pause, on new speech, on a scan interval and on an hourly cap before it
// spends anything). `reason` is for the console, never for the user.
export const sessionCopilotOffer = async (accessToken, userId, { session, playerId, lastActivityAt, turnOpen } = {}) => {
  const response = await fetch(`${API_BASE_URL}/sessionCopilotOffer`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ sessionName: session, playerId, lastActivityAt, turnOpen: !!turnOpen }),
  })
  if (!response.ok) throw new Error(`sessionCopilotOffer ${response.status}`)
  return response.json()
}

// "The room is actually hearing this offer." Posted when playback STARTS, not when the offer
// arrives — the client can receive one and then discard it because somebody started speaking.
//
// Until this lands the offer is pending server-side: its actions are reserved but not permanently
// recorded, so an offer nobody heard does not cost the room those actions for the rest of the
// exercise. Fire-and-forget: a failed ack must never interrupt playback.
export const sessionCopilotOfferDelivered = async (accessToken, userId, { session, offerId } = {}) => {
  const response = await fetch(`${API_BASE_URL}/sessionCopilotOfferDelivered`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ sessionName: session, offerId }),
  })
  if (!response.ok) throw new Error(`sessionCopilotOfferDelivered ${response.status}`)
  return response.json()
}

export const sessionCopilotStream = async (
  accessToken,
  userId,
  {
    sessionName,
    mode,
    question,
    voice,
    org,
    facilitatorOverride,
    debugPrompt,
    conversationHistory,
    signal,
    onStarted,
    onToken,
    onDone,
    onError,
    onPromptDefault,
    onPromptSent,
  }
) => {
  const body = { args: { sessionName, mode } }
  if ((mode === "answer" || mode === "debrief") && question) body.args.question = question
  if (voice) body.args.voice = true
  if (org) body.args.org = org
  // Prompt-tuning playground: thread the prior [{question, answer}] turns so the facilitator's
  // continuity rules actually have context to work with (the live voice path does the same).
  if (Array.isArray(conversationHistory) && conversationHistory.length) body.args.conversationHistory = conversationHistory
  // Prompt-tuning playground: send an edited facilitator persona to test (server uses it
  // for this request only; empty/omitted → the current default rules).
  if (facilitatorOverride && facilitatorOverride.trim()) body.args.facilitatorOverride = facilitatorOverride
  // Ask the server to stream back the fully-composed prompt it sent to the LLM (debug view).
  if (debugPrompt) body.args.debugPrompt = true

  let response
  try {
    response = await fetch(`${API_BASE_URL}/sessionCopilotStream`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err?.name === "AbortError") return
    onError?.({ message: "Could not reach the co-pilot service. Please try again." })
    return
  }

  // takeaways is non-streaming — the endpoint returns JSON (200 or 413 skip).
  if (mode === "takeaways") {
    if (response.status === 413) {
      onError?.({ code: "TOO_LARGE", message: "The transcript is too large to summarize right now. Try clearing older segments." })
      return
    }
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}))
      onError?.({ message: errData?.message || `Takeaways failed (${response.status}).` })
      return
    }
    const data = await response.json().catch(() => ({}))
    onDone?.({ outputId: data?.outputId ?? null, items: Array.isArray(data?.items) ? data.items : [] })
    return
  }

  // Streaming modes: a non-OK status arrives as JSON before the stream opens.
  if (!response.ok) {
    if (response.status === 413) {
      onError?.({ code: "TOO_LARGE", message: "The transcript is too large for the co-pilot right now. Try clearing older segments." })
      return
    }
    const errData = await response.json().catch(() => ({}))
    onError?.({ message: errData?.message || `Co-pilot request failed (${response.status}).` })
    return
  }

  if (!response.body) {
    onError?.({ message: "The co-pilot stream returned no data." })
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  // Parse one SSE frame (block of lines) into { event, data }. Comment lines
  // (": keepalive") and blank lines are ignored.
  const parseFrame = (frame) => {
    let eventName = "message"
    const dataLines = []
    for (const rawLine of frame.split("\n")) {
      const line = rawLine.replace(/\r$/, "")
      if (!line || line.startsWith(":")) continue
      if (line.startsWith("event:")) eventName = line.slice(6).trim()
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""))
    }
    if (dataLines.length === 0) return null
    let data = {}
    try {
      data = JSON.parse(dataLines.join("\n"))
    } catch {
      data = {}
    }
    return { event: eventName, data }
  }

  const handleFrame = (frame) => {
    const parsed = parseFrame(frame)
    if (!parsed) return
    const { event, data } = parsed
    if (event === "started") onStarted?.(data)
    else if (event === "promptDefault") onPromptDefault?.(data?.facilitatorRules ?? "")
    else if (event === "promptSent") onPromptSent?.(data)
    else if (event === "token") onToken?.(data?.text ?? "")
    else if (event === "done") onDone?.(data)
    else if (event === "error") {
      if (data?.code === "TOO_LARGE") {
        onError?.({ code: "TOO_LARGE", message: "The transcript is too large for the co-pilot right now. Try clearing older segments." })
      } else {
        onError?.({ code: data?.code, message: data?.message || "The co-pilot ran into an error." })
      }
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let boundary = buffer.indexOf("\n\n")
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)
        handleFrame(frame)
        boundary = buffer.indexOf("\n\n")
      }
    }
    if (buffer.trim()) handleFrame(buffer)
  } catch (err) {
    if (err?.name === "AbortError") return
    onError?.({ message: "The co-pilot stream was interrupted." })
  } finally {
    try {
      reader.releaseLock()
    } catch {
      // reader already released / stream torn down — nothing to do
    }
  }
}

export const updateOrgVariables = async (accessToken, userId, { orgId, coreSystems, irPolicy, irPolicys, coreSystemSupportingDocs }) => {
  // Build variables obj
  const newVars = {}
  if (coreSystems) {
    newVars[ORG_VARIABLE_CORE_SYSTEMS_ID] = {
      type: ORG_VARIABLE_TYPE_STRING,
      value: coreSystems,
    }
  }
  if (coreSystemSupportingDocs) {
    newVars[ORG_VARIABLE_CORE_SYSTEM_SUPPORTING_DOCS_ID] = {
      type: ORG_VARIABLE_TYPE_ARRAY,
      value: coreSystemSupportingDocs,
    }
  }
  if (irPolicy) {
    newVars[ORG_VARIABLE_INCIDENT_RESPONSE_PLAN_ID] = {
      type: ORG_VARIABLE_TYPE_STRING,
      value: irPolicy,
    }
  }
  if (irPolicys) {
    newVars[ORG_VARIABLE_IR_POLICYS_ID] = {
      type: ORG_VARIABLE_TYPE_ARRAY,
      value: irPolicys,
    }
  }
  if (!Object.keys(newVars).length) {
    return null
  }

  // perf/lean-org-fetch: /getOrg is trimmed, so read the FULL existing variables from the
  // dedicated endpoint before merging — otherwise the heavy sibling keys (profiles,
  // supporting docs, ir_policys) would be absent from the base and deleted on writeback.
  const existingVars = (await fetchOrgVariables(accessToken, userId, { orgId })) ?? {}
  const mergedVars = {
    ...existingVars,
    ...newVars,
  }

  const updatedOrg = await updateOrg(accessToken, userId, { id: orgId, variables: mergedVars })
  return updatedOrg
}

/**
 * Save security analysis document to a core systems profile.
 * Resolves to the primary profile when no profileId is provided.
 * Fails loudly if no primary profile exists (indicates migration/data integrity issue).
 */
export const saveSecurityAnalysis = async (accessToken, userId, { orgId, securityAnalysis, coreSystemsProfileId = null }) => {
  let effectiveProfileId = coreSystemsProfileId
  if (!effectiveProfileId) {
    const profiles = await fetchOrgCoreSystemsProfiles(accessToken, userId, { orgId })
    const primary = profiles.find((p) => p.is_primary)
    if (!primary) {
      throw new Error("Cannot save security analysis: no primary core systems profile found. Organization may need migration.")
    }
    effectiveProfileId = primary.id
  }
  return updateCoreSystemsProfile(accessToken, userId, {
    orgId,
    profileId: effectiveProfileId,
    updates: { value: securityAnalysis },
  })
}

/**
 * Render a settings document (Core Systems or Runbook) as a .docx via the Hasura REST action.
 * Returns { filename, mimeType, bytes } where bytes is a Uint8Array ready for Blob construction.
 */
export const renderSettingsDocument = async (accessToken, userId, { html, markdown, title, docType, logoUrl, brandColor }) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 35000)

  try {
    const response = await fetch(`${window.__RUNTIME_CONFIG__.REACT_APP_API}${API_PATHS.RENDER_SETTINGS_DOCUMENT}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Auth-Access-Id": userId,
        "Content-Type": "application/json",
      },
      // Prefer HTML when supplied (pandoc's HTML reader preserves tables/formatting
      // the markdown reader drops); fall back to markdown for older callers.
      body: JSON.stringify({
        input: { ...(html ? { html } : { markdown }), title, docType, ...(logoUrl ? { logoUrl } : {}), ...(brandColor ? { brandColor } : {}) },
      }),
      signal: controller.signal,
    })

    if (!response.ok) {
      let serverCode = null
      let serverMessage = null
      try {
        const errBody = await response.json()
        serverCode = errBody?.code || errBody?.extensions?.code || errBody?.errors?.[0]?.extensions?.code || null
        serverMessage = errBody?.error || errBody?.message || errBody?.errors?.[0]?.message || null
      } catch (e) {
        // non-JSON error body
      }
      const err = new Error(serverMessage || `HTTP ${response.status}`)
      err.status = response.status
      err.serverCode = serverCode
      throw err
    }

    const body = await response.json()
    const payload = body?.result || body
    const binary = atob(payload.contentBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return { filename: payload.filename, mimeType: payload.mimeType, bytes }
  } catch (err) {
    if (err.name === "AbortError") {
      const e = new Error("Conversion timed out")
      e.status = 504
      e.serverCode = "CLIENT_TIMEOUT"
      throw e
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

export const renderReportDocument = async (accessToken, userId, { markdown, title, docType = "report", logoUrl, brandColor }) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 35000)

  try {
    const response = await fetch(`${window.__RUNTIME_CONFIG__.REACT_APP_API}${API_PATHS.RENDER_REPORT_DOCUMENT}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Auth-Access-Id": userId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ input: { markdown, title, docType, ...(logoUrl ? { logoUrl } : {}), ...(brandColor ? { brandColor } : {}) } }),
      signal: controller.signal,
    })

    if (!response.ok) {
      let serverCode = null
      let serverMessage = null
      try {
        const errBody = await response.json()
        serverCode = errBody?.code || errBody?.extensions?.code || errBody?.errors?.[0]?.extensions?.code || null
        serverMessage = errBody?.error || errBody?.message || errBody?.errors?.[0]?.message || null
      } catch (e) {
        // non-JSON error body
      }
      const err = new Error(serverMessage || `HTTP ${response.status}`)
      err.status = response.status
      err.serverCode = serverCode
      throw err
    }

    const body = await response.json()
    const payload = body?.result || body
    const binary = atob(payload.contentBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return { filename: payload.filename, mimeType: payload.mimeType, bytes }
  } catch (err) {
    if (err.name === "AbortError") {
      const e = new Error("Conversion timed out")
      e.status = 504
      e.serverCode = "CLIENT_TIMEOUT"
      throw e
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

// Render the report content to sanitized HTML via the same pandoc pipeline the
// .docx uses, so the in-app preview matches the downloaded document (same logo,
// same structure). Brand color is applied client-side via CSS.
export const renderReportPreviewHtml = async (accessToken, userId, { markdown, title, docType = "report", logoUrl, brandColor }) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 35000)

  try {
    const response = await fetch(`${window.__RUNTIME_CONFIG__.REACT_APP_API}${API_PATHS.RENDER_REPORT_DOCUMENT}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Auth-Access-Id": userId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        input: { markdown, title, docType, outputFormat: "html", ...(logoUrl ? { logoUrl } : {}), ...(brandColor ? { brandColor } : {}) },
      }),
      signal: controller.signal,
    })

    if (!response.ok) {
      let serverCode = null
      let serverMessage = null
      try {
        const errBody = await response.json()
        serverCode = errBody?.code || errBody?.extensions?.code || errBody?.errors?.[0]?.extensions?.code || null
        serverMessage = errBody?.error || errBody?.message || errBody?.errors?.[0]?.message || null
      } catch (e) {
        // non-JSON error body
      }
      const err = new Error(serverMessage || `HTTP ${response.status}`)
      err.status = response.status
      err.serverCode = serverCode
      throw err
    }

    const body = await response.json()
    const payload = body?.result || body
    return { html: payload.html, title: payload.title }
  } catch (err) {
    if (err.name === "AbortError") {
      const e = new Error("Conversion timed out")
      e.status = 504
      e.serverCode = "CLIENT_TIMEOUT"
      throw e
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Save supporting documents for core systems
 */
export const saveCoreSystemSupportingDocs = async (accessToken, userId, { orgId, documents }) => {
  return patchOrgVariables(accessToken, userId, {
    orgId,
    variableUpdates: {
      [ORG_VARIABLE_CORE_SYSTEM_SUPPORTING_DOCS_ID]: { type: ORG_VARIABLE_TYPE_ARRAY, value: documents },
    },
  })
}

/**
 * Save runbooks/IR plans files to organization's ir_policys variable
 */
export const saveRunbooks = async (accessToken, userId, { orgId, runbooks }) => {
  await updateOrgVariables(accessToken, userId, {
    orgId,
    irPolicys: runbooks,
  })
}

// Function to download scenario in excel format as received from backend
export const downloadScenarioAsRawFile = async (accessToken, userId, { scenarioId, fileName }) => {
  if (!scenarioId) {
    throw Error("No scenario ID provided for download")
  }
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/test/${encodeRFC3986URIComponent(scenarioId)}/data`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let fileContentB64 = null
  let downloadFileName = fileName ?? null

  if (resRaw?.status === 200) {
    // Parse JSON
    const response = (await resRaw.json())?.result ?? null
    fileContentB64 = response
    downloadFileName = `${downloadFileName}.xlsx` ?? `Chaostrack-Scenario-${scenarioId}-Export.xlsx`

    // Kick off file download in browser
    const downloadBytes = Uint8Array.from(Buffer.from(fileContentB64, "base64"))
    const downloadBlob = new Blob([downloadBytes])
    const url = window.URL.createObjectURL(downloadBlob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", downloadFileName)
    document.body.appendChild(link)
    link.click()
    // Cleanup URL object
    link.remove()
    window.URL.revokeObjectURL(url)
  } else {
    throw Error(`Error downloading spreadsheet for simulation with id '${scenarioId}': Got response code ${resRaw?.status}`)
  }
}

/**
 * Fetch available LLM models from the server
 */
// Derive the Step-4 "Short" timeline table, streaming rows as the model emits them so
// the table builds live. onRow fires per validated row; resolves with the final envelope.
export const deriveTimelineTableStream = async (accessToken, userId, { simulationId, timeline, onRow }) => {
  const response = await fetch(`${API_BASE_URL}/v1/simulations/${encodeURIComponent(simulationId)}/timeline-table?stream=1`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ timeline: timeline ?? "" }),
  })
  if (!response.ok || !response.body) return { success: false, error: "Couldn't build the timeline table." }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let result = { success: false, error: "Couldn't build the timeline table." }
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let idx
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const event = buffer.slice(0, idx)
      buffer = buffer.slice(idx + 2)
      const line = event.split("\n").find((l) => l.startsWith("data: "))
      if (!line) continue
      let data
      try {
        data = JSON.parse(line.slice(6))
      } catch {
        continue
      }
      if (data.row && onRow) onRow(data.row)
      else if (data.done) result = { success: true, rows: data.rows, src_hash: data.src_hash }
      else if (data.error) result = { success: false, error: data.error }
    }
  }
  return result
}

// Derive the Step-4 "Short" timeline table from the long timeline. The long text is the
// source of truth; pass the in-editor value so the table matches what the user sees.
export const deriveTimelineTable = async (accessToken, userId, { simulationId, timeline }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulations/${encodeURIComponent(simulationId)}/timeline-table`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ timeline: timeline ?? "" }),
  })
  if (resRaw?.status === 200) return await resRaw.json()
  return { success: false, error: "Couldn't build the timeline table." }
}

export const getLLMModels = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/llm-models`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    // Parse JSON
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

// ---------------- Profile Variables (Report Prompts) ----------------
export const getAllProfileVariables = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/variables/all`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.data ?? null
  }
  return result
}

export const getProfileVariables = async (accessToken, userId, { profile_id, profile_name }) => {
  const queryParam = profile_id ? `profile_id=${encodeRFC3986URIComponent(profile_id)}` : `profile_name=${encodeRFC3986URIComponent(profile_name)}`
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/variables?${queryParam}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.data ?? null
  }
  return result
}

export const createProfileVariable = async (
  accessToken,
  userId,
  { profile_id, profile_name, name, variable_code, type = "STRING", description, prompt }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/variables`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      input: { profile_id, profile_name, name, variable_code, type, description, prompt },
    }),
  })

  let result = null
  let message = null
  if (resRaw?.status === 200 || resRaw?.status === 201) {
    const json = await resRaw.json()
    const responseData = json?.data ?? json
    if (responseData?.success === false) {
      message = responseData?.error
    } else {
      result = responseData?.data ?? responseData
    }
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message
    } catch {
      /* ignore */
    }
  }
  return { result, message }
}

export const updateProfileVariable = async (
  accessToken,
  userId,
  { id, profile_id, profile_name, name, variable_code, type, description, prompt }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/variables/${encodeRFC3986URIComponent(id)}`, {
    method: "PUT",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      data: {
        profile_id,
        profile_name,
        name,
        variable_code,
        type,
        description,
        prompt,
      },
    }),
  })

  let result = null
  let message = null
  if (resRaw?.status === 200) {
    const json = await resRaw.json()
    const responseData = json?.data ?? json
    if (responseData?.success === false) {
      message = responseData?.error
    } else {
      result = responseData?.data ?? responseData
    }
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message
    } catch {
      /* ignore */
    }
  }
  return { result, message }
}

export const deleteProfileVariable = async (accessToken, userId, { id, profile_name }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/variables/${encodeRFC3986URIComponent(id)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null
  let message = null
  if (resRaw?.status === 200) {
    const json = await resRaw.json()
    const responseData = json?.data ?? json
    if (responseData?.success === false) {
      message = responseData?.error
    } else {
      result = responseData?.data ?? responseData
    }
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message
    } catch {
      /* ignore */
    }
  }
  return { result, message }
}

export const bulkDeleteProfileVariables = async (accessToken, userId, { profile_id, profile_name }) => {
  const queryParam = profile_id ? `profile_id=${encodeRFC3986URIComponent(profile_id)}` : `profile_name=${encodeRFC3986URIComponent(profile_name)}`
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/simulation-configs/variables/bulk-delete?${queryParam}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null
  let message = null
  if (resRaw?.status === 200) {
    const json = await resRaw.json()
    const responseData = json?.data ?? json
    if (responseData?.success === false) {
      message = responseData?.error
    } else {
      result = responseData?.data ?? responseData
    }
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message
    } catch {
      /* ignore */
    }
  }
  return { result, message }
}

export const fetchPromptModelConfigs = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/prompt-model-configs`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

export const updatePromptModelConfig = async (accessToken, userId, { name, prompt }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/v1/prompt-model-configs/${encodeRFC3986URIComponent(name)}`, {
    method: "PUT",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({
      name,
      prompt,
    }),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.data ?? null
  }

  return result
}

/**
 * Process a single SSE event and extract data
 * @param {string} eventText - The SSE event text
 * @param {object} state - State object containing fullText and callbacks
 * @returns {boolean} - true if streaming should stop, false to continue
 */
const processSSEEvent = (eventText, state) => {
  if (!eventText.trim()) return false

  const lines = eventText.split("\n")
  for (const line of lines) {
    const trimmedLine = line.trim()

    // Skip empty lines and SSE comments
    if (!trimmedLine || trimmedLine.startsWith(":")) {
      continue
    }

    // Process data lines
    if (trimmedLine.startsWith("data: ")) {
      try {
        const jsonStr = trimmedLine.slice(6) // Remove "data: " prefix

        // Handle empty JSON string
        if (!jsonStr.trim()) {
          return false
        }

        const data = JSON.parse(jsonStr)

        // Builder-detected refusal/meta output (gen_error): the model talked ABOUT the
        // request instead of producing the artifact. Not a transport error — remember it
        // so the done event delivers it via onComplete metadata and the caller can show
        // a friendly inline error, save nothing, and block progression.
        if (data.gen_error && !data.done) {
          state.genError = data.gen_error
          return false // Continue streaming (the done event follows)
        }

        // Check for error first (before chunk/done checks)
        if (data.error !== undefined && data.error !== null) {
          // Error occurred - call onError callback and stop streaming
          const errorMessage = typeof data.error === "string" ? data.error : data.error.message || String(data.error) || "Unknown error occurred"
          const error = new Error(errorMessage)
          console.error("Stream error received:", errorMessage, data)
          if (state.onError) {
            state.onError(error)
          } else {
            console.warn("onError callback not set, cannot notify of error:", errorMessage)
          }
          return true // Signal to stop streaming
        }

        // Stage marker (multi-stage generations, e.g. URL: read article → tailor draft).
        // `reset` clears the text streamed so far so the next stage starts from a clean slate.
        if (data.stage !== undefined && !data.chunk) {
          if (data.reset) state.fullText = ""
          if (state.onStage) {
            // Pass the whole frame: some stages carry a payload (CTF generation streams
            // `{stage:"inject", index, inject}` per finished question and
            // `{stage:"progress", injects, expected}`); callers that only read stage/label are unaffected.
            state.onStage({ ...data, stage: data.stage, label: data.label || "", reset: !!data.reset })
          }
          if (data.reset && state.onChunk) {
            state.onChunk("", state.fullText)
          }
          return false // Continue streaming
        }

        if (data.chunk) {
          // New text chunk received
          state.fullText += data.chunk
          if (state.onChunk) {
            // Throttle UI updates to ~7/s: consumers re-render the FULL accumulated text
            // per callback (tiptap setContent / markdown re-parse), and a per-token cadence
            // janks the main thread badly enough to backpressure the SSE socket itself.
            // onComplete always delivers the final fullText, so skipped ticks lose nothing.
            const now = Date.now()
            if (!state._lastChunkEmit || now - state._lastChunkEmit >= 150) {
              state._lastChunkEmit = now
              state.onChunk(data.chunk, state.fullText, data.stage)
            }
          }
          return false // Continue streaming
        } else if (data.done) {
          // Streaming complete
          if (state.onComplete) {
            state.onComplete(state.fullText, {
              variable: data.variable,
              genError: data.gen_error ?? state.genError ?? null,
              // People the builder found in the output but not in the author's inputs (likely invented).
              unlistedNames: Array.isArray(data.unlisted_names) ? data.unlisted_names : [],
            })
          }
          return true // Signal completion
        }
      } catch (parseError) {
        // Log JSON parsing errors
        if (parseError instanceof SyntaxError) {
          console.error("Error parsing SSE data:", parseError, "Line:", trimmedLine)
        } else {
          // If it's an application error (not a parse error), handle it
          if (state.onError) {
            state.onError(parseError instanceof Error ? parseError : new Error(String(parseError)))
          }
          return true // Stop streaming on non-parse errors
        }
      }
    }
  }
  return false // Continue streaming
}

/**
 * Process the response stream from the server
 * @param {ReadableStreamDefaultReader} reader - The stream reader
 * @param {object} state - State object containing fullText and callbacks
 * @param {AbortController} abortController - Abort controller for cancellation
 * @returns {Promise<{fullText: string, controller: AbortController}>}
 */
const processResponseStream = async (reader, state, abortController) => {
  const decoder = new TextDecoder()
  let buffer = ""
  const eventBoundary = "\n\n"

  while (true) {
    const { done, value } = await reader.read()

    if (done) {
      // Process any remaining data in buffer
      if (buffer.trim()) {
        const events = buffer.split(eventBoundary)
        for (const event of events) {
          if (processSSEEvent(event, state)) {
            return { fullText: state.fullText, controller: abortController }
          }
        }
      }
      break
    }

    // Decode chunk and add to buffer
    buffer += decoder.decode(value, { stream: true })

    // Process complete SSE events (separated by \n\n)
    let boundaryIndex = buffer.indexOf(eventBoundary)

    while (boundaryIndex !== -1) {
      // Extract complete event
      const event = buffer.substring(0, boundaryIndex)
      buffer = buffer.substring(boundaryIndex + eventBoundary.length)

      // Process the complete event
      if (processSSEEvent(event, state)) {
        return { fullText: state.fullText, controller: abortController }
      }

      // Look for next event boundary
      boundaryIndex = buffer.indexOf(eventBoundary)
    }
  }

  return { fullText: state.fullText, controller: abortController }
}

/**
 * Stream simulation prompt generation with real-time updates using fetch API
 *
 * NOTE: We use fetch instead of EventSource because EventSource doesn't support:
 * - POST requests (only GET)
 * - Custom headers (needed for Authorization)
 * - Request body (needed to send prompt data)
 *
 * @param {string} accessToken - Auth0 access token
 * @param {string} userId - User ID
 * @param {number} simulationId - Simulation ID
 * @param {string} variable - Variable name (e.g., 'incident_timeline', 'starting_prompt', 'ir_policy')
 * @param {object} dataDetail - Data details for the prompt
 * @param {function} onChunk - Callback called for each text chunk received (chunk: string, fullText: string) => void
 * @param {function} onComplete - Callback called when streaming completes (fullText: string, metadata: object) => void
 * @param {function} onError - Callback called if an error occurs (error: Error) => void
 * @returns {Promise<{fullText: string, controller: AbortController}>} - Promise that resolves with the full text and abort controller
 */
export const streamSimulationPrompt = async (
  accessToken,
  userId,
  { simulationId, variable, dataDetail, onChunk, onComplete, onError, onStage, signal }
) => {
  const abortController = new AbortController()
  // Optional caller signal (CTF question runs cancel a superseded run). Absent for every other caller.
  if (signal) {
    if (signal.aborted) abortController.abort()
    else signal.addEventListener("abort", () => abortController.abort(), { once: true })
  }
  const state = {
    fullText: "",
    onChunk,
    onComplete,
    onError,
    onStage,
  }

  try {
    const response = await fetch(`${API_BASE_URL}/v1/simulations/${encodeURIComponent(simulationId)}/prompt/stream`, {
      method: "POST",
      // Use the shared header helper so a role-assumption session routes
      // its HS256 JWT through X-CT-Role-Assumption-JWT (and omits the
      // Authorization bearer entirely) instead of leaking the manager's
      // Auth0 token. Hand-rolling `Bearer ${accessToken}` here would
      // bypass that and break role assumption for this caller.
      headers: {
        "Content-Type": "application/json",
        ...authHeadersFor(accessToken, userId),
      },
      body: JSON.stringify({
        data: {
          variable,
          data: dataDetail,
        },
      }),
      signal: abortController.signal,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const reader = response.body.getReader()
    return await processResponseStream(reader, state, abortController)
  } catch (error) {
    console.error("Error in streaming simulation prompt:", error)
    if (onError) {
      onError(error)
    }
    throw error
  }
}

// ─── Live Capture API ─────────────────────────────────────────────────────────

export const startLiveCapture = async (accessToken, userId, { session, meetingUrl, voiceEnabled = false, consentAcknowledged = false }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/live-capture/start`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ meetingUrl, voiceEnabled, consentAcknowledged }),
  })
  const code = resRaw?.status ?? 0
  if (code === 200) {
    const body = await resRaw.json()
    return { result: body?.result ?? null, code, message: null }
  }
  // Non-200: attempt to parse a server-provided error message so callers can show it.
  let message = null
  try {
    const body = await resRaw.json()
    message = body?.error ?? body?.message ?? null
  } catch {
    // non-JSON body — leave message null
  }
  message = message ?? `Capture start failed (HTTP ${code}).`
  return { result: null, code, message }
}

export const getSessionCaptureAudit = async (accessToken, userId, { session }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/live-capture/audit`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  const code = resRaw?.status ?? 0
  if (code === 200) {
    const body = await resRaw.json()
    return { result: body?.result ?? null, code, message: null }
  }
  let message = null
  try {
    const body = await resRaw.json()
    message = body?.error ?? body?.message ?? null
  } catch {
    // non-JSON body
  }
  return { result: null, code, message: message ?? `Audit fetch failed (HTTP ${code}).` }
}

export const stopLiveCapture = async (accessToken, userId, { session }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/live-capture/stop`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({}),
  })
  const result = resRaw?.status === 200 ? ((await resRaw.json())?.result ?? null) : null
  return { result, code: resRaw?.status ?? 0 }
}

export const pushTranscriptSegmentToChat = async (accessToken, userId, { session, segmentId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/transcript/push`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ segmentId }),
  })
  const result = resRaw?.status === 200 ? ((await resRaw.json())?.result ?? null) : null
  return { result, code: resRaw?.status ?? 0 }
}

export const clearTranscriptSegments = async (accessToken, userId, { session }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/transcript/clear`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({}),
  })
  const result = resRaw?.status === 200 ? ((await resRaw.json())?.result ?? null) : null
  return { result, code: resRaw?.status ?? 0 }
}

export const generateCaptureSummary = async (accessToken, userId, { session }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/transcript/summary`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({}),
  })
  const result = resRaw?.status === 200 ? ((await resRaw.json())?.result ?? null) : null
  return { result, code: resRaw?.status ?? 0 }
}

export const addMicTranscriptSegment = async (accessToken, userId, { session, text, speakerName, segmentId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/mic-transcript`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ text, speakerName, segmentId }),
  })
  return { ok: resRaw?.status === 200, code: resRaw?.status ?? 0 }
}

export const getFacilitatorSessionView = async (accessToken, userId, { name }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}/facilitator-view`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  return null
}

export const getFacilitatorSessionViewWithStatus = async (accessToken, userId, { name }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(name)}/facilitator-view`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  const status = resRaw?.status ?? 0
  const data = status === 200 ? ((await resRaw.json())?.result ?? null) : null
  return { data, status }
}

// Facilitated roundtable action bodies arrive as { result: payload } (the REST alias wraps the
// action's output). Servers before the fix wrapped once more ({ result: { result: payload } });
// accept both. The record payload carries its own `result` (the grade, a string), so only an
// object-valued inner `result` is a second wrapper.
export const unwrapFacilitatedActionBody = (body) => {
  const inner = body?.result ?? null
  const nested = inner?.result
  return nested && typeof nested === "object" && !Array.isArray(nested) ? nested : inner
}

// Facilitated roundtable: record the room's decision for a virtual seat. The server
// replays the human gameplay flow as that seat (rank + commit) and returns the
// narrator's answer. Long-running (inline LLM) — the caller should show a busy state.
export const recordFacilitatedDecision = async (accessToken, userId, { session, role, phrase }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/facilitated-decision`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ role, phrase }),
  })
  if (resRaw?.status === 200) {
    const result = unwrapFacilitatedActionBody(await resRaw.json())
    return { ok: true, result }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    message = null
  }
  return { ok: false, message: message ?? "Failed to record the decision." }
}

// Facilitated roundtable Phase 2: ask the server to extract committed decisions from
// the live meeting transcript (facilitator-triggered; one LLM call per request).
export const suggestFacilitatedDecisions = async (accessToken, userId, { session, sinceTs = null }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/facilitated-suggest`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ sinceTs }),
  })
  if (resRaw?.status === 200) {
    const result = unwrapFacilitatedActionBody(await resRaw.json())
    return { ok: true, result }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    message = null
  }
  return { ok: false, message: message ?? "Failed to suggest decisions." }
}

// "Who's speaking": link one transcript speaker to a seat / named participant / facilitator /
// observer (kind "unassigned" suppresses the auto-match). Facilitator-only on the server.
// Returns the server's effective { speakerMap, speakerOptions } on success.
export const setSpeakerMapping = async (accessToken, userId, { session, speakerName, kind, seat = null, participantEmail = null }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/speaker-mapping`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ speakerName, kind, seat, participantEmail }),
  })
  if (resRaw?.status === 200) {
    // The REST alias may wrap the action's { result } once more — unwrap either shape.
    const result = unwrapFacilitatedActionBody(await resRaw.json()) ?? null
    return { ok: true, result }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    message = null
  }
  return { ok: false, message: message ?? "Couldn't save who is speaking." }
}

// audienceSubTeamIds: sub-team ids the inject is sent to; omitted/[] = everyone.
export const pushFacilitatorInject = async (accessToken, userId, { session, injectId, customText, mediaHtml, audienceSubTeamIds }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/inject`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // mediaHtml is a sequence of <img>/<video> tags the facilitator attached. It is sent separately
    // from customText so the server can append it AFTER any bot narration — the media must never be
    // fed to the LLM, which would rewrite or drop the tags.
    body: JSON.stringify({
      injectId,
      customText,
      ...(mediaHtml ? { mediaHtml } : {}),
      ...(audienceSubTeamIds?.length ? { audienceSubTeamIds } : {}),
    }),
  })
  // Bot-enabled sessions run a synchronous LLM call server-side — surface the
  // error body so the facilitator UI can show why a push failed.
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? null
    return { ok: true, result }
  }
  let message = null
  try {
    const resJSON = await resRaw?.json()
    message = resJSON?.message ?? resJSON?.error ?? null
  } catch {
    // Non-JSON error body — fall through to the generic message
  }
  return { ok: false, result: null, message: message ?? "The inject could not be pushed. Please try again." }
}

// Facilitator Manual mode: switch the AI facilitator, and send or discard the AI replies held for
// the facilitator. Each returns { ok, result } where result carries the refreshed repliesQueue, or
// { ok: false, message } with the server's reason (e.g. another facilitator already sent it).
const postFacilitatorReplies = async (accessToken, userId, { session, path, body, fallback }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/${path}`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(body),
  })
  if (resRaw?.status === 200) return { ok: true, result: (await resRaw.json())?.result ?? null }
  let message = null
  try {
    const resJSON = await resRaw?.json()
    message = resJSON?.message ?? resJSON?.error ?? null
  } catch {
    // Non-JSON error body — fall through to the generic message
  }
  return { ok: false, result: null, message: message ?? fallback }
}

export const setFacilitatorMode = (accessToken, userId, { session, mode }) =>
  postFacilitatorReplies(accessToken, userId, {
    session,
    path: "facilitator-mode",
    body: { mode },
    fallback: "Manual mode could not be changed. Please try again.",
  })

export const resolveFacilitatorReply = (accessToken, userId, { session, id, outcome, text, written = false }) =>
  postFacilitatorReplies(accessToken, userId, {
    session,
    path: "facilitator-reply",
    body: { id, outcome, ...(text !== undefined ? { text } : {}), written },
    fallback: outcome === "send" ? "The reply could not be sent. Please try again." : "The reply could not be discarded. Please try again.",
  })

// Facilitator correction to the org profile: keep it for this exercise ('session') or also update
// the organization's settings ('org', sponsor access).
export const resolveOrgCorrection = (accessToken, userId, { session, id, decision }) =>
  postFacilitatorReplies(accessToken, userId, {
    session,
    path: "org-correction",
    body: { id, decision },
    fallback:
      decision === "org"
        ? "The organization's settings could not be updated. Please try again."
        : "The correction could not be saved. Please try again.",
  })

export const resolveAllFacilitatorReplies = (accessToken, userId, { session, outcome }) =>
  postFacilitatorReplies(accessToken, userId, {
    session,
    path: "facilitator-replies-all",
    body: { outcome },
    fallback: "The replies could not be updated. Please try again.",
  })

// Facilitator-only shared parking lot. All three calls return the updated { parkingLot }
// array so the caller can refresh immediately (other facilitators pick it up on their poll).
export const addParkingLotNote = async (accessToken, userId, { session, text }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/parking-lot/add`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ text }),
  })
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? null
    return { ok: true, parkingLot: result?.parkingLot ?? [] }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    // non-JSON error body
  }
  return { ok: false, parkingLot: null, message: message ?? "The note could not be added. Please try again." }
}

export const updateParkingLotNote = async (accessToken, userId, { session, noteId, text, done }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/parking-lot/update`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ noteId, text, done }),
  })
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? null
    return { ok: true, parkingLot: result?.parkingLot ?? [] }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    // non-JSON error body
  }
  return { ok: false, parkingLot: null, message: message ?? "The note could not be updated. Please try again." }
}

export const deleteParkingLotNote = async (accessToken, userId, { session, noteId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/parking-lot/delete`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ noteId }),
  })
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? null
    return { ok: true, parkingLot: result?.parkingLot ?? [] }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    // non-JSON error body
  }
  return { ok: false, parkingLot: null, message: message ?? "The note could not be deleted. Please try again." }
}

// ─── Exercise artifacts (Exercise Progress → Artifacts) ──────────────────────
const errorMessageOf = async (resRaw, fallback) => {
  try {
    // Hasura REST relays an action's 4xx message under `error`; a direct server reply uses `message`.
    const body = await resRaw?.json()
    return body?.error ?? body?.message ?? fallback
  } catch {
    return fallback
  }
}

// Share a file into an ongoing exercise. `fileContent` is base64 (no data: prefix). Returns
// { ok, artifact } or { ok: false, message } — the server's message is user-facing (size/type/limit).
export const uploadSessionArtifact = async (accessToken, userId, { session, fileName, fileContent }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/artifacts/upload`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ fileName, fileContent }),
  })
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? null
    return { ok: true, artifact: result?.artifact ?? null }
  }
  return { ok: false, message: await errorMessageOf(resRaw, "The artifact could not be shared. Please try again.") }
}

// Facilitator "Move team here": move the exercise into a timeline phase. Returns { ok } or { ok: false, message }.
export const setFacilitatorPhase = async (accessToken, userId, { session, phaseId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/facilitator/phase`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ phaseId }),
  })
  if (resRaw?.status === 200) return { ok: true }
  return { ok: false, message: await errorMessageOf(resRaw, "The exercise could not be moved to that phase. Please try again.") }
}

// Fetch one artifact's file. Returns { ok, fileName, mediaType, fileContent } or { ok: false, message }.
export const getSessionArtifact = async (accessToken, userId, { session, artifactId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/artifacts/get`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ artifactId }),
  })
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? null
    if (result?.fileContent) return { ok: true, fileName: result.fileName, mediaType: result.mediaType, fileContent: result.fileContent }
  }
  return { ok: false, message: await errorMessageOf(resRaw, "The artifact could not be opened.") }
}

// THROWS on a failed fetch instead of returning []. An empty array is a legitimate answer ("this
// org has no bots"), so returning it for a 500 made a transient failure indistinguishable from an
// empty library — and the caller marks the library authoritative on success, which let a save
// treat stored react bots as unmanaged and delete them. The only caller catches this and leaves
// its loaded flag unset, which keeps the non-destructive omit semantics.
export const getBotProfiles = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/bot-profiles`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result?.profiles ?? []
  throw new Error(`Could not load AI bot profiles (status ${resRaw?.status ?? "no response"})`)
}

// Create or update an AI bot profile (manager only). Only non-null fields are
// applied server-side, so a partial payload (e.g. just `enabled`) is a valid update.
// The editor needs the whole answer: the profiles AND whether their prompt text is managed in
// Langfuse (`prompt_source: "langfuse"` → the text field is read-only there). getBotProfiles keeps
// returning just the list for the game-setup picker.
export const getBotProfilesWithSource = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/bot-profiles`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) {
    const result = (await resRaw.json())?.result ?? {}
    return { profiles: result.profiles ?? [], promptSource: result.prompt_source ?? "db" }
  }
  return { profiles: [], promptSource: "db" }
}

export const upsertBotProfile = async (
  accessToken,
  userId,
  { key, name, promptTemplate, trigger, model, enabled, role, persona, behavior, defaultFor }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/bot-profiles`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ key, name, promptTemplate, trigger, model, enabled, role, persona, behavior, defaultFor }),
  })
  if (resRaw?.status === 200) {
    return { ok: true, result: (await resRaw.json())?.result ?? null }
  }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    // non-JSON error body
  }
  return { ok: false, result: null, message: message ?? "Failed to save the bot profile." }
}

// Delete a bot profile override (reverts to the code-shipped default if one exists).
export const deleteBotProfile = async (accessToken, userId, { key }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/bot-profiles/${encodeRFC3986URIComponent(key)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })
  return { ok: resRaw?.status === 200 }
}

// ─── Voice Copilot settings (manager only) ───────────────────────────────────
// The editable voice prompts (each with its own model + output cap) and the STT/TTS provider
// block. Platform-wide, not per-org. Returns the EFFECTIVE values plus their code defaults so
// the UI can flag "modified" and offer a per-prompt Reset.
export const getVoiceSettings = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/voice-settings`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  return null
}

// Save one setting. Only non-null fields are applied server-side, so a partial payload
// (e.g. just the model) is a valid update and never clears the prompt text. To explicitly CLEAR
// an override field back to the server default, send the empty string "" (stored as null) —
// null means "keep the existing value", it can never clear one.
export const upsertVoiceSetting = async (accessToken, userId, payload) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/voice-settings`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(payload),
  })
  if (resRaw?.status === 200) return { ok: true, result: (await resRaw.json())?.result ?? null }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    // non-JSON error body
  }
  return { ok: false, result: null, message: message ?? "Failed to save the voice setting." }
}

// ─── Tracy's behaviour: nudge timing, transcript streaming, orb design ────────
// Omit orgId for the platform default; pass one to override it for a single organization.
// Manager-only at both levels (enforced by the Hasura action and again in the handler).
export const getVoiceBehaviour = async (accessToken, userId, { orgId } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/voice-behaviour`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ orgId: orgId || null }),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  return null
}

export const upsertVoiceBehaviour = async (accessToken, userId, payload) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/voice-behaviour-upsert`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(payload),
  })
  if (resRaw?.status === 200) return { ok: true, result: (await resRaw.json())?.result ?? null }
  let message = null
  try {
    message = (await resRaw?.json())?.message ?? null
  } catch {
    // non-JSON error body
  }
  // The server's message names the offending field and its valid range, which is more use than
  // "save failed" — surface it rather than replacing it.
  return { ok: false, result: null, message: message ?? "Failed to save the voice behaviour." }
}

// Reset one setting to its code default (removes the override document).
export const deleteVoiceSetting = async (accessToken, userId, { key }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/voice-settings/${encodeRFC3986URIComponent(key)}`, {
    method: "DELETE",
    headers: reqHeaders(accessToken, userId),
  })
  return { ok: resRaw?.status === 200 }
}

// Audition a candidate TTS voice BEFORE saving. Returns an object URL to play, or null.
export const testVoiceSample = async (
  accessToken,
  userId,
  { text, ttsProvider, openaiTtsModel, openaiTtsVoice, pollyVoice, pollyEngine, pollyRegion, ttsStyleText }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/voice-settings/test-voice`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ text, ttsProvider, openaiTtsModel, openaiTtsVoice, pollyVoice, pollyEngine, pollyRegion, ttsStyleText }),
  })
  if (resRaw?.status !== 200) {
    let message = null
    try {
      message = (await resRaw?.json())?.message ?? null
    } catch {
      // non-JSON error body
    }
    return { ok: false, url: null, message: message ?? "Voice preview failed." }
  }
  const result = (await resRaw.json())?.result ?? {}
  if (!result.audioBase64) return { ok: false, url: null, message: "No audio returned." }
  // base64 → Blob → object URL for <audio> playback.
  const bytes = Uint8Array.from(atob(result.audioBase64), (c) => c.charCodeAt(0))
  return {
    ok: true,
    url: URL.createObjectURL(new Blob([bytes], { type: result.mime || "audio/wav" })),
    effectiveProvider: result.effectiveProvider ?? null,
    fellBack: result.fellBack === true,
  }
}

export const saveCustomInject = async (accessToken, userId, { session, title, description, mediaHtml }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/custom-inject`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // Attachments are authored WITH the inject, so they travel with its definition and arrive
    // every time it is pushed rather than being re-attached each time.
    body: JSON.stringify({ title, description, ...(mediaHtml ? { mediaHtml } : {}) }),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  return null
}

// Replace the attachments on a SAVED custom inject. mediaHtml is the full remaining set, so
// removing one attachment means sending the others; an empty string clears them all.
export const updateCustomInjectMedia = async (accessToken, userId, { session, injectId, mediaHtml }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/custom-inject-media`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ injectId, mediaHtml: mediaHtml ?? "" }),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  return null
}

export const generateFacilitatorInjects = async (accessToken, userId, { session }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/session/${encodeRFC3986URIComponent(session)}/generate-injects`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({}),
  })
  if (resRaw?.status === 200) return (await resRaw.json())?.result ?? null
  return null
}

export const resolveInviteToken = async (accessToken, userId, { tokenId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/invite/${encodeRFC3986URIComponent(tokenId)}`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  let result = null
  let status = resRaw?.status
  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  }
  return { result, status }
}

export const peekInviteToken = async ({ tokenId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/invite/peek`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tokenId }),
  })
  if (resRaw?.status === 200) {
    const data = await resRaw.json()
    return { email: data?.email ?? null, authHint: data?.authHint ?? "email" }
  }
  return null
}

/**
 * Request a 6-digit sign-in code (player email-OTP plane).
 *
 * Unauthenticated by definition — this is how a session starts. The server ALWAYS
 * answers 200 with the same generic body whether or not the address has an account,
 * so this function cannot be used to enumerate customers; callers must not try to
 * infer existence from the response.
 *
 * Returns { ok, challengeId, rateLimited, message }. `challengeId` is a random UUID
 * and is useless without the emailed code.
 */
export const requestLoginCode = async ({ email }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/login/request-code`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  })
  if (!resRaw) return { ok: false, message: "Unable to reach the sign-in service. Please try again." }

  const data = await resRaw.json().catch(() => ({}))
  const payload = data?.requestLoginCode ?? data
  if (resRaw.status === 429) {
    return { ok: false, rateLimited: true, message: payload?.message ?? "Too many attempts. Please wait a few minutes." }
  }
  return { ok: resRaw.status === 200, challengeId: payload?.challengeId ?? null, message: payload?.message ?? null }
}

/**
 * Redeem a code for a player session token.
 *
 * On success returns { ok: true, session } where `session` is the entry to hand to
 * playerSessionStore.setPlayerSession. On failure the message is deliberately vague
 * (wrong code, expired challenge and exhausted attempts are indistinguishable) so the
 * endpoint gives an attacker no signal about which of the three happened.
 */
export const verifyLoginCode = async ({ challengeId, code }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/login/verify-code`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ challengeId, code }),
  })
  if (!resRaw) return { ok: false, message: "Unable to reach the sign-in service. Please try again." }

  const data = await resRaw.json().catch(() => ({}))
  const payload = data?.verifyLoginCode ?? data

  if (resRaw.status === 200 && payload?.token) {
    return {
      ok: true,
      session: {
        token: payload.token,
        expiresAt: payload.expiresAt,
        email: payload.email,
        userId: payload.userId,
        orgId: payload.orgId ?? "",
      },
    }
  }
  // 403 = the account must use a password (an elevated role can never hold a player
  // session). Surface that distinctly: it is actionable, and it leaks nothing an
  // attacker could not learn by simply trying to log in with a password.
  if (resRaw.status === 403) {
    return { ok: false, usePassword: true, message: payload?.message ?? "This account must sign in with a password." }
  }
  return { ok: false, message: payload?.message ?? "That code is not valid or has expired. Request a new one." }
}

/**
 * Check a set-password invite token. Public — the invitee has no session yet.
 *
 * Returns { valid, email }. A missing, expired, consumed or wrong-purpose token all
 * come back as `valid: false` with no detail, so the endpoint cannot be used to probe
 * which tokens exist.
 */
export const peekPasswordSetup = async ({ tokenId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/password-setup/peek`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tokenId }),
  })
  if (!resRaw) return { valid: false }
  const data = await resRaw.json().catch(() => ({}))
  const payload = data?.peekPasswordSetup ?? data
  return { valid: payload?.valid === true, email: payload?.email ?? null }
}

/**
 * "Forgot password?": ask for a one-time set-password link. The server answers the same
 * way for every address (no account enumeration), so the only failure worth showing is
 * the per-IP rate limit.
 */
export const requestPasswordReset = async ({ email }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/password-reset/request`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  })
  if (!resRaw) return { ok: false, message: "Unable to reach the sign-in service. Please try again." }
  const data = await resRaw.json().catch(() => ({}))
  const payload = data?.requestPasswordReset ?? data
  if (resRaw.status === 200) return { ok: true, message: payload?.message }
  return { ok: false, message: payload?.message ?? data?.error ?? "Too many requests. Please wait a few minutes and try again." }
}

/**
 * Redeem a set-password token and set the account password.
 *
 * A 400 carries the realm password policy's own rejection text, which is surfaced to
 * the user verbatim so they are told which rule they broke.
 */
export const completePasswordSetup = async ({ tokenId, password }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/password-setup/complete`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tokenId, password }),
  })
  if (!resRaw) return { ok: false, message: "Unable to reach the sign-in service. Please try again." }

  const data = await resRaw.json().catch(() => ({}))
  const payload = data?.completePasswordSetup ?? data
  if (resRaw.status === 200 && payload?.ok) return { ok: true, email: payload.email }
  return { ok: false, message: payload?.message ?? "Unable to set your password. Please try again." }
}

export const uploadOrgLogo = async (accessToken, userId, { imageBase64, contentType }) => {
  const response = await fetch(`${window.__RUNTIME_CONFIG__.REACT_APP_API}${API_PATHS.UPLOAD_ORG_LOGO}`, {
    method: "POST",
    // Use the shared auth headers (not a hardcoded Authorization bearer) so an
    // active role assumption is honored: authHeadersFor sends the assumed-role
    // token via X-CT-Role-Assumption-JWT, carrying the target org. Without this,
    // a manager assuming a sponsor uploaded with their own (org-less) token and
    // the builder rejected it with "Missing org".
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ input: { imageBase64, contentType } }),
  })
  if (!response.ok) {
    let serverCode = null
    let msg = `HTTP ${response.status}`
    try {
      const e = await response.json()
      serverCode = e?.code || e?.extensions?.code || e?.errors?.[0]?.extensions?.code || null
      msg = e?.error || e?.message || e?.errors?.[0]?.message || msg
    } catch (e) {
      // non-JSON
    }
    const err = new Error(msg)
    err.status = response.status
    err.serverCode = serverCode
    throw err
  }
  const body = await response.json()
  const payload = body?.result || body
  return payload.logoUrl
}

/**
 * Fetch the live CTF leaderboard for a game group.
 * Endpoint: POST /getCtfLeaderboard
 * Body arg: { name: <groupName> }  (from the Hasura query_collections `name: String!`)
 * Returns: { leaderboard: [{ rank, playerName, avatar, total, maxStreak }], players: <count> }
 */
export const getCtfLeaderboard = async (accessToken, userId, { groupName }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/getCtfLeaderboard`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ name: groupName }),
  })

  if (resRaw?.status === 200) {
    return (await resRaw.json())?.result ?? null
  }

  // The creator turned the leaderboard off: the server refuses players with CTF_LEADERBOARD_DISABLED.
  // Hasura may relay that as a 4xx with the code nested or only the message, so match any of them.
  let errBody = null
  try {
    errBody = await resRaw?.json()
  } catch (_) {
    // non-JSON body
  }
  if (ctfLeaderboardDisabledResponse(errBody)) return { disabled: true, leaderboard: null }

  // Any other failure is an error, not an empty board ("No scores yet" used to hide outages).
  const err = new Error(`getCtfLeaderboard failed: HTTP ${resRaw?.status ?? "network error"}`)
  err.status = resRaw?.status ?? 0
  throw err
}

// ── CTF live event (host-paced) — live-event design spec §6/§8 ──────────────────────────────
// Hasura REST endpoints getCtfLiveState / ctfLiveCommand / ctfLiveReact / openCtfQuestion (POST,
// same transport as getCtfLeaderboard). getCtfLiveState's `result` is the CtfScreenModel; a failed
// call throws an Error carrying the HTTP `status` and the server `code` (e.g. CTF_LIVE_CLOSED).
const ctfLivePost = async (path, accessToken, userId, body) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/${path}`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify(body),
  })
  if (resRaw?.status === 200) {
    noteServerDate(resRaw)
    return (await resRaw.json())?.result ?? null
  }
  let errBody = null
  try {
    errBody = await resRaw?.json()
  } catch (_) {
    // non-JSON body
  }
  const err = new Error(`${path} failed: HTTP ${resRaw?.status ?? "network error"}`)
  err.status = resRaw?.status ?? 0
  // Hasura relays an action's message (as `error`) and its `extensions`; a bare top-level `code`
  // from the server is only seen when the endpoint is hit directly.
  err.code = errBody?.extensions?.code ?? errBody?.code ?? null
  err.serverMessage = errBody?.error ?? errBody?.message ?? null
  throw err
}

export const getCtfLiveState = (accessToken, userId, { name, presence = true, view = "player" }) =>
  ctfLivePost("getCtfLiveState", accessToken, userId, { name, presence, view })

export const ctfLiveCommand = (accessToken, userId, { name, command, expectedSeq, args = {} }) =>
  ctfLivePost("ctfLiveCommand", accessToken, userId, { name, command, expectedSeq, args })

export const ctfLiveReact = (accessToken, userId, { name, emoji }) => ctfLivePost("ctfLiveReact", accessToken, userId, { name, emoji })

// ── CTF reports (CTF reports spec §4) ────────────────────────────────────────────────────────
// TODO(ctf-reports P3/P4): the server actions getCtfPlayerReport / getCtfEventReport (Hasura action
// + REST + query collection, same POST transport as the live endpoints) are not registered yet.
// Until they are, these calls fail (404) and the report pages show their "not available yet" state;
// the dev harness /__dev/ctf-report renders the same components from fixtures.
// Errors carry `status` + server `code` (e.g. 403 for a player opening another player's report).
export const getCtfPlayerReport = (accessToken, userId, { name }) => ctfLivePost("getCtfPlayerReport", accessToken, userId, { name })

// The action takes one `name` (group or session — the server resolves either); sending `groupName`
// was rejected by Hasura ("Unexpected variable groupName"), so every event report failed to load.
export const getCtfEventReport = (accessToken, userId, { groupName = null, name = null }) =>
  ctfLivePost("getCtfEventReport", accessToken, userId, { name: groupName || name })

// ── CTF dynamic questions (poc spec 2026-10-13-ctf-dynamic-questions-design.md) ─────────────────
// Staff only (trainer / sponsor / manager): preview an exercise's generated set — the answer key only
// with revealKey:true, which the server audits — and regenerate it / retry a held set before start.
// Server actions: getDynamicQuestionSet { name, reveal } and regenerateDynamicQuestionSet { name }.
export const getFreshQuestionSet = (accessToken, userId, { name, revealKey = false }) =>
  ctfLivePost("getDynamicQuestionSet", accessToken, userId, { name, ...(revealKey ? { reveal: true } : {}) })

export const regenerateFreshQuestionSet = (accessToken, userId, { name }) =>
  ctfLivePost("regenerateDynamicQuestionSet", accessToken, userId, { name })

// The facilitator pack (deck, answer-key booklet, cheat sheet, evidence) for ONE exercise of a
// dynamic-question CTF, built from that exercise's generated set → { fileName, mimeType, contentBase64 }.
// Available once the set is ready (409 NOT_READY before); staff only, audited server-side.
export const exportExerciseCtfPack = async (accessToken, userId, { name }) => {
  const pkg = await ctfLivePost("exportExerciseCtfPack", accessToken, userId, { name })
  if (pkg?.contentBase64) return pkg
  throw new Error("Couldn't build the facilitator pack — please try again.")
}

// Self-paced CTF: open the current question — starts its clock server-side and returns the composed
// session WITH the question (the regular session poll holds a pending question's content back).
export const openCtfQuestion = (accessToken, userId, { name }) => ctfLivePost("openCtfQuestion", accessToken, userId, { name })

export const ctfLeaderboardDisabledResponse = (body) => {
  if (!body || typeof body !== "object") return false
  const codes = [body.code, body.extensions?.code, ...(Array.isArray(body.errors) ? body.errors.map((e) => e?.extensions?.code) : [])]
  if (codes.includes("CTF_LEADERBOARD_DISABLED")) return true
  const messages = [body.message, body.error, ...(Array.isArray(body.errors) ? body.errors.map((e) => e?.message) : [])]
  return messages.some((m) => typeof m === "string" && m.includes("leaderboard is turned off"))
}

// ─── Incidents (Task 22) ──────────────────────────────────────────────────────
//
// All incident helpers below hit Hasura REST endpoints registered in
// `hasura/metadata/rest_endpoints.yaml`. Hasura maps the flat POST body fields
// directly to the named GraphQL variables in `query_collections.yaml`, then
// routes the Action to server-node's `/createIncident` (etc.) webhook with the
// Hasura-origin secret + session_variables envelope. The client only sees the
// REST surface.
//
// Attachment upload + signed-read endpoints (uploadIncidentAttachment +
// getIncidentAttachmentUrl) hit server-node directly — Hasura cannot proxy
// multipart uploads or 302 redirects.

/**
 * Declare a new incident. Returns { incidentKey, teamChannelKey, aiChannelKey }.
 */
export const createIncident = async (
  accessToken,
  userId,
  { name, description, severity, declaredAt, linkedTestId, linkedSessionName, runbookId, members, org }
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ name, description, severity, declaredAt, linkedTestId, linkedSessionName, runbookId, members, org }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error creating incident. Status: ${resRaw.status}.`
    } catch {
      message = `Error creating incident. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

/**
 * List incidents the caller is a member of (paginated).
 */
export const getIncidents = async (accessToken, userId, { limit, skip, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/list`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ limit, skip, org }),
  })

  let result = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  }

  return result
}

/**
 * Get full incident detail (channels, recent messages, members, runbook, links).
 * Returns `{ result, status, message }` so callers can distinguish 404/403/500
 * and surface the server's `message` text on non-2xx (mirrors the mutation
 * helpers in this file). `message` is null on 200.
 */
export const getIncidentView = async (accessToken, userId, { incidentKey, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/view`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error loading incident. Status: ${status}.`
    } catch {
      message = `Error loading incident. Status: ${status}.`
    }
  }

  return { result, status, message }
}

/**
 * Apply a partial patch to an incident. expectedRev enables optimistic concurrency.
 */
export const updateIncident = async (accessToken, userId, { incidentKey, patch, expectedRev, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/update`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, patch, expectedRev, org }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error updating incident. Status: ${resRaw.status}.`
    } catch {
      message = `Error updating incident. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

/**
 * Add a member to an incident.
 */
export const addIncidentMember = async (accessToken, userId, { incidentKey, member, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/members`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, member, org }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error adding incident member. Status: ${resRaw.status}.`
    } catch {
      message = `Error adding incident member. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

// M4.3: flip a single member's read/write access on ONE channel. Privileged
// callers only (trainer/sponsor/manager/creator — server enforces + clamps).
// The 200 body is `{ updated: true, email, channelKey, access, incidentRev }`
// (no `result` wrapper), so treat any 200 as success. Errors carry `message`.
export const updateIncidentMemberAccess = async (accessToken, userId, { incidentKey, email, channelKey, access, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/members/access`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, email, channelKey, access, org: org || "" }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json()) ?? null
  } else if (resRaw) {
    try {
      const parsed = await resRaw.json()
      message = (parsed?.message ?? parsed?.error) || `Error updating member access. Status: ${resRaw.status}.`
    } catch {
      message = `Error updating member access. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

// M4.4: soft-remove a member from an incident. Privileged callers only
// (sponsor/manager/creator — server refuses removing the creator with a 400).
// The 200 body is `{ removed: true, email, ... }` (no `result` wrapper), so
// treat any 200 as success. Errors carry `message` (e.g. the 400 "The incident
// creator cannot be removed.").
export const removeIncidentMember = async (accessToken, userId, { incidentKey, email, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/members/remove`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, email, org: org || "" }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json()) ?? null
  } else if (resRaw) {
    try {
      const parsed = await resRaw.json()
      message = (parsed?.message ?? parsed?.error) || `Error removing member. Status: ${resRaw.status}.`
    } catch {
      message = `Error removing member. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

export const getIncidentSynopsis = async (accessToken, userId, { incidentKey, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/synopsis`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, org }),
  })
  let result = null
  let message = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error generating synopsis. Status: ${resRaw.status}.`
    } catch {
      message = `Error generating synopsis. Status: ${resRaw.status}.`
    }
  }
  return { result, message }
}

export const generateIncidentAfterAction = async (accessToken, userId, { incidentKey, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/after-action`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, org }),
  })
  let result = null
  let message = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error generating after-action summary. Status: ${resRaw.status}.`
    } catch {
      message = `Error generating after-action summary. Status: ${resRaw.status}.`
    }
  }
  return { result, message }
}

// M4.7: download the after-action report as a Word (.docx) document. Unlike the
// generate flow this is NOT a JSON action — the endpoint streams a binary .docx
// (nginx passthrough), so we read the response as a Blob and trigger a browser
// download from a user click (never from useEffect). On a non-200 (e.g. 409 when
// no report has been generated yet) we parse the JSON {message} and surface it.
export const downloadIncidentAfterActionDocx = async (accessToken, userId, { incidentKey } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/${encodeRFC3986URIComponent(incidentKey)}/after-action.docx`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (!resRaw) {
    return { ok: false, message: "Couldn't reach the server to download the report." }
  }
  if (resRaw.status !== 200) {
    let message = `Couldn't download the report (status ${resRaw.status}).`
    try {
      message = (await resRaw.json())?.message ?? message
    } catch {
      // non-JSON error body — keep the status-based fallback message
    }
    return { ok: false, message }
  }

  // Prefer the server-provided filename (Content-Disposition), else a sensible default.
  const fallbackName = `incident-${incidentKey}-after-action.docx`
  const disposition = resRaw.headers?.get?.("Content-Disposition") || ""
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition)
  const filename = match?.[1] ? decodeURIComponent(match[1].trim()) : fallbackName

  const blob = await resRaw.blob()
  const objectUrl = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement("a")
    anchor.href = objectUrl
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
  return { ok: true }
}

export const pushIncidentChannelSummary = async (
  accessToken,
  userId,
  { incidentKey, sourceChannelKey, targetChannelKeys, customMessage, org } = {}
) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/channels/push-summary`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, sourceChannelKey, targetChannelKeys, customMessage, org }),
  })
  let result = null
  let message = null
  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      const parsed = await resRaw.json()
      message = extractApiErrorMessage(parsed, resRaw.status, "Couldn't push summary.")
    } catch {
      message = `Couldn't push summary (status ${resRaw.status}).`
    }
  }
  return { result, message }
}

// Hasura REST error responses have inconsistent shapes depending on whether the
// failure happened in the gateway (top-level `error: "..."`), in the action
// webhook (top-level `message: "..."`), or in the GraphQL layer (`errors: [{
// message, extensions: { code } }]`). Pull whichever the response actually
// carries so the user sees the real reason ("A channel with that name already
// exists.") instead of a generic "Status: 400.".
const extractApiErrorMessage = (parsed, status, fallback) => {
  if (!parsed) return fallback || `Request failed (status ${status}).`
  if (typeof parsed.message === "string" && parsed.message) return parsed.message
  if (typeof parsed.error === "string" && parsed.error) return parsed.error
  if (parsed.error && typeof parsed.error === "object") {
    if (typeof parsed.error.message === "string" && parsed.error.message) return parsed.error.message
    if (typeof parsed.error.error === "string" && parsed.error.error) return parsed.error.error
  }
  if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
    const first = parsed.errors[0]
    if (typeof first?.message === "string" && first.message) return first.message
  }
  return fallback || `Request failed (status ${status}).`
}

export const createIncidentChannel = async (accessToken, userId, { incidentKey, name, memberEmails, memberAccess, sensitivityTier, org }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/channels`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    // M4.3: `memberAccess` is an optional {[email]: 'rw'|'read'} map; the
    // server sanitizes/clamps it (non-privileged callers can only grant 'rw').
    // M4.9: `sensitivityTier` ('standard'|'privileged') is an optional passthrough
    // applied to both the team channel and its paired AI channel; the server
    // defaults missing/invalid values to 'standard'.
    body: JSON.stringify({ incidentKey, name, memberEmails, memberAccess: memberAccess || {}, sensitivityTier, org: org || "" }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      const parsed = await resRaw.json()
      message = extractApiErrorMessage(parsed, resRaw.status, `Couldn't create channel "${name}".`)
    } catch {
      message = `Couldn't create channel "${name}" (status ${resRaw.status}).`
    }
  }

  return { result, status: resRaw?.status ?? null, message }
}

/**
 * M4.9: change a channel's sensitivity tier ('standard' | 'privileged').
 * Sponsor/manager only (the server enforces this). The server updates BOTH
 * channels of the pair (the team channel and its paired AI channel). A
 * 'privileged' AI channel can see cross-team summaries from 'standard'
 * channels; 'standard' channels see nothing cross-team.
 *
 * Returns { result, status, message } — `result` carries the success message
 * (message ?? error) so callers can invalidate + surface confirmation.
 */
export const updateIncidentChannelTier = async (accessToken, userId, { incidentKey, channelKey, sensitivityTier, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/channels/tier`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, channelKey, sensitivityTier, org: org || "" }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    const parsed = await resRaw.json()
    // The action returns a message; surface it (fall back to a generic error
    // field) so the caller has a non-null success signal to key on.
    result = parsed?.message ?? parsed?.error ?? "Sensitivity updated."
  } else if (resRaw) {
    try {
      const parsed = await resRaw.json()
      message = extractApiErrorMessage(parsed, status, `Couldn't update channel sensitivity. Status: ${status}.`)
    } catch {
      message = `Couldn't update channel sensitivity. Status: ${status}.`
    }
  }

  return { result, status: status ?? null, message }
}

/**
 * Post a human message to an incident's team channel.
 */
export const postIncidentMessage = async (accessToken, userId, { incidentKey, channelKey, body, attachments, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/messages`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, channelKey, body, attachments, org }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error posting incident message. Status: ${resRaw.status}.`
    } catch {
      message = `Error posting incident message. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

/**
 * Add a correction note tied to an AI message in an incident.
 */
export const postIncidentCorrection = async (accessToken, userId, { incidentKey, aboutMessageKey, note, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/corrections`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, aboutMessageKey, note, org }),
  })

  let result = null
  let message = null

  if (resRaw?.status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error posting correction. Status: ${resRaw.status}.`
    } catch {
      message = `Error posting correction. Status: ${resRaw.status}.`
    }
  }

  return { result, message }
}

/**
 * Fetch paginated audit rows for an incident (sponsor/manager-only).
 * Returns `{ result, status, message }` so callers can distinguish 403 (no
 * permission) from other failures and surface the server's `message` text on
 * non-2xx (mirrors the mutation helpers in this file). `result` is
 * `{ audit: [rows], hasMore: boolean }` on 200; `message` is null on 200.
 */
export const getIncidentAuditView = async (accessToken, userId, { incidentKey, limit, beforeSeq, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/audit`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, limit, beforeSeq, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error loading incident audit. Status: ${status}.`
    } catch {
      message = `Error loading incident audit. Status: ${status}.`
    }
  }

  return { result, status, message }
}

// M4.5: member-scoped audit feed. All four roles may call this. USER/TRAINER get
// a redacted, channel-scoped set (actorDisplayName instead of actorEmail, no
// requestId, purge.*/legalHold.* excluded); SPONSOR/MANAGER get the FULL
// unredacted set from the SAME endpoint. Mirrors getIncidentAuditView's shape.
export const getIncidentAuditForMember = async (accessToken, userId, { incidentKey, limit, beforeSeq, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/audit/member`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, limit, beforeSeq, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      message = (await resRaw.json())?.message ?? `Error loading incident audit. Status: ${status}.`
    } catch {
      message = `Error loading incident audit. Status: ${status}.`
    }
  }

  return { result, status, message }
}

// M4.8: place or release a legal hold on an incident (SPONSOR/MANAGER only; the
// server 403s otherwise). A hold exempts the incident's audit + message history
// from the daily retention purge. The 200 body is `{ incident, legalHold }`.
export const setIncidentLegalHold = async (accessToken, userId, { incidentKey, enabled, note, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/legal-hold`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, enabled, note, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      const parsed = await resRaw.json()
      message = extractApiErrorMessage(parsed, status, `Error updating legal hold. Status: ${status}.`)
    } catch {
      message = `Error updating legal hold. Status: ${status}.`
    }
  }

  return { result, status, message }
}

/**
 * Upload an attachment to an incident. Goes directly to server-node (Hasura
 * does not proxy multipart). The browser sets the multipart boundary — do NOT
 * set Content-Type by hand.
 *
 * Returns { result: { attachmentKey, name, mimeType, warning? }, message }.
 *
 * Callers must pass `sessionVars` carrying the same Hasura session-variable
 * fields the JWT-derived envelope would contain (role/email/org/userId), since
 * the upload route bypasses Hasura's session-variables forwarding and the
 * server reads each field directly.
 */
export const uploadIncidentAttachment = async (accessToken, userId, { incidentKey, file, org, sessionVars } = {}) => {
  const form = new FormData()
  form.append("file", file)
  // server-node parses `session_variables` from a multipart form field on this
  // route because Hasura cannot proxy multipart uploads — there is no Hasura
  // session-variables forwarding here, so the client has to inject the full
  // envelope itself. The server-side handler reads:
  //   - x-hasura-role     -> isManagerRole/isSponsorRole/isTrainerRole/isUserRole
  //                          (server.js lines 10657-10664; auth.js lines 92-118)
  //   - x-hasura-org-id   -> hasuraOrgFromSession  (server.js:10690; auth.js:124)
  //   - x-hasura-email    -> callerEmail, used for incident membership check
  //                          (server.js:10695, 10707-10710)
  //   - x-hasura-user-id  -> callerUserId, recorded in the audit row
  //                          (server.js:10696, 10772)
  // Omitting any of these makes the request 404 (role gate) or 404 again (no
  // email -> empty string -> membership check fails). Keep this shape in sync
  // with the handler at /incidents/:incidentKey/attachments in server/server.js.
  // The companion endpoint /channels/:channelKey/ask-ai (server.js:10116-10125)
  // reads the same fields off the JSON body's session_variables.
  form.append("session_variables", JSON.stringify(sessionVars ?? { "x-hasura-user-id": userId }))
  if (org) form.append("org", org)

  const response = await fetchOrLogError(`${API_BASE_URL}/incidents/${encodeRFC3986URIComponent(incidentKey)}/attachments`, {
    method: "POST",
    // Strip content-type so the browser fills in the multipart boundary.
    headers: authHeadersFor(accessToken, userId),
    body: form,
  })

  let result = null
  let message = null

  if (response?.status === 200) {
    result = (await response.json()) ?? null
  } else if (response) {
    try {
      message = (await response.json())?.message ?? `Error uploading attachment. Status: ${response.status}.`
    } catch {
      message = `Error uploading attachment. Status: ${response.status}.`
    }
  }

  return { result, message }
}

/**
 * Public (unauthenticated) preview of an incident invite token — parallels
 * `peekInviteToken` for gameplay. Direct server-node route (NOT a Hasura
 * action): possession of the token UUID (delivered by private email) is proof
 * of access. Returns `{ email, authHint, incidentKey, channelKeys,
 * channelNames }` on 200; `{ notFound: true }` on a real 404 (token missing,
 * expired, or already consumed — the server returns a uniform 404 to prevent
 * enumeration); or `{ networkError: true }` for anything transient (fetch
 * failure, 5xx, rate limit) so callers can offer a retry instead of the
 * misleading "invite invalid" screen. `channelKeys` / `channelNames` may be
 * EMPTY arrays — invited to the incident with channels granted later —
 * callers must handle that gracefully.
 */
export const peekIncidentInvite = async ({ tokenId }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/public/incident-invite/peek`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tokenId }),
  })
  // Distinguish transient failure (fetch returned null, 5xx, 429) from a real
  // 404 — only the 404 means the token itself is dead.
  if (!resRaw) return { networkError: true }
  if (resRaw.status === 200) {
    const data = await resRaw.json()
    return {
      email: data?.email ?? null,
      authHint: data?.authHint ?? "email",
      incidentKey: data?.incidentKey ?? null,
      channelKeys: Array.isArray(data?.channelKeys) ? data.channelKeys : [],
      channelNames: Array.isArray(data?.channelNames) ? data.channelNames : [],
    }
  }
  if (resRaw.status === 404) return { notFound: true }
  return { networkError: true }
}

/**
 * Consume an incident invite token to record joined:true on the caller's
 * channel entries (Hasura REST → server-node `/joinIncidentChannel`). The org
 * comes from the token — the server ignores any caller-supplied org here.
 * Returns `{ result, status, message }`; `result` is
 * `{ joined: true, incidentKey, channelKeys }` on 200. The server returns a
 * uniform 404 for invalid / expired / consumed / wrong-email tokens.
 */
export const joinIncidentChannel = async (accessToken, userId, { tokenId, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/join`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ tokenId, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    // Hasura surfaces action-handler 4xx as HTTP 400 with the handler's
    // message in `error` (not `message`) — read both.
    try {
      const body = await resRaw.json()
      message = body?.message ?? body?.error ?? `Error joining channel. Status: ${status}.`
    } catch {
      message = `Error joining channel. Status: ${status}.`
    }
  }

  return { result, status, message }
}

/**
 * In-app "Join channel" (no token) — Hasura REST → server-node
 * `/joinIncidentChannelDirect`. Requires the caller to already hold a
 * joined:false entry for `channelKey` on this incident; the server also joins
 * the paired AI channel. Returns `{ result, status, message }`; `result` is
 * `{ joined: true, incidentKey, channelKeys }` on 200.
 */
export const joinIncidentChannelDirect = async (accessToken, userId, { incidentKey, channelKey, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/join-direct`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ incidentKey, channelKey, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    // Hasura surfaces action-handler 4xx as HTTP 400 with the handler's
    // message in `error` (not `message`) — read both.
    try {
      const body = await resRaw.json()
      message = body?.message ?? body?.error ?? `Error joining channel. Status: ${status}.`
    } catch {
      message = `Error joining channel. Status: ${status}.`
    }
  }

  return { result, status, message }
}

// M4.6: fetch the caller's OWN incident notification feed + unread badge count
// (the in-app bell). recipientEmail is always resolved server-side from the
// caller's session — a caller can never read another user's notifications.
// createdAt on each row is in SECONDS (TTL field); readAt/emailedAt are in ms.
// Returns `{ result, status, message }` mirroring the other incident fetchers;
// `result` is `{ notifications, unreadCount }` on 200, null otherwise.
export const getIncidentNotifications = async (accessToken, userId, { unreadOnly, limit, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/notifications`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ unreadOnly, limit, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      const body = await resRaw.json()
      message = body?.message ?? body?.error ?? `Error loading notifications. Status: ${status}.`
    } catch {
      message = `Error loading notifications. Status: ${status}.`
    }
  }

  return { result, status, message }
}

// M4.6: stamp readAt on the caller's OWN notification rows only (recipientEmail
// is the server-side ownership guard — a key belonging to another user is never
// touched). `result` is `{ marked, unreadCount }` on 200.
export const markIncidentNotificationsRead = async (accessToken, userId, { notificationKeys, org } = {}) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/incidents/notifications/read`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ notificationKeys, org }),
  })

  let result = null
  let message = null
  const status = resRaw?.status

  if (status === 200) {
    result = (await resRaw.json())?.result ?? null
  } else if (resRaw) {
    try {
      const body = await resRaw.json()
      message = body?.message ?? body?.error ?? `Error updating notifications. Status: ${status}.`
    } catch {
      message = `Error updating notifications. Status: ${status}.`
    }
  }

  return { result, status, message }
}

// ── Replane runtime config (manager Settings > Runtime Config) ────────────────
// These two hit server-node directly (nginx routes /api/getReplaneConfigs and
// /api/updateReplaneConfig/* straight to it, bypassing Hasura); the server verifies the
// Bearer JWT and requires manager/admin, then talks to Replane's admin API. The browser
// never contacts Replane.

export const fetchReplaneConfigs = async (accessToken, userId) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/getReplaneConfigs`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (resRaw?.status === 200) return { configs: (await resRaw.json())?.configs ?? [], status: 200, message: null }
  let message = `Error loading runtime config. Status: ${resRaw?.status}.`
  try {
    message = (await resRaw?.json())?.message ?? message
  } catch {
    /* keep the status-based message */
  }
  return { configs: null, status: resRaw?.status, message }
}

// Identity-provider user sync (Runtime Config > `auth`). Same server-direct route as the
// two above. mode: 'status' (last run, no IdP calls) | 'preview' (dry run) | 'run' (starts
// in the background — poll 'status' for the result).
// `sourceSecret`: the other provider's admin secret, pasted for this call only (never stored).
// `to`: which provider to copy INTO ("keycloak" | "auth0"); omitted = the server's default.
// `managers`: platform-manager emails explicitly picked for copying (never implied).
export const syncAuthUsers = async (accessToken, userId, { mode, sourceSecret, to, managers }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/syncAuthUsers`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ mode, ...(sourceSecret ? { sourceSecret } : {}), ...(to ? { to } : {}), ...(managers?.length ? { managers } : {}) }),
  })
  let body = {}
  try {
    body = (await resRaw?.json()) ?? {}
  } catch {
    /* non-JSON error page */
  }
  const ok = resRaw?.status === 200 || resRaw?.status === 202
  return { ok, data: ok ? body : null, message: ok ? null : (body?.message ?? `User sync failed. Status: ${resRaw?.status}.`) }
}

// `expectedVersion` is the version the tab loaded; the server rejects with 409 if someone
// else saved in the meantime, so a stale tab cannot silently clobber their change.
export const updateReplaneConfig = async (accessToken, userId, { projectId, name, value, expectedVersion }) => {
  const resRaw = await fetchOrLogError(`${API_BASE_URL}/updateReplaneConfig/${encodeURIComponent(projectId)}/${encodeURIComponent(name)}`, {
    method: "PUT",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ value, expectedVersion }),
  })
  if (resRaw?.status === 200) return { ok: true, version: (await resRaw.json())?.version ?? null, message: null }
  let message = `Error saving runtime config. Status: ${resRaw?.status}.`
  try {
    message = (await resRaw?.json())?.message ?? message
  } catch {
    /* keep the status-based message */
  }
  return { ok: false, version: null, message }
}

// ── Scenario media (builder video attachments) ────────────────────────────────
//
// These hit server-node directly, like uploadIncidentAttachment above, because Hasura cannot proxy
// multipart uploads. Unlike that route they carry NO session_variables envelope: server-node
// verifies the Auth0 bearer token and reads role and org from the verified claims, so a
// client-asserted envelope would be meaningless (and forgeable).
//
// The upload is chunked so a large video never needs one oversized request. Each chunk is an S3
// part; the server returns its ETag, and the complete call hands back the STABLE url to store in
// the scene text.

// Begin an upload. Returns { mediaId, chunkBytes, maxBytes } — chunkBytes is authoritative and
// comes from Replane, so the client must not assume a size of its own.
export const createScenarioMediaUpload = async (accessToken, userId, { filename, contentType, size, signal }) => {
  const response = await fetchOrLogError(`${API_BASE_URL}/scenario-media/uploads`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ filename, contentType, size }),
    signal,
  })
  return await scenarioMediaResult(response, "Could not start the upload.")
}

export const uploadScenarioMediaPart = async (accessToken, userId, { mediaId, partNumber, blob, signal }) => {
  const form = new FormData()
  form.append("chunk", blob)
  const response = await fetchOrLogError(`${API_BASE_URL}/scenario-media/uploads/${encodeRFC3986URIComponent(mediaId)}/parts/${partNumber}`, {
    method: "POST",
    // Strip content-type so the browser fills in the multipart boundary.
    headers: authHeadersFor(accessToken, userId),
    body: form,
    signal,
  })
  return await scenarioMediaResult(response, `Chunk ${partNumber} failed to upload.`)
}

export const completeScenarioMediaUpload = async (accessToken, userId, { mediaId, parts, signal }) => {
  const response = await fetchOrLogError(`${API_BASE_URL}/scenario-media/uploads/${encodeRFC3986URIComponent(mediaId)}/complete`, {
    method: "POST",
    headers: reqHeaders(accessToken, userId),
    body: JSON.stringify({ parts }),
    signal,
  })
  return await scenarioMediaResult(response, "Could not finish the upload.")
}

// Best effort — an abort that fails must not replace the error that triggered it.
export const abortScenarioMediaUpload = async (accessToken, userId, { mediaId }) => {
  try {
    await fetchOrLogError(`${API_BASE_URL}/scenario-media/uploads/${encodeRFC3986URIComponent(mediaId)}`, {
      method: "DELETE",
      headers: reqHeaders(accessToken, userId),
    })
  } catch {
    // Swallowed on purpose: S3 keeps the orphan parts, the caller keeps its original error.
  }
}

const scenarioMediaResult = async (response, fallbackMessage) => {
  if (response?.status === 200) return { result: (await response.json()) ?? null, message: null }
  let message = fallbackMessage
  if (response) {
    try {
      message = (await response.json())?.message ?? `${fallbackMessage} Status: ${response.status}.`
    } catch {
      message = `${fallbackMessage} Status: ${response.status}.`
    }
  }
  return { result: null, message }
}

// What the uploader needs before the author picks a file: whether object storage exists at all
// (without it the page falls back to inlining a single base64 image), the chunk size, and the caps.
export const getScenarioMediaConfig = async (accessToken, userId) => {
  const response = await fetchOrLogError(`${API_BASE_URL}/scenario-media/config`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (response?.status !== 200) return null
  return (await response.json()) ?? null
}

// The stored media path is deliberately API-mount agnostic ("/scenario-media/{id}"), so it has to
// be resolved against the API base before it is fetched. Requesting it verbatim hits the SITE root
// ("https://host/scenario-media/{id}"), which 404s — and because the callers treat a failed signing
// as "leave this one out", that surfaced as a permanently broken image and an empty video player
// rather than as an error.
const scenarioMediaRequestUrl = (url) => {
  if (/^https?:\/\//i.test(url)) return url // already absolute: a legacy stored value or a pasted link
  if (url.startsWith(`${API_BASE_URL}/`)) return url // already resolved
  return `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`
}

// Record that a clip was started, finished or blocked from replaying.
//
// Fire-and-forget: telemetry must never interrupt playback, so a failure here is swallowed. The
// server takes the player's identity from the token, not from this body.
export const recordMediaPlayback = async (accessToken, userId, { session, mediaKey, event, positionSeconds, durationSeconds }) => {
  try {
    await fetchOrLogError(`${API_BASE_URL}/scenario-media/playback`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify({ session, mediaKey, event, positionSeconds, durationSeconds }),
    })
  } catch {
    // A lost telemetry event must not surface to the player mid-clip.
  }
}

// Resolve a stored /scenario-media/{id} URL to a short-lived signed URL the browser can load
// directly. A <video> tag cannot send an Authorization header, so the element's src has to be a
// URL that needs none.
export const getScenarioMediaSignedUrl = async (accessToken, userId, { url }) => {
  const requestUrl = scenarioMediaRequestUrl(url)
  const sep = requestUrl.includes("?") ? "&" : "?"
  const response = await fetchOrLogError(`${requestUrl}${sep}format=json`, {
    method: "GET",
    headers: reqHeaders(accessToken, userId),
  })
  if (response?.status !== 200) return null
  return (await response.json())?.url ?? null
}

/**
 * Uploads a video file in chunks and resolves to the stable URL to put in the scene text.
 *
 * Aborts on the FIRST failed chunk rather than pressing on. The only other chunked upload in these
 * repos (importNLPDataFromFile in the admin app) keys chunks by filename, sends no sequence number
 * and continues the loop after a failed chunk, which silently assembles a corrupt file.
 *
 * `onProgress` receives 0-100.
 */
export const uploadScenarioMedia = async (accessToken, userId, { file, onProgress = () => {}, signal } = {}) => {
  // The signal is threaded through EVERY call, not just the part uploads: Cancel has to work
  // while the create or complete request is in flight, which is exactly when a stuck upload
  // leaves the user staring at 0%.
  const started = await createScenarioMediaUpload(accessToken, userId, {
    filename: file.name,
    contentType: file.type,
    size: file.size,
    signal,
  })
  if (signal?.aborted) return { url: null, message: "Upload cancelled." }
  if (!started.result) return { url: null, message: started.message }

  const { mediaId, chunkBytes } = started.result
  const parts = []
  try {
    for (let offset = 0, partNumber = 1; offset < file.size; offset += chunkBytes, partNumber += 1) {
      const blob = file.slice(offset, Math.min(offset + chunkBytes, file.size))
      const part = await uploadScenarioMediaPart(accessToken, userId, { mediaId, partNumber, blob, signal })
      if (!part.result) throw new Error(part.message)
      parts.push({ partNumber, etag: part.result.etag })
      onProgress(Math.round((Math.min(offset + chunkBytes, file.size) / file.size) * 100))
    }

    const finished = await completeScenarioMediaUpload(accessToken, userId, { mediaId, parts, signal })
    if (!finished.result) throw new Error(finished.message)
    // Deliberately RELATIVE, as the server returns it: this string is baked into the artifact text
    // and travels Postgres -> XLSX -> ArangoDB, so an environment-specific absolute URL would mean
    // a scenario published elsewhere points at dead media.
    return { url: finished.result.url, message: null }
  } catch (error) {
    // Abort with a FRESH request: the caller's signal is already aborted in the cancel case, so
    // reusing it would make the cleanup call a no-op and leak the multipart upload in S3.
    await abortScenarioMediaUpload(accessToken, userId, { mediaId })
    if (signal?.aborted) return { url: null, message: "Upload cancelled." }
    return { url: null, message: error?.message || "Upload failed." }
  }
}

// ─── Shared integrations service (Jira / ServiceNow ticketing) ───────────────
// Browser → Hasura REST → Hasura action → server-node → the shared integrations service. The browser
// never calls that service, never holds its key and never receives a saved secret. Every endpoint is a
// POST whose action result is aliased `result`. A failure throws an Error carrying `serverCode`
// (integrations_not_configured, integrations_disabled, account_in_use, gap_analysis_not_ready, …), the
// server's readable message and, for multiple_enabled_accounts, `accounts` ([{ tool, name }]). Callers
// key on the CODE: the server answers its 5xx cases as HTTP 424 so Hasura passes the body through (the
// real status is in extensions.status). Request bodies can carry a password or API token, so nothing
// here logs them.
const INTEGRATIONS_TIMEOUT_MS = 35000

const withOrg = (org) => (org ? { org } : {})

// One parser for the three error shapes that reach the browser: Hasura's RESTified
// { error, code, path }, the action's own { message, extensions: { code } }, and GraphQL
// { errors: [{ message, extensions: { code } }] }.
const integrationsErrorFrom = (body, httpStatus) => {
  const first = Array.isArray(body?.errors) ? body.errors[0] : null
  const extensions = body?.extensions ?? first?.extensions ?? null
  const err = new Error(body?.error || body?.message || first?.message || "The request failed.")
  err.serverCode = body?.code || extensions?.code || null
  err.status = extensions?.status ?? httpStatus ?? null
  err.accounts = Array.isArray(extensions?.accounts) ? extensions.accounts : null
  return err
}

const integrationsRequest = async (accessToken, userId, { path, input }) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), INTEGRATIONS_TIMEOUT_MS)

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: reqHeaders(accessToken, userId),
      body: JSON.stringify({ input }),
      signal: controller.signal,
    })
    let body = null
    try {
      body = await response.json()
    } catch {
      // non-JSON body (e.g. a proxy error page)
    }
    if (!response.ok || (Array.isArray(body?.errors) && body.errors.length > 0)) {
      throw integrationsErrorFrom(body, response.status)
    }
    return body && typeof body === "object" && "result" in body ? body.result : body
  } catch (err) {
    if (err.name === "AbortError") {
      const e = new Error("The request timed out. Try again.")
      e.status = 504
      e.serverCode = "CLIENT_TIMEOUT"
      throw e
    }
    if (err.name === "TypeError") {
      const e = new Error("Network error. Check your connection and try again.")
      e.status = null
      e.serverCode = "NETWORK_ERROR"
      throw e
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

// { configured, disabled, catalog, allowedTools, accounts }. disabled:true = the platform's feature switch
// is off (configured false, nothing else filled). Catalog tools carry `ticketing` / `displayOnly`: only
// ticketing tools can be connected, display-only ones are listed as "Disabled". `org` is honoured for
// platform admins only; every other role always gets its own organization.
export const integrationsOverview = async (accessToken, userId, { org } = {}) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.INTEGRATIONS_OVERVIEW, input: { ...withOrg(org) } })

// Try a connection without saving it. Blank secrets are filled from the saved account of the same name.
export const integrationsTestAccount = async (accessToken, userId, { org, tool, name, config }) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.INTEGRATIONS_TEST_ACCOUNT, input: { ...withOrg(org), tool, name, config } })

// Create or update an account (tested on save, saved either way). Returns { account, test }.
export const integrationsSaveAccount = async (accessToken, userId, { org, tool, name, config, enabled }) =>
  integrationsRequest(accessToken, userId, {
    path: API_PATHS.INTEGRATIONS_SAVE_ACCOUNT,
    input: { ...withOrg(org), tool, name, config, ...(typeof enabled === "boolean" ? { enabled } : {}) },
  })

// Re-test a saved account and store the result as its health. Returns { health }.
export const integrationsCheckAccount = async (accessToken, userId, { org, tool, name }) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.INTEGRATIONS_CHECK_ACCOUNT, input: { ...withOrg(org), tool, name } })

// Prepare's own on/off switch for one account (the account row's switch). Returns { account }.
export const integrationsSetAccountEnabled = async (accessToken, userId, { org, tool, name, enabled }) =>
  integrationsRequest(accessToken, userId, {
    path: API_PATHS.INTEGRATIONS_SET_ACCOUNT_ENABLED,
    input: { ...withOrg(org), tool, name, enabled: !!enabled },
  })

// Refused with serverCode "account_in_use" while another product still has the account turned on.
export const integrationsDeleteAccount = async (accessToken, userId, { org, tool, name }) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.INTEGRATIONS_DELETE_ACCOUNT, input: { ...withOrg(org), tool, name } })

// Platform admin: { configured, disabled, allowedTools, isDefault, catalog } for an explicit org.
export const integrationsGetAllowedTools = async (accessToken, userId, { org }) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.INTEGRATIONS_GET_ALLOWED_TOOLS, input: { org } })

// Platform admin: replace an org's allowed tools. Returns { allowedTools, isDefault }.
export const integrationsSetAllowedTools = async (accessToken, userId, { org, allowedTools }) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.INTEGRATIONS_SET_ALLOWED_TOOLS, input: { org, allowedTools } })

// One report of one exercise (or group exercise). `reportId` is the report's id (`gap_analysis`, `runbook`,
// "Executive Summary", …); the server defaults to the Gap Analysis.
const reportTarget = ({ sessionName, groupName, reportId }) => ({
  ...(groupName ? { groupName } : { sessionName }),
  ...(reportId ? { reportId } : {}),
})

// { configured, disabled, accounts: [{ tool, name, label, ticket, excludedItems }], tickets,
// report: { id, name, sections } } for one report of an exercise.
export const reportTicketStatus = async (accessToken, userId, { sessionName, groupName, reportId } = {}) =>
  integrationsRequest(accessToken, userId, { path: API_PATHS.REPORT_TICKET_STATUS, input: reportTarget({ sessionName, groupName, reportId }) })

// One ticket per report and exercise in each account: the first call creates it, later calls update it.
// The server loads the report itself. `items` = the ids of the parts to include (omit to keep the
// account's last choice); `preview: true` returns { preview: { title, description } } and files nothing.
// Returns { ticket, created, updated, items: { included, total } }.
export const createOrUpdateReportTicket = async (accessToken, userId, { sessionName, groupName, reportId, tool, accountName, items, preview } = {}) =>
  integrationsRequest(accessToken, userId, {
    path: API_PATHS.REPORT_TICKET,
    input: {
      ...reportTarget({ sessionName, groupName, reportId }),
      ...(tool ? { tool } : {}),
      ...(accountName ? { accountName } : {}),
      ...(Array.isArray(items) ? { items } : {}),
      ...(preview ? { preview: true } : {}),
    },
  })
